import type { AppUser } from '@/types/auth'
import { type GeneratedAddress } from '@/types/database'
import { getPublicCountryIdForApi } from '@/utils/publicCountryStore'

export interface LiberiaApiAuthContext {
  user: AppUser | null
  role: string | null
  hasAddressGenerationAccess: boolean
  /** Portal slugs from GET /me/roles — merged into x-dev-roles with metadata + active portal */
  portalRolesForHeaders?: string[] | null
}

export class LiberiaApiError extends Error {
  code?: string
  status?: number
  details?: unknown

  constructor(
    message: string,
    extra: { code?: string; status?: number; details?: unknown } = {}
  ) {
    super(message)
    this.name = 'LiberiaApiError'
    this.code = extra.code
    this.status = extra.status
    this.details = extra.details
  }
}

export interface LiberiaLocationCaptureToken {
  id: string
  incident_id: string | null
  incident_number: string | null
  token: string
  status: 'pending' | 'sent' | 'captured' | 'expired'
  latitude: number | null
  longitude: number | null
  phone_number: string | null
  twilio_message_sid: string | null
  twilio_message_status: string | null
  expires_at: string | null
  captured_at: string | null
  created_at: string
  updated_at: string
  location_link?: string
}

export interface LiberiaLocationCaptureSmsStatus {
  success: boolean
  status: string
  messageSid: string
  to?: string | null
}

export type LiberiaFeaturePermissionFeature = 'api-access' | 'address-generation' | 'boundary-creation'

export interface LiberiaFeaturePermissionRecord {
  id: string
  user_id: string
  granted_by: string | null
  granted_at: string
  revoked_at: string | null
  revoked_by: string | null
  is_active: boolean
}

export interface LiberiaUserFeatureAccess {
  id: string
  email: string
  full_name: string | null
  role: string
  has_access: boolean
  access_source: 'role' | 'explicit' | 'none'
  api_permission?: LiberiaFeaturePermissionRecord | null
  address_permission?: LiberiaFeaturePermissionRecord | null
  boundary_permission?: LiberiaFeaturePermissionRecord | null
}

export interface LiberiaAnalysisResult {
  analysisId: string
  duplicateAddress: GeneratedAddress | null
  boundaryConflict: {
    boundaryId: string
    addressId: string
    formattedAddress: string
    conflictType: 'within_boundary'
  } | null
  officialAddress: any
  nearbyAddresses: GeneratedAddress[]
  recommendation: {
    mode: 'official' | 'enhanced' | 'new'
    reason: string
  }
}

export interface LiberiaDraftResult {
  draftId: string
  address: GeneratedAddress
}

export interface LiberiaReferenceLocation {
  latitude: number
  longitude: number
  source: string
}

export interface LiberiaPlusCodeResult {
  plusCode: string
  coordinates: {
    latitude: number
    longitude: number
  }
}

export interface LiberiaDecodedPlusCodeResult {
  success: boolean
  latitude: number
  longitude: number
  plusCode: string
  shortPlusCode?: string
  referenceSource?: string | null
}

export interface LiberiaSnoocodeRecord {
  id: string
  file_name: string
  file_size?: number | null
  uploaded_by?: string | null
  uploaded_at: string
  status: string
  error_message?: string | null
  snoocode_id?: string | null
  structure_description?: string | null
  address_type?: string | null
  address_category?: string | null
  full_name?: string | null
  mobile_number?: string | null
  post_code?: string | null
  community_name?: string | null
  street_name?: string | null
  address_number?: string | null
  number_of_male?: number | null
  number_of_female?: number | null
  total_occupant?: number | null
  other_info?: string | null
  integrated_to_qls: boolean
  integration_status: 'pending' | 'integrated' | 'failed'
  qls_address_id?: string | null
  integration_notes?: string | null
  integrated_at?: string | null
  integrated_by?: string | null
  created_at: string
  updated_at: string
}

export interface LiberiaSnoocodeSummary {
  total: number
  pending: number
  integrated: number
  failed: number
}

export interface LiberiaPoiRelationship {
  relationship_type: string
  distance_meters?: number | null
  relevance_score?: number | null
  is_verified: boolean
}

export interface LiberiaPoiStagingRecord {
  id: string
  place_id: string
  name: string
  business_status?: string | null
  formatted_address?: string | null
  geometry?: any
  photos?: any[]
  place_types: string[]
  plus_code?: any
  price_level?: number | null
  rating?: number | null
  user_ratings_total?: number | null
  vicinity?: string | null
  website?: string | null
  phone_number?: string | null
  opening_hours?: any
  permanently_closed?: boolean
  latitude: number
  longitude: number
  captured_at: string
  captured_by?: string | null
  source_address_id?: string | null
  search_radius_meters?: number | null
  integration_status: 'pending' | 'approved' | 'rejected' | 'integrated'
  reviewed_at?: string | null
  reviewed_by?: string | null
  integration_notes?: string | null
  created_at: string
  updated_at: string
  address_poi_relationships?: LiberiaPoiRelationship[]
}

export interface LiberiaRegistryAddress extends GeneratedAddress {
  user_profiles?: {
    full_name: string | null
    email: string | null
  }
  projects?: {
    id: string | null
    name: string | null
    partner_id: string | null
    user_profiles?: {
      full_name: string | null
      email: string | null
    } | null
  }
}

export interface LiberiaTaxValidatedAddress {
  id: string
  formatted_address: string
  city: string
  state: string
  district: string
}

export interface LiberiaTaxpayerProfile {
  id: string
  user_id: string
  taxpayer_id: string
  full_name: string
  email: string
  phone_number: string
  national_id?: string | null
  preferred_notification_method: string
  is_verified: boolean
  verification_date?: string | null
  created_at: string
  updated_at: string
}

export interface LiberiaTaxpayerAssessment {
  id: string
  tax_property_id: string
  tax_year: number
  assessed_value: number
  tax_rate_percentage: number
  base_tax_amount: number
  total_tax_due: number
  amount_paid: number
  balance_due: number
  due_date?: string | null
  assessment_date?: string | null
  assessment_status: string
  created_at: string
  updated_at: string
  tax_properties: {
    id: string
    parcel_number?: string | null
    address?: {
      formatted_address: string
      city?: string
      county?: string
      state?: string
    } | null
  }
}

export interface LiberiaTaxpayerBill {
  id: string
  bill_number: string
  assessment_id: string
  bill_date?: string | null
  due_date?: string | null
  total_amount: number
  amount_paid: number
  balance: number
  bill_status: string
  payment_instructions?: string | null
  payment_enabled?: boolean
  tax_property: {
    id: string
    parcel_number?: string | null
    address?: {
      formatted_address: string
      city?: string
      county?: string
      state?: string
    } | null
  }
  assessment?: {
    id: string
    tax_year: number
    assessed_value: number
    tax_rate_percentage: number
    base_tax_amount: number
    total_tax_due: number
    amount_paid: number
    balance_due: number
    due_date?: string | null
    assessment_status: string
  } | null
}

export interface LiberiaTaxpayerPayment {
  id: string
  transaction_ref: string
  payment_date: string
  payment_method: string
  amount: number
  currency: string
  payment_status: string
  reconciled: boolean
  assessment_id?: string | null
  tax_property_id?: string | null
}

export interface LiberiaPaymentProviderConfig {
  id: string
  provider_name: string
  provider_type: string
  api_key_encrypted?: string | null
  /** Orange Money / Apco Pay. Base URL defaults to the official host when empty. */
  apco_api_base_url?: string | null
  /** Legacy bearer token. New checkouts use Client ID, Client Secret, and the signature secret. */
  apco_bearer_token?: string | null
  apco_client_id?: string | null
  apco_client_secret?: string | null
  apco_signature_secret?: string | null
  is_active: boolean
  test_mode: boolean
  supported_countries: string[]
  supported_currencies: string[]
  bank_name?: string | null
  account_name?: string | null
  account_number?: string | null
  routing_number?: string | null
  /** True when bank transfer has a saved account number. The number itself is not on this list. */
  bank_account_saved?: boolean
  instructions?: string | null
  created_at: string
  updated_at: string
}

export interface LiberiaRoadInfrastructureStats {
  total_segments: number
  total_length_km: number
  active_work_orders: number
  total_assets: number
  assets_needing_maintenance: number
}

export interface LiberiaRoadSegment {
  id: string
  segment_name: string
  road_name?: string | null
  road_type: string
  surface_type?: string | null
  length_km?: number | null
  width_m?: number | null
  geobloc?: string | null
  coordinates_path?: {
    coordinates?: Array<{ lat: number; lng: number }>
  } | null
  country?: string | null
  state?: string | null
  city?: string | null
  condition?: string | null
  last_inspection_date?: string | null
  next_maintenance_date?: string | null
  notes?: string | null
  created_at?: string | null
  updated_at?: string | null
}

export interface LiberiaRoadWorkOrder {
  id: string
  work_order_number: string
  segment_id?: string | null
  address_id?: string | null
  work_type: string
  priority?: string | null
  status?: string | null
  description?: string | null
  notes?: string | null
  assigned_crew?: string | null
  budget_usd?: number | null
  scheduled_start_date?: string | null
  scheduled_end_date?: string | null
  actual_start_date?: string | null
  actual_end_date?: string | null
  closure_required?: boolean
  affected_addresses_count?: number
  affected_geoblocs?: string[]
  created_at?: string | null
  updated_at?: string | null
}

export interface LiberiaRoadAsset {
  id: string
  segment_id?: string | null
  asset_type?: string | null
  asset_name: string
  coordinates: { lat?: number; lng?: number }
  geobloc?: string | null
  condition?: string | null
  installation_date?: string | null
  last_maintenance_date?: string | null
  next_maintenance_due?: string | null
  notes?: string | null
  photo_url?: string | null
  created_at?: string | null
  updated_at?: string | null
}

export interface LiberiaRoadInspection {
  id: string
  segment_id?: string | null
  inspection_date?: string | null
  inspector_id?: string | null
  inspector_name?: string | null
  condition_rating?: string | null
  pci_score?: number | null
  maintenance_recommendations?: string | null
  notes?: string | null
  defects_found?: string[] | null
  photos?: unknown
  created_at?: string | null
}

export interface LiberiaAmbulanceUnit {
  id: string
  unit_number: string
  unit_name: string
  vehicle_type: string
  license_plate?: string | null
  current_status: string
  current_latitude?: number | null
  current_longitude?: number | null
  last_location_update?: string | null
  equipment_status?: unknown
  is_active: boolean
  crew?: LiberiaAmbulanceCrew[]
  created_at?: string | null
  updated_at?: string | null
}

export interface LiberiaAmbulanceCrew {
  id: string
  user_id?: string | null
  crew_name: string
  certification_level: string
  license_number?: string | null
  is_on_duty: boolean
  current_unit_id?: string | null
  user_profile?: {
    id: string
    full_name?: string | null
    email?: string | null
  } | null
  created_at?: string | null
  updated_at?: string | null
}

export interface LiberiaLinkableEmsUser {
  id: string
  full_name?: string | null
  email?: string | null
  ems_roles?: string[]
}

export interface LiberiaEmsDispatchAssignment {
  id: string
  incident_id: string
  unit_id: string
  assigned_at: string
  assigned_by?: string | null
  assignment_status: 'assigned' | 'en_route' | 'on_scene' | 'transporting' | 'cleared' | 'cancelled'
  cleared_at?: string | null
  response_time_seconds?: number | null
  notes?: string | null
  emergency_incidents: {
    incident_number: string
    incident_type: string
    priority: string
    status: string
    address_id?: string | null
    address?: string | null
    latitude?: number | null
    longitude?: number | null
  }
  ambulance_units: {
    unit_number: string
    unit_name: string
    vehicle_type: string
    current_status: string
  }
}

export interface LiberiaFireStation {
  id: string
  station_number: string
  station_name: string
  address_id?: string | null
  latitude?: number | null
  longitude?: number | null
  phone?: string | null
  is_active: boolean
  equipment_inventory?: Record<string, unknown>
  created_at?: string | null
  updated_at?: string | null
  address?: {
    id: string
    formatted_address?: string | null
    street_name?: string | null
    building_number?: string | null
    city?: string | null
    county?: string | null
    state?: string | null
  } | null
}

export interface LiberiaFireApparatus {
  id: string
  apparatus_number: string
  apparatus_name: string
  apparatus_type: string
  station_id?: string | null
  current_status: string
  current_latitude?: number | null
  current_longitude?: number | null
  last_location_update?: string | null
  equipment_status: Record<string, unknown>
  license_plate?: string | null
  is_active: boolean
  created_at?: string | null
  updated_at?: string | null
}

export interface LiberiaFireHydrant {
  id: string
  hydrant_number: string
  address_id?: string | null
  latitude: number
  longitude: number
  plus_code?: string | null
  hydrant_type: string
  flow_rate_gpm?: number | null
  pressure_psi?: number | null
  status: string
  last_inspection_date?: string | null
  next_inspection_date?: string | null
  notes?: string | null
  created_at?: string | null
  updated_at?: string | null
}

export interface LiberiaFireIncident {
  id: string
  incident_number: string
  incident_type: string
  priority: string
  status: string
  address_id?: string | null
  latitude?: number | null
  longitude?: number | null
  reported_at: string
  completed_at?: string | null
  description?: string | null
  chief_complaint?: string | null
}

export interface LiberiaFireDispatchAssignment {
  id: string
  incident_id: string
  apparatus_id?: string | null
  assigned_personnel: string[]
  assignment_status: string
  dispatched_at: string
  arrived_at?: string | null
  cleared_at?: string | null
  notes?: string | null
  incident?: {
    incident_number: string
    incident_type: string
    priority: string
    status: string
    reported_at: string
  } | null
  apparatus?: {
    apparatus_number: string
    apparatus_name: string
    apparatus_type: string
  } | null
}

export interface LiberiaEmergencyIncident {
  id: string
  incident_number: string
  priority: string
  status: string
  incident_type: string
  caller_name?: string | null
  caller_phone?: string | null
  caller_relationship?: string | null
  patient_name?: string | null
  patient_age?: number | null
  patient_gender?: string | null
  chief_complaint: string
  address_id?: string | null
  patient_address?: string | null
  patient_ping?: string | null
  location_details?: string | null
  latitude?: number | null
  longitude?: number | null
  reported_at?: string | null
  dispatched_at?: string | null
  first_unit_arrived_at?: string | null
  patient_transported_at?: string | null
  completed_at?: string | null
  notes?: string | null
  created_at?: string | null
  updated_at?: string | null
  dispatches?: any[]
}

export interface LiberiaEmsAlert {
  id: string
  alert_type: 'emergency' | 'warning' | 'info' | 'system'
  title: string
  message: string
  priority: 'critical' | 'high' | 'medium' | 'low'
  incident_id?: string | null
  unit_id?: string | null
  created_by?: string | null
  is_read: boolean
  created_at: string
  updated_at: string
}

export interface LiberiaEmergencyShelter {
  id: string
  shelter_name: string
  shelter_type: string
  address_id?: string | null
  latitude?: number | null
  longitude?: number | null
  total_capacity: number
  current_occupancy: number
  status: string
  amenities?: string[] | null
  contact_phone?: string | null
  contact_email?: string | null
  operating_hours?: string | null
  managed_by?: string | null
  event_id?: string | null
  opened_at?: string | null
  closed_at?: string | null
  created_at: string
  updated_at: string
}

export interface LiberiaLawEnforcementAgency {
  id: string
  name: string
  agency_type: string
  jurisdiction: string
  is_active: boolean
  contact_email?: string | null
  contact_phone?: string | null
  address_id?: string | null
  badge_prefix?: string | null
  county?: string | null
  state?: string | null
  country?: string | null
}

export interface LiberiaLawEnforcementDepartment {
  id: string
  name: string
  description?: string | null
  agency_id?: string | null
  is_active: boolean
}

export interface LiberiaLawEnforcementUnit {
  id: string
  unit_identifier: string
  unit_type: string
  responsibilities?: string | null
  current_status: string
  current_latitude?: number | null
  current_longitude?: number | null
  officer_name?: string | null
  officer_user_id?: string | null
  badge_number?: string | null
  last_location_update?: string | null
  agency_id?: string | null
  is_active?: boolean
}

export interface LiberiaPoliceOfficer {
  id: string
  user_id: string
  full_name?: string | null
  email?: string | null
  badge_number?: string | null
  rank?: string | null
  phone_number?: string | null
  officer_email?: string | null
  emergency_contact?: string | null
  address_id?: string | null
  date_of_hire?: string | null
  blood_type?: string | null
  date_of_birth?: string | null
  department_id?: string | null
  is_active: boolean
  current_unit_id?: string | null
  current_unit_identifier?: string | null
}

export interface LiberiaOfficerAgencyAssignment {
  id: string
  officer_user_id: string
  agency_id: string
  badge_number?: string | null
  is_current: boolean
  started_at?: string | null
  ended_at?: string | null
}

export interface LiberiaOfficerSchedule {
  id: string
  officer_user_id: string
  schedule_date: string
  schedule_end_date?: string | null
  start_time: string
  end_time: string
  is_active: boolean
  notes?: string | null
  created_at: string
  updated_at: string
}

export interface LiberiaOfficerCertification {
  id: string
  officer_user_id: string
  certification_name: string
  certification_type: string
  issuing_authority?: string | null
  certification_number?: string | null
  issue_date?: string | null
  expiry_date?: string | null
  status: string
  notes?: string | null
  created_at: string
  updated_at: string
}

export interface LiberiaLawEnforcementIncident {
  id: string
  incident_number: string
  incident_type: string
  priority_level: string
  status: string
  incident_address?: string | null
  incident_latitude?: number | null
  incident_longitude?: number | null
  caller_ping_latitude?: number | null
  caller_ping_longitude?: number | null
  location_type?: string | null
  description: string
  reported_at: string
  dispatched_at?: string | null
  arrived_at?: string | null
  resolved_at?: string | null
  closed_at?: string | null
  dispatch_notes?: string | null
  officer_notes?: string | null
  resolution_notes?: string | null
  assigned_to?: string | null
  created_at: string
  updated_at: string
  created_by?: string | null
  location_sms_sent?: boolean | null
  location_sms_sent_at?: string | null
}

export interface LiberiaIncidentNote {
  id: string
  incident_id: string
  note: string
  created_by: string
  created_at: string
}

export interface LiberiaDispatchAssignment {
  id: string
  incident_id: string
  unit_id: string
  assigned_at: string
  dispatched_at?: string | null
  arrived_at?: string | null
  cleared_at?: string | null
  assignment_status: string
  assignment_type: 'primary' | 'backup'
  notes?: string | null
  incident?: {
    incident_number: string
    incident_type: string
    priority_level: string
    incident_address?: string | null
    incident_latitude?: number | null
    incident_longitude?: number | null
  } | null
  unit?: {
    unit_identifier: string
    unit_type: string
    officer_name?: string | null
    officer_user_id?: string | null
  } | null
}

export interface LiberiaAddressFlag {
  id: string
  address_id: string
  flag_type: string
  severity: string
  description: string
  is_active: boolean
  active_from: string
  active_until?: string | null
  created_by?: string | null
  created_at: string
  agency_id?: string | null
  generated_addresses?: {
    formatted_address?: string | null
  } | null
}

export interface LiberiaCreditPackage {
  id: string
  name: string
  credits: number
  price_usd: number
  currency: string
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface LiberiaPricingModel {
  id: string
  name: string
  description?: string | null
  price_per_api_call: number
  price_per_address_generated: number
  price_per_address_validated: number
  monthly_base_fee: number
  is_active: boolean
  created_at: string
  updated_at: string
  created_by?: string | null
}

export interface LiberiaApiSubscription {
  id: string
  user_id: string
  pricing_model_id?: string | null
  subscription_type: 'monthly' | 'yearly' | 'api_calls'
  start_date: string
  end_date: string
  api_calls_limit?: number | null
  api_calls_used: number
  status: 'pending' | 'active' | 'expired' | 'cancelled' | 'cancelling'
  amount: number
  currency: string
  created_at: string
  updated_at: string
  user_profiles?: {
    id: string
    email?: string | null
    full_name?: string | null
  } | null
}

export interface LiberiaApiPaymentRecord {
  id: string
  user_id: string
  subscription_id: string
  amount: number
  currency: string
  payment_method: string
  payment_date: string
  verification_status: 'pending' | 'verified' | 'rejected'
  verified_by?: string | null
  verified_at?: string | null
  admin_notes?: string | null
  payment_proof_filename?: string | null
  payment_proof_mime_type?: string | null
  payment_proof_url?: string | null
  payment_purpose?: 'api_access' | 'api_credits' | 'logistics_monthly_fee' | string
  source_reference?: string | null
  created_at: string
  updated_at: string
  user_profiles?: {
    id: string
    email?: string | null
    full_name?: string | null
  } | null
  api_subscriptions?: {
    id: string
    subscription_type?: string | null
    amount?: number | null
    currency?: string | null
  } | null
}

export interface LiberiaUserApiKey {
  id: string
  user_id: string
  name: string
  api_key: string
  is_active: boolean
  last_used_at?: string | null
  usage_count: number
  created_at: string
  user_profiles?: {
    id: string
    email?: string | null
    full_name?: string | null
  } | null
}

export interface LiberiaApiKeyOwner {
  id: string
  email?: string | null
  full_name?: string | null
}

export interface LiberiaApiKeyTestResult {
  ok: boolean
  key_id: string
  key_name: string
  owner_email?: string | null
  query: string
  result_count: number
  sample: unknown[]
}

export interface LiberiaApiUsageRecord {
  id: string
  api_key_id: string
  endpoint: string
  request_count: number
  success_count?: number | null
  error_count?: number | null
  response_time_ms?: number | null
  bulk_job_id?: string | null
  date: string
  created_at: string
}

export interface LiberiaUserApiCredits {
  credits_balance: number
  credits_used: number
}

export interface LiberiaApiSubscriptionAccess {
  hasAccess: boolean
  daysUntilExpiry: number
  activeSubscription: LiberiaApiSubscription | null
}

export interface LiberiaTaxPaymentRecord {
  id: string
  receipt_number: string
  transaction_reference: string | null
  transaction_ref: string
  payment_date: string
  payment_method: string
  amount: number
  currency: string
  payment_status: string
  notes?: string | null
  mobile_money_providers?: {
    id: string | null
    name: string
  } | null
  tax_assessments?: {
    id: string
    tax_year: number
    tax_properties: {
      id: string
      parcel_number: string | null
      generated_addresses: {
        formatted_address: string
      } | null
    }
  } | null
}

export interface LiberiaTaxpayerAppeal {
  id: string
  appeal_number: string
  assessment_id: string
  tax_property_id: string
  current_assessed_value: number
  requested_value: number
  reason: string
  status: string
  submitted_at: string
  reviewed_at?: string | null
  review_notes?: string | null
  tax_properties: {
    address?: {
      formatted_address: string
    } | null
  }
}

export interface LiberiaTaxFieldCollector {
  id: string
  user_id: string | null
  collector_code: string
  full_name: string
  phone_number: string
  collection_target_monthly: number
  assigned_districts: string[]
  assigned_geoblocs: string[]
  is_active: boolean
  created_by?: string | null
  created_at: string
  updated_at: string
}

export interface LiberiaPropertyValuationRule {
  id: string
  rule_name: string
  rule_type: string
  property_category?: string | null
  base_value_per_sqm?: number | null
  multiplier?: number | null
  priority: number
  city?: string | null
  zone?: string | null
  location_criteria?: Record<string, unknown> | null
  min_size_sqm?: number | null
  max_size_sqm?: number | null
  is_active: boolean
  created_by?: string | null
  created_at: string
  updated_at: string
}

export interface LiberiaPropertyInvitation {
  id: string
  tax_property_id: string
  email: string
  phone?: string | null
  invitation_token: string
  invited_by: string
  invited_at: string
  expires_at: string
  accepted_at?: string | null
  status: string
  property_address?: string
  parcel_number?: string
  invitation_url?: string
  email_sent?: boolean
  delivery_status?: string
}

export interface LiberiaCountry {
  id: string
  name: string
  code: string
  status: 'active' | 'inactive' | 'pending'
  created_at: string
  updated_at: string
}

export interface LiberiaAdministrativeAreaType {
  id: string
  name: string
  description?: string | null
  level: number
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface LiberiaCountryHierarchyLevel {
  id: string
  country_id: string
  level_number: number
  level_name: string
  level_type: string
  is_required: boolean
  sort_order: number
  created_at: string
  updated_at: string
}

export interface LiberiaPartnerType {
  id: string
  name: string
  description?: string | null
  created_at: string
  updated_at: string
}

export interface LiberiaPartnerProfile {
  id: string
  user_id: string
  company_name?: string | null
  contact_person?: string | null
  phone?: string | null
  website?: string | null
  description?: string | null
  partner_type_id?: string | null
  created_at: string
  updated_at: string
}

export interface LiberiaProjectBulkValidation {
  id: string
  project_id: string
  partner_id: string
  download_count: number
  last_downloaded_at?: string | null
  last_uploaded_at?: string | null
  uploaded_addresses_count: number
  created_at: string
  updated_at: string
}

export interface LiberiaPartnerAreaAssignment {
  id: string
  partner_id: string
  administrative_area_id: string | null
  assigned_by: string
  assigned_at: string
  status: 'active' | 'completed' | 'inactive'
  notes?: string | null
  created_at: string
  updated_at: string
  partner_profile?: {
    full_name: string
    email: string
  }
  assigned_by_profile?: {
    full_name: string
    email: string
  }
  administrative_area?: {
    id: string
    name: string
    type: string
    level: number
    country?: {
      name: string
      iso_code: string
    }
  }
}

export interface LiberiaStructureType {
  id: string
  name: string
  description?: string | null
  is_system_required: boolean
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface LiberiaPropertyOwner {
  id: string
  full_name: string
  email?: string | null
  phone?: string | null
  id_number?: string | null
  id_type?: string | null
  address?: string | null
  owner_type: string
  company_name?: string | null
  tax_id?: string | null
  notes?: string | null
  created_by?: string | null
  created_at: string
  updated_at: string
  user_id?: string | null
  linked_user?: {
    id: string
    email: string
    full_name?: string | null
  } | null
}

// Result of GET /api/v1/property/owners/linkable-users — used by the
// AddPropertyOwnerDialog to let the property manager pick the user account
// that this property is being assigned to. The selected user will be
// auto-granted the `taxpayer` role on save.
export interface LiberiaLinkableOwnerUser {
  id: string
  email: string
  full_name: string | null
  primary_portal_role: string | null
  already_taxpayer: boolean
  already_linked_as_owner: boolean
}

export interface LiberiaPropertyOwnership {
  id: string
  address_id: string
  owner_id: string
  ownership_type: string
  ownership_percentage?: number | null
  start_date?: string | null
  end_date?: string | null
  deed_id?: string | null
  notes?: string | null
  is_current: boolean
  created_by?: string | null
  created_at: string
  updated_at: string
  property_owners?: {
    id: string
    full_name: string
    email?: string | null
    phone?: string | null
    owner_type?: string | null
    company_name?: string | null
  } | null
  generated_addresses?: {
    id: string
    formatted_address: string
  } | null
  property_deeds?: {
    id?: string | null
    deed_title: string
  } | null
}

export interface LiberiaPropertyRegistryEntry {
  id: string
  formatted_address: string
  source_id: string
  latitude: number
  longitude: number
  structure_type: string
  validation_status: string
  owner_name?: string
  owner_type?: string
  ownership_type?: string
  has_deed: boolean
  has_boundary: boolean
  deed_count: number
  created_at: string
}

export interface LiberiaPropertyDeedType {
  id: string
  name: string
  description?: string | null
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface LiberiaBoundaryCorner {
  id: string
  boundary_id: string
  latitude: number
  longitude: number
  elevation_m?: number | null
  corner_sequence: number
  corner_type: string
  marker_type?: string | null
  survey_marker?: string | null
  notes?: string | null
  created_by?: string | null
  created_at: string
}

export interface LiberiaPropertyBoundary {
  id: string
  address_id: string
  boundary_polygon: unknown
  boundary_source: string
  boundary_status: string
  accuracy_level: string
  area_sqm?: number | null
  perimeter_m?: number | null
  notes?: string | null
  created_by?: string | null
  created_at: string
  updated_at: string
  boundary_corners: LiberiaBoundaryCorner[]
}

export interface LiberiaPropertyDeed {
  id: string
  address_id: string
  boundary_id?: string | null
  deed_type_id?: string | null
  deed_type_name?: string | null
  deed_title: string
  grantor_name?: string | null
  grantee_name?: string | null
  administrator_name?: string | null
  county?: string | null
  deceased_name?: string | null
  administrator_type?: string | null
  estate_county?: string | null
  consideration_amount?: string | null
  amount_numbers?: string | null
  legal_description?: string | null
  property_description?: string | null
  witness_name?: string | null
  witness_names?: string[] | null
  witness_signatures?: string[] | null
  administrator_signature?: string | null
  administrator_signature_combined?: string | null
  deed_date?: string | null
  recorded_date?: string | null
  book_reference?: string | null
  page_reference?: string | null
  notary_info?: unknown
  additional_notes?: string | null
  admin_day?: string | null
  admin_month?: string | null
  admin_year?: string | null
  deceased_location?: string | null
  executor_admin_status?: string | null
  heir_status?: string | null
  in_presence_of_combined?: string | null
  recipient_name?: string | null
  record_location?: string | null
  seal_day?: string | null
  seal_month?: string | null
  seal_year_numbers?: string | null
  seal_year_words?: string | null
  signer_name?: string | null
  created_by?: string | null
  created_at: string
  updated_at: string
}

export interface LiberiaDeedDocument {
  id: string
  deed_id: string
  document_number?: string | null
  document_date?: string | null
  file_path: string
  file_name: string
  file_size?: number | null
  mime_type?: string | null
  uploaded_by?: string | null
  upload_date: string
  created_at: string
  updated_at: string
  notes?: string | null
}

export interface LiberiaSurveyDocumentPhoto {
  id: string
  survey_document_id: string
  file_name: string
  mime_type: string | null
  file_size: number | null
  created_by?: string | null
  created_at: string
  updated_at?: string | null
}

export interface LiberiaSurveyDocument {
  id: string
  address_id: string
  document_type: string
  title: string
  notes: string
  measurements: unknown
  photo_urls: string[]
  // Structured photo metadata from survey_document_photos. Each entry carries
  // the filename, size, and ids — the raw bytes are streamed separately from
  // the download endpoint (see getLiberiaSurveyDocumentPhotoDownloadUrl).
  photos?: LiberiaSurveyDocumentPhoto[]
  created_by?: string | null
  created_at: string
  updated_at: string
  formatted_address?: string | null
}

export interface LiberiaPropertyAddressDetails {
  id: string
  formatted_address: string
  city: string
  state: string
  country: string
  latitude: number
  longitude: number
  structure_type: string
  created_at: string
  tax_property_id?: string | null
  property_boundaries: LiberiaPropertyBoundary[]
  property_deeds: LiberiaPropertyDeed[]
}

export interface LiberiaAnnouncement {
  id: string
  subject: string
  message: string
  status: 'active' | 'inactive'
  priority: 'normal' | 'urgent' | 'emergency'
  category?: string | null
  announcement_type?: string | null
  target_geoblocs?: string[] | null
  affected_area_description?: string | null
  detour_description?: string | null
  estimated_delay_minutes?: number | null
  work_start_date?: string | null
  work_end_date?: string | null
  /** Null means the announcement is for every country. */
  country_id?: string | null
  created_at: string
  updated_at: string
}

export interface LiberiaSupportSubmission {
  id: string
  name: string
  email: string
  phone?: string | null
  organization?: string | null
  subject: string
  message: string
  submission_type: string
  status?: string | null
  admin_response?: string | null
  responded_at?: string | null
  responded_by?: string | null
  country_id?: string | null
  country_name?: string | null
  created_at: string
  updated_at: string
}

export interface LiberiaUserNotification {
  id: string
  user_id: string
  title: string
  message: string
  notification_type: string
  data?: Record<string, unknown> | null
  is_read: boolean
  created_at: string
  updated_at: string
}

export interface LiberiaPartnerUser {
  id: string
  full_name: string
  email: string
  role: string
}

export interface LiberiaProject {
  id: string
  name: string
  description: string | null
  partner_id: string
  status: 'active' | 'inactive' | 'completed' | 'archived'
  country_id: string | null
  administrative_area_id: string | null
  created_at: string
  updated_at: string
}

export interface LiberiaProjectMember {
  id: string
  user_id: string
  role: string
  status: string
  joined_at: string
  user_profile?: {
    email: string
    full_name: string | null
  }
}

export interface LiberiaSystemSetting {
  id: string
  setting_key: string
  setting_value: string
  setting_type: string
  description?: string | null
  created_at: string
  updated_at: string
}

export interface LiberiaSystemHealth {
  database: string
  totalAddresses: number
  recentAddresses: number
  lastChecked: string
  error?: string
}

export interface LiberiaAdministrativeArea {
  id: string
  name: string
  type: string
  level: number
  parent_id: string | null
  country_id: string | null
  iso_code: string | null
  population: number | null
  area_km2: number | null
  coordinates: {
    latitude: number
    longitude: number
  } | null
  boundaries: unknown
  metadata: Record<string, unknown>
  is_active: boolean
  created_at: string
  updated_at: string
  country?: {
    id: string
    name: string
    code: string
  } | null
  parent?: {
    id: string
    name: string
    type: string
  } | null
}

export interface LiberiaAdminUser {
  id: string
  email: string
  full_name: string | null
  role: string
  coarse_role: string
  primary_portal_role: string
  is_qls_admin?: boolean
  country_id?: string | null
  country_name?: string | null
  country_code?: string | null
  is_provisioned_account: boolean
  must_change_password: boolean
  mfa_enabled?: boolean
  mfa_required?: boolean
  provisioned_at: string | null
  created_at: string
  updated_at: string
  roles: string[]
  partner_profile: null
  leRoles: string[]
  emsRoles: string[]
  fireRoles: string[]
  disasterRoles: string[]
  roadRoles: string[]
  wasteRoles: string[]
  taxRoles: string[]
  censusRoles: string[]
  permitRoles: string[]
  propertyMgmtRoles: string[]
  realEstateRoles: string[]
  tourismRoles: string[]
  educationRoles: string[]
  healthRoles: string[]
  socialRoles: string[]
  utilityRoles: string[]
  businessRegRoles: string[]
  logisticsRoles: string[]
  communityMappingRoles: string[]
}

export interface LiberiaCurrentUser {
  id: string
  email: string | null
  fullName: string | null
  primaryRole: string | null
  coarseRole: string | null
  roles: string[]
  mustChangePassword: boolean
  status: string
  countryId?: string | null
  activeCountryId?: string | null
  countryCode?: string | null
  isQlsAdmin?: boolean
}

export interface TenancyCountry {
  id: string
  name: string
  code: string
  status: string
}

export interface LiberiaUserProfile {
  id: string
  email: string
  full_name: string
  phone_number: string
  city: string
  county: string
  state: string
  country: string
  created_at: string
  updated_at: string
}

export interface LiberiaNotificationPreferences {
  id: string
  user_id: string
  weekly_reports: boolean
  project_invitations: boolean
  address_validations: boolean
  system_updates: boolean
  created_at: string
  updated_at: string
}

export interface LiberiaAuthSession {
  authenticated: boolean
  expiresAt: string | null
  isSessionValid: boolean
  user: LiberiaCurrentUser | null
  mfaRequired?: boolean
  challengeToken?: string
  challengeExpiresAt?: string | null
}

export interface LiberiaRoleAccess {
  role: string
  subRoles?: string[]
  permissions?: string[]
  source?: string
  leRoles?: string[]
  emsRoles?: string[]
  fireRoles?: string[]
  disasterRoles?: string[]
  utilityRoles?: string[]
  roadRoles?: string[]
  wasteRoles?: string[]
  taxRoles?: string[]
  censusRoles?: string[]
  permitRoles?: string[]
  propertyMgmtRoles?: string[]
  realEstateRoles?: string[]
  logisticsRoles?: string[]
  tourismRoles?: string[]
  educationRoles?: string[]
  healthRoles?: string[]
  socialServicesRoles?: string[]
  businessRegistrationRoles?: string[]
  businessRegistrationAdminRoles?: string[]
  communityMappingRoles?: string[]
}

const liberiaApiBaseUrl = import.meta.env.VITE_LIBERIA_API_BASE_URL?.replace(/\/$/, '') || ''

const toNullableString = (value: unknown) => {
  return typeof value === 'string' && value.trim().length > 0 ? value : null
}

export const isLiberiaApiEnabled = () => Boolean(liberiaApiBaseUrl)

export const getLiberiaApiBaseUrl = () => liberiaApiBaseUrl || null

export const mapBackendAddressToGeneratedAddress = (address: any): GeneratedAddress => {
  const addressSource =
    address?.addressSource === 'official'
      ? 'google'
      : address?.addressSource === 'new'
        ? 'qls'
        : (address?.addressSource || 'qls')

  const latitude = Number(address?.latitude ?? address?.coordinates?.latitude ?? 0)
  const longitude = Number(address?.longitude ?? address?.coordinates?.longitude ?? 0)

  return {
    id: toNullableString(address?.id) || '',
    user_id: toNullableString(address?.userId) || '',
    address_source: addressSource,
    source_id: toNullableString(address?.sourceId) || '',
    formatted_address: toNullableString(address?.formattedAddress) || '',
    building_number: toNullableString(address?.buildingNumber) || '',
    street_name: toNullableString(address?.streetName) || '',
    city: toNullableString(address?.city) || '',
    district: toNullableString(address?.district) || '',
    county: toNullableString(address?.county) || '',
    state: toNullableString(address?.state) || '',
    country: toNullableString(address?.country) || '',
    postal_code: toNullableString(address?.postalCode) || '',
    zipcode: toNullableString(address?.zipcode),
    plus_code: toNullableString(address?.plusCode),
    short_plus_code: toNullableString(address?.plusCodeShort),
    coordinates: address?.coordinates || {
      latitude,
      longitude,
    },
    geobloc: toNullableString(address?.geobloc),
    geobloc_id: toNullableString(address?.geoblocId),
    latitude,
    longitude,
    structure_type: toNullableString(address?.structureType) || '',
    structure_name: toNullableString(address?.structureName),
    structure_description: toNullableString(address?.structureDescription),
    additional_info: toNullableString(address?.additionalInfo),
    raw_source_data: address?.rawSourceData || {},
    location_types: address?.locationTypes || null,
    google_maps_url: null,
    validation_status: address?.validationStatus || 'pending',
    validated_by: toNullableString(address?.validatedBy),
    validated_at: address?.validatedAt ? String(address.validatedAt) : null,
    project_id: toNullableString(address?.projectId),
    created_at: address?.createdAt || new Date().toISOString(),
    updated_at: address?.updatedAt || new Date().toISOString(),
  }
}

const mapBackendAddressToRegistryAddress = (address: any): LiberiaRegistryAddress => {
  const mapped = mapBackendAddressToGeneratedAddress(address) as LiberiaRegistryAddress
  const createdBy = address?.createdByUser
  const project = address?.project

  if (createdBy?.fullName || createdBy?.email) {
    mapped.user_profiles = {
      full_name: createdBy.fullName || null,
      email: createdBy.email || null,
    }
  }

  if (project?.id || project?.name || project?.partnerId || project?.partnerUser?.fullName || project?.partnerUser?.email) {
    mapped.projects = {
      id: project?.id || null,
      name: project?.name || null,
      partner_id: project?.partnerId || null,
      user_profiles: project?.partnerUser?.fullName || project?.partnerUser?.email
        ? {
            full_name: project.partnerUser.fullName || null,
            email: project.partnerUser.email || null,
          }
        : null,
    }
  }

  return mapped
}

const mapLiberiaAnalysisResult = (analysis: any): LiberiaAnalysisResult => ({
  analysisId: analysis.analysisId,
  duplicateAddress: analysis.duplicateAddress
    ? mapBackendAddressToGeneratedAddress(analysis.duplicateAddress)
    : null,
  boundaryConflict: analysis.boundaryConflict || null,
  officialAddress: analysis.officialAddress || null,
  nearbyAddresses: Array.isArray(analysis.nearbyAddresses)
    ? analysis.nearbyAddresses.map(mapBackendAddressToGeneratedAddress)
    : [],
  recommendation: {
    mode: analysis.recommendation?.mode || 'new',
    reason: analysis.recommendation?.reason || 'unknown',
  },
})

const mapLiberiaDraftResult = (draft: any): LiberiaDraftResult => ({
  draftId: draft.draftId,
  address: mapBackendAddressToGeneratedAddress(draft.address),
})

const buildAddressGenerationPermissions = (context: LiberiaApiAuthContext) => {
  const permissions = ['address_generation:read']

  if (context.hasAddressGenerationAccess || context.role === 'admin' || context.role === 'partner') {
    permissions.push('address_generation:create')
  }

  return permissions
}

/** Match backend announcement checks: lowercase, hyphen/space → underscore */
const normalizeDevRoleSlugForHeader = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/-/g, '_')

function publicCountryHeaders(): Record<string, string> {
  const countryId = getPublicCountryIdForApi()
  return countryId ? { 'x-qls-country-id': countryId } : {}
}

const buildLiberiaApiHeaders = (context?: LiberiaApiAuthContext) => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...publicCountryHeaders(),
  }

  if (!context?.user) {
    return headers
  }

  headers['x-dev-user-id'] = context.user.id

  if (context.user.email) {
    headers['x-dev-user-email'] = context.user.email
  }

  const metadataRoles = Array.isArray(context.user.user_metadata?.roles)
    ? context.user.user_metadata.roles.filter((role): role is string => typeof role === 'string' && role.length > 0)
    : []
  const portalRolesFromAccess = Array.isArray(context.portalRolesForHeaders)
    ? context.portalRolesForHeaders.filter((role): role is string => typeof role === 'string' && role.length > 0)
    : []
  // Always union metadata, active portal (context.role), and /me/roles portals. Metadata can lag;
  // context.role can be null until /me + /me/roles finish; missing x-dev-roles causes 403 on admin announce.
  const resolvedRoles = Array.from(
    new Set(
      [...metadataRoles, ...(context.role ? [context.role] : []), ...portalRolesFromAccess]
        .filter((role): role is string => typeof role === 'string' && role.length > 0)
        .map(normalizeDevRoleSlugForHeader)
    )
  )

  if (resolvedRoles.length > 0) {
    headers['x-dev-roles'] = resolvedRoles.join(',')
  }

  headers['x-dev-permissions'] = buildAddressGenerationPermissions(context).join(',')

  return headers
}

const buildLiberiaApiHeadersWithoutContentType = (context?: LiberiaApiAuthContext) => {
  const headers = buildLiberiaApiHeaders(context)
  delete headers['Content-Type']
  return headers
}

async function liberiaApiRequest<T>(
  path: string,
  {
    method = 'GET',
    body,
    context,
  }: {
    method?: string
    body?: unknown
    context?: LiberiaApiAuthContext
  } = {}
): Promise<T> {
  if (!liberiaApiBaseUrl) {
    throw new Error('QLS API base URL is not configured')
  }

  const response = await fetch(`${liberiaApiBaseUrl}${path}`, {
    method,
    credentials: 'include',
    headers: buildLiberiaApiHeaders(context),
    body: body !== undefined ? JSON.stringify(body) : undefined,
  })

  const payload = await response.json().catch(() => null)

  if (!response.ok) {
    throw new LiberiaApiError(payload?.error?.message || `QLS API request failed (${response.status})`, {
      code: payload?.error?.code,
      status: response.status,
      details: payload?.error?.details,
    })
  }

  return payload?.data as T
}

export async function liberiaAuthRequest<T>(
  path: string,
  {
    method = 'GET',
    body,
  }: {
    method?: string
    body?: unknown
  } = {}
): Promise<T> {
  if (!liberiaApiBaseUrl) {
    throw new Error('QLS API base URL is not configured')
  }

  const response = await fetch(`${liberiaApiBaseUrl}${path}`, {
    method,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...publicCountryHeaders(),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  })

  const payload = await response.json().catch(() => null)

  if (!response.ok) {
    throw new Error(payload?.error?.message || `QLS API request failed (${response.status})`)
  }

  return payload?.data as T
}

export const loginLiberiaAuth = async (email: string, password: string) => {
  return liberiaAuthRequest<LiberiaAuthSession>('/api/v1/auth/login', {
    method: 'POST',
    body: { email, password },
  })
}

export const verifyLiberiaMfaLogin = async (challengeToken: string, code: string) => {
  return liberiaAuthRequest<LiberiaAuthSession>('/api/v1/auth/mfa/verify', {
    method: 'POST',
    body: { challengeToken, code },
  })
}

export interface LiberiaMfaStatus {
  enabled: boolean
  enabledAt?: string | null
  lastVerifiedAt?: string | null
  mustEnrollMfa?: boolean
  requireAdminMfa?: boolean
}

export interface LiberiaMfaSetupResponse {
  otpauthUrl: string
  issuer: string
  secret?: string
}

export const getLiberiaMfaStatus = async (context: LiberiaApiAuthContext) => {
  return liberiaApiRequest<LiberiaMfaStatus>('/api/v1/auth/mfa/status', { context })
}

export const setupLiberiaMfa = async (context: LiberiaApiAuthContext) => {
  return liberiaApiRequest<LiberiaMfaSetupResponse>('/api/v1/auth/mfa/setup', {
    method: 'POST',
    context,
  })
}

export const enableLiberiaMfa = async (code: string, context: LiberiaApiAuthContext) => {
  return liberiaApiRequest<{ enabled: boolean }>('/api/v1/auth/mfa/enable', {
    method: 'POST',
    body: { code },
    context,
  })
}

export const disableLiberiaMfa = async (code: string, context: LiberiaApiAuthContext) => {
  return liberiaApiRequest<{ enabled: boolean }>('/api/v1/auth/mfa/disable', {
    method: 'POST',
    body: { code },
    context,
  })
}

export const logoutLiberiaAuth = async () => {
  return liberiaAuthRequest<{ loggedOut: boolean }>('/api/v1/auth/logout', {
    method: 'POST',
  })
}

export const getLiberiaAuthSession = async () => {
  return liberiaAuthRequest<{
    authenticated: boolean
    expiresAt: string | null
    isSessionValid: boolean
    userId: string | null
  }>('/api/v1/auth/session')
}

export const touchLiberiaSessionActivity = async (body: {
  country?: string | null
  city?: string | null
  deviceInfo?: Record<string, unknown> | null
}) => {
  return liberiaAuthRequest<{
    updated: boolean
    sessionId: string | null
    lastActivity: string | null
  }>('/api/v1/auth/session/activity', {
    method: 'POST',
    body,
  })
}

export const createLiberiaLocationCaptureToken = async (
  body: {
    incident_id?: string | null
    incident_number?: string | null
    expires_at?: string | null
  },
  context?: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaLocationCaptureToken>('/api/v1/location-capture/tokens', {
    method: 'POST',
    body,
    context,
  })
}

export const getLiberiaLocationCaptureToken = async (
  token: string,
  context?: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaLocationCaptureToken>(
    `/api/v1/location-capture/tokens/${encodeURIComponent(token)}`,
    { context }
  )
}

export const submitLiberiaCapturedCallerLocation = async (
  token: string,
  latitude: number,
  longitude: number
) => {
  return liberiaApiRequest<{
    incident_number: string | null
    token: string
    status: string
  }>('/api/v1/location-capture/capture', {
    method: 'POST',
    body: {
      token,
      latitude,
      longitude,
    },
  })
}

export const sendLiberiaLocationCaptureSms = async (
  token: string,
  phoneNumber: string,
  context?: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<{
    success: boolean
    messageStatus: string
    messageSid: string
    twilio_message_sid?: string | null
    twilio_message_status?: string | null
  }>(`/api/v1/location-capture/tokens/${encodeURIComponent(token)}/sms`, {
    method: 'POST',
    body: {
      phone_number: phoneNumber,
    },
    context,
  })
}

export const getLiberiaLocationCaptureSmsStatus = async (
  messageSid: string,
  context?: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaLocationCaptureSmsStatus>(
    `/api/v1/location-capture/sms/${encodeURIComponent(messageSid)}`,
    { context }
  )
}

export const registerLiberiaAuth = async (
  email: string,
  password: string,
  fullName?: string,
  countryCode?: string
) => {
  return liberiaAuthRequest<LiberiaAuthSession>('/api/v1/auth/register', {
    method: 'POST',
    body: {
      email,
      password,
      fullName,
      countryCode,
    },
  })
}

export const listLiveTenancyCountries = async () => {
  return liberiaAuthRequest<TenancyCountry[]>('/api/v1/tenancy/countries')
}

/** Public: live country for the visitor IP, or null when it is not in the catalog. */
export const guessVisitorTenancyCountry = async () => {
  return liberiaAuthRequest<TenancyCountry | null>('/api/v1/tenancy/visitor-country')
}

export const switchTenancyCountry = async (
  countryId: string,
  context?: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<{ switched: boolean; country: TenancyCountry }>('/api/v1/tenancy/switch', {
    method: 'POST',
    body: { countryId },
    context,
  })
}

export const createLiveTenancyCountry = async (
  body: { name: string; code: string },
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<TenancyCountry>('/api/v1/tenancy/countries', {
    method: 'POST',
    body,
    context,
  })
}

export const launchTenancyCountry = async (
  body: {
    name: string
    code: string
    city: string
    structureType?: string
    buildingTypes?: string[]
    maxBuildings?: number
    switchSession?: boolean
  },
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<{
    country: TenancyCountry
    batch: { batchId: string; generatedCount: number; message: string }
    switched: boolean
  }>('/api/v1/tenancy/launch', {
    method: 'POST',
    body,
    context,
  })
}

export const changeLiberiaPassword = async (newPassword: string, context?: LiberiaApiAuthContext) => {
  return liberiaApiRequest<{ changed: boolean }>('/api/v1/auth/password/change', {
    method: 'POST',
    body: { newPassword },
    context,
  })
}

const GENERIC_PASSWORD_RESET_MESSAGE =
  'If that email is on file, we sent a reset link. Check your inbox.'

export const requestLiberiaPasswordReset = async (email: string) => {
  const result = await liberiaAuthRequest<{ requested: boolean; message?: string }>(
    '/api/v1/auth/password/reset-request',
    {
      method: 'POST',
      body: { email },
    }
  )
  return {
    requested: Boolean(result?.requested),
    message: result?.message || GENERIC_PASSWORD_RESET_MESSAGE,
  }
}

export const completeLiberiaPasswordReset = async (token: string, newPassword: string) => {
  return liberiaAuthRequest<{ reset: boolean }>('/api/v1/auth/password/reset', {
    method: 'POST',
    body: { token, newPassword },
  })
}

export const analyzeLiberiaAddressGeneration = async (
  {
    latitude,
    longitude,
    structureType,
  }: {
    latitude: number
    longitude: number
    structureType: string
  },
  context: LiberiaApiAuthContext
) => {
  const data = await liberiaApiRequest<any>('/api/v1/address-generation/analyze', {
    method: 'POST',
    body: {
      latitude,
      longitude,
      structureType,
    },
    context,
  })

  return mapLiberiaAnalysisResult(data)
}

export const generateLiberiaAddressDraft = async (
  {
    analysisId,
    mode,
    additionalInfo,
    projectId,
    structureName,
    structureDescription,
  }: {
    analysisId: string
    mode: 'official' | 'enhanced' | 'new'
    additionalInfo: string
    projectId: string | null
    structureName?: string
    structureDescription?: string
  },
  context: LiberiaApiAuthContext
) => {
  const data = await liberiaApiRequest<any>('/api/v1/address-generation/generate', {
    method: 'POST',
    body: {
      analysisId,
      mode,
      additionalInfo,
      projectId,
      structureName,
      structureDescription,
    },
    context,
  })

  return mapLiberiaDraftResult(data)
}

export const reverseLiberiaGeocode = async (
  latitude: number,
  longitude: number
) => {
  return liberiaApiRequest<any>('/api/v1/geocode/reverse', {
    method: 'POST',
    body: {
      latitude,
      longitude,
    },
  })
}

export const forwardLiberiaGeocode = async (address: string) => {
  return liberiaApiRequest<any[]>('/api/v1/geocode/forward', {
    method: 'POST',
    body: {
      address,
    },
  })
}

export const getLiberiaReferenceLocation = async ({
  cityHint,
  stateHint,
  countryHint,
  coordinates,
}: {
  cityHint?: string
  stateHint?: string
  countryHint?: string
  coordinates?: { latitude: number; longitude: number } | null
}) => {
  return liberiaApiRequest<LiberiaReferenceLocation>('/api/v1/geocode/reference-location', {
    method: 'POST',
    body: {
      cityHint,
      stateHint,
      countryHint,
      coordinates: coordinates || null,
    },
  })
}

export const listLiberiaSnoocodeRecords = async (
  {
    status,
    integrationStatus,
    limit = 1000,
  }: {
    status?: string
    integrationStatus?: string
    limit?: number
  },
  context: LiberiaApiAuthContext
) => {
  const params = new URLSearchParams()
  if (status) params.set('status', status)
  if (integrationStatus) params.set('integrationStatus', integrationStatus)
  params.set('limit', String(limit))

  return liberiaApiRequest<LiberiaSnoocodeRecord[]>(`/api/v1/admin/location-tools/snoocode?${params.toString()}`, {
    context,
  })
}

export const getLiberiaSnoocodeSummary = async (context: LiberiaApiAuthContext) => {
  return liberiaApiRequest<LiberiaSnoocodeSummary>('/api/v1/admin/location-tools/snoocode/summary', {
    context,
  })
}

export const uploadLiberiaSnoocodeRecords = async (
  {
    fileName,
    fileSize,
    records,
  }: {
    fileName: string
    fileSize?: number | null
    records: Array<Record<string, unknown>>
  },
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<{ inserted: number; fileName: string }>('/api/v1/admin/location-tools/snoocode/upload', {
    method: 'POST',
    body: {
      fileName,
      fileSize: fileSize || null,
      records,
    },
    context,
  })
}

export const updateLiberiaSnoocodeRecord = async (
  id: string,
  updates: Record<string, unknown>,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaSnoocodeRecord>(`/api/v1/admin/location-tools/snoocode/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: updates,
    context,
  })
}

export const captureLiberiaPoiData = async (
  {
    latitude,
    longitude,
    addressId,
    radiusMeters = 1000,
  }: {
    latitude: number
    longitude: number
    addressId?: string
    radiusMeters?: number
  },
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<any>('/api/v1/admin/location-tools/poi/capture', {
    method: 'POST',
    body: {
      latitude,
      longitude,
      addressId,
      radiusMeters,
    },
    context,
  })
}

export const listLiberiaPoiStaging = async (
  {
    status,
    addressId,
    limit,
  }: {
    status?: string
    addressId?: string
    limit?: number
  },
  context: LiberiaApiAuthContext
) => {
  const params = new URLSearchParams()
  if (status) params.set('status', status)
  if (addressId) params.set('addressId', addressId)
  if (typeof limit === 'number') params.set('limit', String(limit))

  return liberiaApiRequest<LiberiaPoiStagingRecord[]>(`/api/v1/admin/location-tools/poi?${params.toString()}`, {
    context,
  })
}

export const updateLiberiaPoiStaging = async (
  id: string,
  updates: Record<string, unknown>,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaPoiStagingRecord>(`/api/v1/admin/location-tools/poi/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: updates,
    context,
  })
}

export const generateLiberiaPlusCode = async (latitude: number, longitude: number) => {
  return liberiaApiRequest<LiberiaPlusCodeResult>('/api/v1/geocode/plus-code', {
    method: 'POST',
    body: {
      latitude,
      longitude,
    },
  })
}

export const decodeLiberiaPlusCode = async (
  plusCode: string,
  referenceLocation?: { latitude: number; longitude: number; source?: string | null } | null
) => {
  return liberiaApiRequest<LiberiaDecodedPlusCodeResult>('/api/v1/geocode/plus-code', {
    method: 'POST',
    body: {
      action: 'decode',
      plusCode,
      referenceLocation: referenceLocation || null,
    },
  })
}

export const checkLiberiaDuplicateAddress = async (
  latitude: number,
  longitude: number,
  countryCode?: string | null
) => {
  const params = new URLSearchParams({
    latitude: String(latitude),
    longitude: String(longitude),
  })
  if (countryCode) {
    params.set('countryCode', countryCode)
  }
  const data = await liberiaApiRequest<{
    duplicate: any | null
    nearby: any[]
  }>(
    `/api/v1/address-generation/public/duplicate-check?${params.toString()}`
  )

  return {
    duplicate: data?.duplicate ? mapBackendAddressToGeneratedAddress(data.duplicate) : null,
    nearby: Array.isArray(data?.nearby) ? data.nearby.map(mapBackendAddressToGeneratedAddress) : [],
  }
}

export const saveLiberiaGeneratedAddress = async (
  draftId: string,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<{
    id: string
    formattedAddress: string
    addressSource: string
    validationStatus: string
    existing: boolean
    persistence: string
  }>('/api/v1/generated-addresses', {
    method: 'POST',
    body: { draftId },
    context,
  })
}

export const createLiberiaGeneratedAddressDirect = async (
  address: Record<string, unknown>,
  context: LiberiaApiAuthContext
) => {
  const data = await liberiaApiRequest<any>('/api/v1/generated-addresses/direct', {
    method: 'POST',
    body: { address },
    context,
  })

  return mapBackendAddressToGeneratedAddress(data)
}

export const getLiberiaGeneratedAddress = async (
  addressId: string,
  context: LiberiaApiAuthContext
) => {
  const data = await liberiaApiRequest<any>(`/api/v1/generated-addresses/${addressId}`, {
    context,
  })

  return mapBackendAddressToGeneratedAddress(data)
}

export const getLiberiaAddressDeliverability = async (addressId: string) => {
  return liberiaApiRequest<LiberiaDeliverabilityScore>(
    `/api/v1/generated-addresses/${encodeURIComponent(addressId)}/deliverability`
  )
}

export const listLiberiaGeneratedAddresses = async (
  {
    search = '',
    status = 'all',
    source = 'all',
    limit = 250,
  }: {
    search?: string
    status?: string
    source?: string
    limit?: number
  },
  context: LiberiaApiAuthContext
) => {
  const params = new URLSearchParams()

  if (search.trim()) params.set('search', search.trim())
  if (status && status !== 'all') params.set('status', status)
  if (source && source !== 'all') params.set('source', source)
  params.set('limit', String(limit))

  const data = await liberiaApiRequest<any[]>(`/api/v1/generated-addresses?${params.toString()}`, {
    context,
  })

  return Array.isArray(data) ? data.map(mapBackendAddressToRegistryAddress) : []
}

export const listLiberiaMyGeneratedAddresses = async (
  {
    limit = 250,
  }: {
    limit?: number
  },
  context: LiberiaApiAuthContext
) => {
  const data = await liberiaApiRequest<any[]>(`/api/v1/generated-addresses/mine?limit=${encodeURIComponent(String(limit))}`, {
    context,
  })

  return Array.isArray(data) ? data.map(mapBackendAddressToRegistryAddress) : []
}

export const listLiberiaPartnerScopeGeneratedAddresses = async (
  {
    limit = 500,
  }: {
    limit?: number
  },
  context: LiberiaApiAuthContext
) => {
    const data = await liberiaApiRequest<any[]>(`/api/v1/generated-addresses/partner-scope?limit=${encodeURIComponent(String(limit))}`, {
      context,
    })

    return Array.isArray(data) ? data.map(mapBackendAddressToRegistryAddress) : []
}

export const listLiberiaProjectGeneratedAddresses = async (
  projectId: string,
  {
    limit = 500,
  }: {
    limit?: number
  },
  context: LiberiaApiAuthContext
) => {
  const data = await liberiaApiRequest<any[]>(`/api/v1/generated-addresses/project/${encodeURIComponent(projectId)}?limit=${encodeURIComponent(String(limit))}`, {
    context,
  })

  return Array.isArray(data) ? data.map(mapBackendAddressToRegistryAddress) : []
}

export const listLiberiaPropertyOwners = async (context: LiberiaApiAuthContext) => {
  return liberiaApiRequest<LiberiaPropertyOwner[]>('/api/v1/property/owners', {
    context,
  })
}

export const createLiberiaPropertyOwner = async (
  body: Record<string, unknown>,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaPropertyOwner>('/api/v1/property/owners', {
    method: 'POST',
    body,
    context,
  })
}

// Returns user accounts eligible to be linked as a property owner. Powers
// the user picker in AddPropertyOwnerDialog. The selected user gets the
// `taxpayer` role auto-granted when the ownership is saved.
export const listLiberiaLinkableOwnerUsers = async (
  searchTerm: string | undefined,
  context: LiberiaApiAuthContext
) => {
  const suffix = searchTerm?.trim()
    ? `?search=${encodeURIComponent(searchTerm.trim())}`
    : ''
  return liberiaApiRequest<LiberiaLinkableOwnerUser[]>(
    `/api/v1/property/owners/linkable-users${suffix}`,
    { context }
  )
}

export const listLiberiaPropertyOwnership = async (
  addressId: string | undefined,
  context: LiberiaApiAuthContext
) => {
  const suffix = addressId ? `?addressId=${encodeURIComponent(addressId)}` : ''
  return liberiaApiRequest<LiberiaPropertyOwnership[]>(`/api/v1/property/ownership${suffix}`, {
    context,
  })
}

export const createLiberiaPropertyOwnership = async (
  body: Record<string, unknown>,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaPropertyOwnership>('/api/v1/property/ownership', {
    method: 'POST',
    body,
    context,
  })
}

export const listLiberiaPropertyRegistry = async (
  searchTerm: string | undefined,
  context: LiberiaApiAuthContext
) => {
  const suffix = searchTerm?.trim()
    ? `?search=${encodeURIComponent(searchTerm.trim())}`
    : ''

  return liberiaApiRequest<LiberiaPropertyRegistryEntry[]>(`/api/v1/property/registry${suffix}`, {
    context,
  })
}

export const getLiberiaPropertyStats = async (context: LiberiaApiAuthContext) => {
  return liberiaApiRequest<{
    owners: number
    deeds: number
    boundaries: number
    properties: number
  }>('/api/v1/property/stats', { context })
}

export const listLiberiaPropertyDeedTypes = async (context: LiberiaApiAuthContext) => {
  return liberiaApiRequest<LiberiaPropertyDeedType[]>('/api/v1/property/deed-types', { context })
}

export const listLiberiaPropertyDeedAddresses = async (
  searchTerm: string | undefined,
  context: LiberiaApiAuthContext
) => {
  const suffix = searchTerm?.trim()
    ? `?search=${encodeURIComponent(searchTerm.trim())}`
    : ''

  return liberiaApiRequest<LiberiaPropertyAddressDetails[]>(`/api/v1/property/deeds${suffix}`, {
    context,
  })
}

export const listLiberiaPropertyDeedsForAddress = async (
  addressId: string,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaPropertyDeed[]>(
    `/api/v1/property/deeds?addressId=${encodeURIComponent(addressId)}`,
    { context }
  )
}

export const getLiberiaPropertyDeed = async (
  deedId: string,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaPropertyDeed>(`/api/v1/property/deeds/${encodeURIComponent(deedId)}`, {
    context,
  })
}

export const createLiberiaPropertyDeed = async (
  body: Record<string, unknown>,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaPropertyDeed>('/api/v1/property/deeds', {
    method: 'POST',
    body,
    context,
  })
}

export const updateLiberiaPropertyDeed = async (
  deedId: string,
  body: Record<string, unknown>,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaPropertyDeed>(`/api/v1/property/deeds/${encodeURIComponent(deedId)}`, {
    method: 'PATCH',
    body,
    context,
  })
}

export const listLiberiaDeedDocuments = async (
  deedId: string,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaDeedDocument[]>(
    `/api/v1/property/deeds/${encodeURIComponent(deedId)}/documents`,
    { context }
  )
}

export const createLiberiaDeedDocument = async (
  deedId: string,
  body: Record<string, unknown>,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaDeedDocument>(
    `/api/v1/property/deeds/${encodeURIComponent(deedId)}/documents`,
    {
      method: 'POST',
      body,
      context,
    }
  )
}

export const uploadLiberiaDeedDocument = async (
  deedId: string,
  file: File,
  body: {
    document_number?: string | null
    document_date?: string | null
    notes?: string | null
  },
  context: LiberiaApiAuthContext
) => {
  if (!liberiaApiBaseUrl) {
    throw new Error('QLS API base URL is not configured')
  }

  const formData = new FormData()
  formData.append('file', file)
  if (body.document_number) formData.append('document_number', body.document_number)
  if (body.document_date) formData.append('document_date', body.document_date)
  if (body.notes) formData.append('notes', body.notes)

  const response = await fetch(
    `${liberiaApiBaseUrl}/api/v1/property/deeds/${encodeURIComponent(deedId)}/documents/upload`,
    {
      method: 'POST',
      credentials: 'include',
      headers: buildLiberiaApiHeadersWithoutContentType(context),
      body: formData,
    }
  )

  const payload = await response.json().catch(() => null)

  if (!response.ok) {
    throw new Error(payload?.error?.message || `QLS API request failed (${response.status})`)
  }

  return payload?.data as LiberiaDeedDocument
}

export const downloadLiberiaDeedDocument = async (
  documentId: string,
  context: LiberiaApiAuthContext
) => {
  if (!liberiaApiBaseUrl) {
    throw new Error('QLS API base URL is not configured')
  }

  const response = await fetch(
    `${liberiaApiBaseUrl}/api/v1/property/deed-documents/${encodeURIComponent(documentId)}/download`,
    {
      method: 'GET',
      credentials: 'include',
      headers: buildLiberiaApiHeadersWithoutContentType(context),
    }
  )

  if (!response.ok) {
    const payload = await response.json().catch(() => null)
    throw new Error(payload?.error?.message || `QLS API request failed (${response.status})`)
  }

  return response.blob()
}

export const getLiberiaPropertyAddressDetails = async (
  addressId: string,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaPropertyAddressDetails>(
    `/api/v1/property/addresses/${encodeURIComponent(addressId)}`,
    { context }
  )
}

export const listLiberiaPropertyBoundaryQueue = async (
  searchTerm: string | undefined,
  context: LiberiaApiAuthContext
) => {
  const suffix = searchTerm?.trim()
    ? `?search=${encodeURIComponent(searchTerm.trim())}`
    : ''

  return liberiaApiRequest<LiberiaPropertyAddressDetails[]>(`/api/v1/property/boundaries${suffix}`, {
    context,
  })
}

export const getLiberiaPublicBoundary = async (addressId: string) => {
  return liberiaApiRequest<LiberiaPropertyBoundary | null>(
    `/api/v1/property/public/addresses/${encodeURIComponent(addressId)}/boundary`
  )
}

export const saveLiberiaPropertyBoundary = async (
  body: Record<string, unknown>,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaPropertyBoundary>('/api/v1/property/boundaries', {
    method: 'POST',
    body,
    context,
  })
}

export const listLiberiaSurveyDocuments = async (
  params: {
    addressId?: string
    documentType?: string
  },
  context: LiberiaApiAuthContext
) => {
  const query = new URLSearchParams()
  if (params.addressId) query.set('addressId', params.addressId)
  if (params.documentType) query.set('documentType', params.documentType)
  const suffix = query.toString() ? `?${query.toString()}` : ''

  return liberiaApiRequest<LiberiaSurveyDocument[]>(`/api/v1/property/survey-documents${suffix}`, {
    context,
  })
}

export const createLiberiaSurveyDocument = async (
  body: Record<string, unknown>,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaSurveyDocument>('/api/v1/property/survey-documents', {
    method: 'POST',
    body,
    context,
  })
}

// Partial update for an existing survey document. Only include the fields you
// want to change; anything omitted stays untouched on the server.
export const updateLiberiaSurveyDocument = async (
  id: string,
  body: Record<string, unknown>,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaSurveyDocument>(
    `/api/v1/property/survey-documents/${id}`,
    {
      method: 'PATCH',
      body,
      context,
    }
  )
}

// Hard-deletes a survey document by id. Surfaces the backend's 404 when the
// document is already gone so the caller can show a friendly message.
export const deleteLiberiaSurveyDocument = async (
  id: string,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<{ id: string; deleted: boolean }>(
    `/api/v1/property/survey-documents/${id}`,
    {
      method: 'DELETE',
      context,
    }
  )
}

// Uploads one or more photos for a survey document. Files are sent via
// multipart/form-data under the `photos` field so multer can collect them
// into `req.files`. Returns the created photo metadata rows.
export const uploadLiberiaSurveyDocumentPhotos = async (
  surveyDocumentId: string,
  files: File[],
  context: LiberiaApiAuthContext
) => {
  if (!liberiaApiBaseUrl) {
    throw new Error('QLS API base URL is not configured')
  }
  if (!files || files.length === 0) {
    return [] as LiberiaSurveyDocumentPhoto[]
  }

  const formData = new FormData()
  for (const file of files) {
    formData.append('photos', file)
  }

  const response = await fetch(
    `${liberiaApiBaseUrl}/api/v1/property/survey-documents/${encodeURIComponent(
      surveyDocumentId
    )}/photos`,
    {
      method: 'POST',
      credentials: 'include',
      headers: buildLiberiaApiHeadersWithoutContentType(context),
      body: formData,
    }
  )

  const payload = await response.json().catch(() => null)

  if (!response.ok) {
    throw new Error(
      payload?.error?.message || `QLS API request failed (${response.status})`
    )
  }

  return (payload?.data as LiberiaSurveyDocumentPhoto[]) || []
}

// Rename a photo — only the filename is mutable server-side.
export const renameLiberiaSurveyDocumentPhoto = async (
  photoId: string,
  fileName: string,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaSurveyDocumentPhoto>(
    `/api/v1/property/survey-document-photos/${encodeURIComponent(photoId)}`,
    {
      method: 'PATCH',
      body: { file_name: fileName },
      context,
    }
  )
}

// Hard-delete a single photo. Backend surfaces a 404 when the photo is
// already gone so callers can show a friendly message.
export const deleteLiberiaSurveyDocumentPhoto = async (
  photoId: string,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<{ id: string; deleted: boolean }>(
    `/api/v1/property/survey-document-photos/${encodeURIComponent(photoId)}`,
    {
      method: 'DELETE',
      context,
    }
  )
}

// Builds the absolute download URL for a photo so the UI can expose a
// "Location" link and, when desired, render the image via <img src=...> after
// fetching the blob with buildLiberiaApiHeadersWithoutContentType headers.
export const getLiberiaSurveyDocumentPhotoDownloadUrl = (photoId: string) => {
  if (!liberiaApiBaseUrl) return null
  return `${liberiaApiBaseUrl}/api/v1/property/survey-document-photos/${encodeURIComponent(
    photoId
  )}/download`
}

export const updateLiberiaGeneratedAddressValidationStatus = async (
  {
    addressId,
    validationStatus,
  }: {
    addressId: string
    validationStatus: 'validated' | 'invalid' | 'pending'
  },
  context: LiberiaApiAuthContext
) => {
  const data = await liberiaApiRequest<any>(`/api/v1/generated-addresses/${addressId}/validation-status`, {
    method: 'PATCH',
    body: { validationStatus },
    context,
  })

  return mapBackendAddressToRegistryAddress(data)
}

export const bulkUpdateLiberiaGeneratedAddressValidationStatuses = async (
  rows: Array<{
    id: string
    validationStatus: 'validated' | 'invalid' | 'pending'
    notes?: string | null
  }>,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<{
    updated: LiberiaRegistryAddress[]
    failed: Array<{ id: string; message: string }>
  }>('/api/v1/generated-addresses/bulk-validation', {
    method: 'POST',
    body: { rows },
    context,
  })
}

export const updateLiberiaGeneratedAddress = async (
  {
    addressId,
    updates,
  }: {
    addressId: string
    updates: Record<string, unknown>
  },
  context: LiberiaApiAuthContext
) => {
  const data = await liberiaApiRequest<any>(`/api/v1/generated-addresses/${addressId}`, {
    method: 'PATCH',
    body: updates,
    context,
  })

  return mapBackendAddressToRegistryAddress(data)
}

export const updateLiberiaGeneratedAddressSelf = async (
  {
    addressId,
    updates,
  }: {
    addressId: string
    updates: Record<string, unknown>
  },
  context: LiberiaApiAuthContext
) => {
  const data = await liberiaApiRequest<any>(`/api/v1/generated-addresses/${addressId}/self`, {
    method: 'PATCH',
    body: updates,
    context,
  })

  return mapBackendAddressToRegistryAddress(data)
}

export const deleteLiberiaGeneratedAddress = async (
  addressId: string,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<{ id: string; deleted: boolean }>(`/api/v1/generated-addresses/${addressId}`, {
    method: 'DELETE',
    context,
  })
}

export const listLiberiaTaxValidatedAddresses = async (context: LiberiaApiAuthContext) => {
  const data = await liberiaApiRequest<any[]>('/api/v1/tax/validated-addresses', {
    context,
  })

  return Array.isArray(data)
    ? data.map((address) => ({
        id: address.id,
        formatted_address: address.formattedAddress,
        city: address.city || '',
        state: address.state || '',
        district: address.district || '',
      }))
    : []
}

export const listLiberiaTaxLandUseTypes = async (context: LiberiaApiAuthContext) => {
  return liberiaApiRequest<any[]>('/api/v1/tax/land-use-types', { context })
}

export const listLiberiaPaymentProviderConfigs = async (
  context: LiberiaApiAuthContext,
  options: { activeOnly?: boolean } = {}
) => {
  const query = options.activeOnly ? '?active_only=true' : ''
  return liberiaApiRequest<LiberiaPaymentProviderConfig[]>(`/api/v1/tax/payment-providers${query}`, { context })
}

export const updateLiberiaPaymentProviderConfig = async (
  id: string,
  body: Record<string, unknown>,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaPaymentProviderConfig>(`/api/v1/tax/payment-providers/${id}`, {
    method: 'PATCH',
    body,
    context,
  })
}

export const listLiberiaCreditPackages = async (
  context: LiberiaApiAuthContext,
  options: { activeOnly?: boolean } = {}
) => {
  const query = options.activeOnly ? '?active_only=true' : ''
  return liberiaApiRequest<LiberiaCreditPackage[]>(`/api/v1/tax/credit-packages${query}`, { context })
}

export const listPendingBankTransferCreditPurchases = async (context: LiberiaApiAuthContext) => {
  return liberiaApiRequest<Array<{
    id: string
    payment_reference: string
    status: string
    credits: number
    amount: number
    currency: string
    provider: string
    created_at: string
    package_name: string
    buyer_email?: string | null
    buyer_name?: string | null
  }>>('/api/v1/api-access/admin/credit-purchases', { context })
}

export const markBankTransferCreditPurchaseReceived = async (
  purchaseId: string,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<{
    id: string
    status: string
    payment_reference: string
    credits_added: number
    amount: number
    currency: string
    already_completed: boolean
  }>(`/api/v1/api-access/admin/credit-purchases/${purchaseId}/receive`, {
    method: 'POST',
    context,
  })
}

export const listLiberiaAdminCreditPackages = async (context: LiberiaApiAuthContext) => {
  return liberiaApiRequest<LiberiaCreditPackage[]>('/api/v1/admin/setup/credit-packages', { context })
}

export const getLiberiaMyPermissions = async (context: LiberiaApiAuthContext) => {
  return liberiaApiRequest<{ permissions: string[] }>('/api/v1/me/permissions', { context })
}

export const listLiberiaUsersWithFeatureAccess = async (
  feature: LiberiaFeaturePermissionFeature,
  context: LiberiaApiAuthContext
) => {
  // Server mounts admin user routes on `/api/v1/admin` (not `admin-users`).
  return liberiaApiRequest<LiberiaUserFeatureAccess[]>(`/api/v1/admin/permissions/${feature}/users`, { context })
}

export const setLiberiaUserFeatureAccess = async (
  feature: LiberiaFeaturePermissionFeature,
  userId: string,
  body: { active: boolean },
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<any>(`/api/v1/admin/permissions/${feature}/users/${userId}`, {
    method: 'PUT',
    body,
    context,
  })
}

export const listLiberiaPricingModels = async (
  context: LiberiaApiAuthContext,
  options: { activeOnly?: boolean } = {}
) => {
  const query = options.activeOnly ? '?activeOnly=true' : ''
  return liberiaApiRequest<LiberiaPricingModel[]>(`/api/v1/api-access/pricing-models${query}`, { context })
}

export const createLiberiaPricingModel = async (
  body: Omit<LiberiaPricingModel, 'id' | 'created_at' | 'updated_at'>,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaPricingModel>('/api/v1/api-access/admin/pricing-models', {
    method: 'POST',
    body,
    context,
  })
}

export const updateLiberiaPricingModel = async (
  id: string,
  body: Partial<Omit<LiberiaPricingModel, 'id' | 'created_at' | 'updated_at'>>,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaPricingModel>(`/api/v1/api-access/admin/pricing-models/${id}`, {
    method: 'PATCH',
    body,
    context,
  })
}

export const updateLiberiaBusinessPortalPricingModel = async (
  id: string,
  body: Partial<Omit<LiberiaPricingModel, 'id' | 'created_at' | 'updated_at'>>,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaPricingModel>(
    `/api/v1/api-access/business/pricing-models/${encodeURIComponent(id)}`,
    {
      method: 'PATCH',
      body,
      context,
    }
  )
}

export const deleteLiberiaPricingModel = async (
  id: string,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<{ id: string; deleted: boolean }>(`/api/v1/api-access/admin/pricing-models/${id}`, {
    method: 'DELETE',
    context,
  })
}

export const listLiberiaUserApiSubscriptions = async (context: LiberiaApiAuthContext) => {
  return liberiaApiRequest<LiberiaApiSubscription[]>('/api/v1/api-access/subscriptions', { context })
}

export const getLiberiaActiveApiSubscription = async (context: LiberiaApiAuthContext) => {
  return liberiaApiRequest<LiberiaApiSubscription | null>('/api/v1/api-access/subscriptions/active', { context })
}

export const getLiberiaApiSubscriptionAccess = async (context: LiberiaApiAuthContext) => {
  return liberiaApiRequest<LiberiaApiSubscriptionAccess>('/api/v1/api-access/subscriptions/access', { context })
}

export const createLiberiaApiSubscription = async (
  body: Omit<LiberiaApiSubscription, 'id' | 'user_id' | 'created_at' | 'updated_at'>,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaApiSubscription>('/api/v1/api-access/subscriptions', {
    method: 'POST',
    body,
    context,
  })
}

export const cancelLiberiaApiSubscription = async (
  subscriptionId: string,
  body: { cancelImmediately?: boolean },
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaApiSubscription>(`/api/v1/api-access/subscriptions/${subscriptionId}/cancel`, {
    method: 'POST',
    body,
    context,
  })
}

export const renewLiberiaApiSubscription = async (
  subscriptionId: string,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaApiSubscription & { keys_revoked?: boolean; renewed_from_expired?: boolean }>(
    `/api/v1/api-access/subscriptions/${subscriptionId}/renew`,
    {
      method: 'POST',
      context,
    }
  )
}

export const listLiberiaUserPaymentRecords = async (context: LiberiaApiAuthContext) => {
  return liberiaApiRequest<LiberiaApiPaymentRecord[]>('/api/v1/api-access/payment-records', { context })
}

export const createLiberiaPaymentRecord = async (
  body: {
    subscription_id: string
    amount: number
    currency: string
    payment_method: string
    payment_date: string
    payment_proof_base64?: string | null
    payment_proof_filename?: string | null
    payment_proof_mime_type?: string | null
  },
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaApiPaymentRecord>('/api/v1/api-access/payment-records', {
    method: 'POST',
    body,
    context,
  })
}

export const buildLiberiaPaymentRecordProofUrl = (paymentRecordId: string) => {
  if (!liberiaApiBaseUrl) {
    return null
  }

  return `${liberiaApiBaseUrl}/api/v1/api-access/payment-records/${paymentRecordId}/proof`
}

export const getLiberiaUserApiCredits = async (context: LiberiaApiAuthContext) => {
  return liberiaApiRequest<LiberiaUserApiCredits>('/api/v1/api-access/credits', { context })
}

export const listLiberiaUserApiKeys = async (context: LiberiaApiAuthContext) => {
  return liberiaApiRequest<LiberiaUserApiKey[]>('/api/v1/api-access/keys', { context })
}

export const listLiberiaUserApiUsage = async (
  context: LiberiaApiAuthContext,
  limit = 30
) => {
  return liberiaApiRequest<LiberiaApiUsageRecord[]>(`/api/v1/api-access/usage?limit=${encodeURIComponent(String(limit))}`, {
    context,
  })
}

export const createLiberiaUserApiKey = async (
  body: { name: string },
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaUserApiKey>('/api/v1/api-access/keys', {
    method: 'POST',
    body,
    context,
  })
}

export const updateLiberiaUserApiKey = async (
  id: string,
  body: Partial<Pick<LiberiaUserApiKey, 'name' | 'is_active'>>,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaUserApiKey>(`/api/v1/api-access/keys/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body,
    context,
  })
}

export const deleteLiberiaUserApiKey = async (
  id: string,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<{ id: string; deleted: boolean }>(`/api/v1/api-access/keys/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    context,
  })
}

export const listLiberiaAdminApiSubscriptions = async (context: LiberiaApiAuthContext) => {
  return liberiaApiRequest<LiberiaApiSubscription[]>('/api/v1/api-access/admin/subscriptions', { context })
}

export const listLiberiaAdminPaymentRecords = async (context: LiberiaApiAuthContext) => {
  return liberiaApiRequest<LiberiaApiPaymentRecord[]>('/api/v1/api-access/admin/payment-records', { context })
}

/** Business Portal — same subscription rows as admin list; requires `business` or `admin` role. */
export const listLiberiaBusinessPortalSubscriptions = async (context: LiberiaApiAuthContext) => {
  return liberiaApiRequest<LiberiaApiSubscription[]>('/api/v1/api-access/business/subscriptions', { context })
}

export const listLiberiaBusinessPortalPaymentRecords = async (context: LiberiaApiAuthContext) => {
  return liberiaApiRequest<LiberiaApiPaymentRecord[]>('/api/v1/api-access/business/payment-records', { context })
}

export type LiberiaQlsFeeStatus = 'paid' | 'owed' | 'overdue'

export interface LiberiaQlsFeeAccount {
  id: string
  kind: 'logistics_monthly_fee' | 'api_plan'
  name: string
  payer_email: string | null
  amount: number
  currency: string
  status: LiberiaQlsFeeStatus
  due_or_paid_through: string | null
  service_status?: string | null
  subscription_status?: string | null
}

export const listLiberiaBusinessPortalFees = async (context: LiberiaApiAuthContext) => {
  return liberiaApiRequest<LiberiaQlsFeeAccount[]>('/api/v1/api-access/business/fees', { context })
}

export interface LiberiaOperatingCostType {
  id: string
  name: string
  description: string
  country_id: string | null
  is_global: boolean
}

export interface LiberiaOperatingCost {
  id: string
  cost_type_id: string
  cost_type_name: string
  amount: number
  currency: string
  frequency: 'monthly' | 'annually'
  reason: string
  country_id: string
  monthly_equivalent: number
  created_at: string
  updated_at: string
}

export const listLiberiaOperatingCostTypes = async (context: LiberiaApiAuthContext) => {
  return liberiaApiRequest<LiberiaOperatingCostType[]>('/api/v1/api-access/business/operating-cost-types', { context })
}

export const createLiberiaOperatingCostType = async (
  body: { name: string; description?: string },
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaOperatingCostType>('/api/v1/api-access/business/operating-cost-types', {
    method: 'POST',
    body,
    context,
  })
}

export const listLiberiaOperatingCosts = async (context: LiberiaApiAuthContext) => {
  return liberiaApiRequest<LiberiaOperatingCost[]>('/api/v1/api-access/business/operating-costs', { context })
}

export const createLiberiaOperatingCost = async (
  body: {
    cost_type_id: string
    amount: number
    currency?: string
    frequency: 'monthly' | 'annually'
    reason: string
  },
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaOperatingCost>('/api/v1/api-access/business/operating-costs', {
    method: 'POST',
    body,
    context,
  })
}

export const updateLiberiaOperatingCost = async (
  id: string,
  body: Partial<{
    cost_type_id: string
    amount: number
    currency: string
    frequency: 'monthly' | 'annually'
    reason: string
  }>,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaOperatingCost>(
    `/api/v1/api-access/business/operating-costs/${encodeURIComponent(id)}`,
    { method: 'PATCH', body, context }
  )
}

export const deleteLiberiaOperatingCost = async (id: string, context: LiberiaApiAuthContext) => {
  return liberiaApiRequest<{ id: string; deleted: boolean }>(
    `/api/v1/api-access/business/operating-costs/${encodeURIComponent(id)}`,
    { method: 'DELETE', context }
  )
}

export const listLiberiaBusinessPortalApiKeys = async (context: LiberiaApiAuthContext) => {
  return liberiaApiRequest<LiberiaUserApiKey[]>('/api/v1/api-access/business/keys', { context })
}

export const listLiberiaBusinessPortalApiKeyOwners = async (context: LiberiaApiAuthContext) => {
  return liberiaApiRequest<LiberiaApiKeyOwner[]>('/api/v1/api-access/business/key-owners', { context })
}

export const createLiberiaBusinessPortalApiKey = async (
  body: { name: string; user_id?: string; email?: string },
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaUserApiKey>('/api/v1/api-access/business/keys', {
    method: 'POST',
    body,
    context,
  })
}

export const updateLiberiaBusinessPortalApiKey = async (
  id: string,
  body: Partial<Pick<LiberiaUserApiKey, 'name' | 'is_active'>>,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaUserApiKey>(`/api/v1/api-access/business/keys/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body,
    context,
  })
}

export const deleteLiberiaBusinessPortalApiKey = async (
  id: string,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<{ id: string; deleted: boolean }>(
    `/api/v1/api-access/business/keys/${encodeURIComponent(id)}`,
    {
      method: 'DELETE',
      context,
    }
  )
}

export const testLiberiaBusinessPortalApiKey = async (
  id: string,
  query: string,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaApiKeyTestResult>(
    `/api/v1/api-access/business/keys/${encodeURIComponent(id)}/test`,
    {
      method: 'POST',
      body: { query },
      context,
    }
  )
}

export interface LiberiaBusinessPortalMetrics {
  totalRevenue: number
  apiRevenue: number
  addressRevenue: number
  revenueGrowth: number
  totalApiCalls: number
  totalAddresses: number
  validatedAddresses?: number
  activePartners: number
  avgRevenuePerPartner: number
  currentPricing: {
    apiCall: number
    addressGenerated: number
    addressValidated: number
    monthlyBase: number
  }
}

export const getLiberiaBusinessPortalMetrics = async (context: LiberiaApiAuthContext) => {
  return liberiaApiRequest<LiberiaBusinessPortalMetrics>('/api/v1/api-access/business/metrics', { context })
}

export const getLiberiaBusinessPortalAnalytics = async (context: LiberiaApiAuthContext) => {
  return liberiaApiRequest<{
    totalRevenue: number
    totalCosts: number
    profitMargin: number
    activePartners: number
    partnerMetrics: Array<{
      partner_id: string
      partner_name: string
      total_projects: number
      active_projects: number
      api_usage: number
      last_activity: string
      total_addresses: number
      validated_addresses: number
    }>
    revenueHistory: Array<{ period: string; revenue: number; costs: number; profit: number }>
    conversionRate: number
    customerLifetimeValue: number
    totalApiCalls: number
    totalAddressesGenerated: number
    partnerGeoblocMetrics: Array<{
      partner_id: string
      partner_name: string
      geobloc: string
      address_count: number
      validated_count: number
      latest_generation: string
    }>
    geoblocSummary: Array<{
      geobloc: string
      total_addresses: number
      partner_count: number
      validation_rate: number
    }>
  }>('/api/v1/api-access/business/analytics', { context })
}

export const verifyLiberiaApiPaymentRecord = async (
  paymentId: string,
  body: { status: 'verified' | 'rejected'; adminNotes?: string },
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaApiPaymentRecord>(`/api/v1/api-access/admin/payment-records/${paymentId}/verify`, {
    method: 'POST',
    body,
    context,
  })
}

export const initializeLiberiaCreditPurchase = async (
  body: {
    provider: string
    package_id: string
    amount: number
    currency: string
    credits: number
    /** Required for MTN MoMo and Orange Money (Apco) checkout. */
    payer_phone?: string
  },
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<{
    purchaseId: string
    payment_url: string | null
    payment_reference: string
    provider: string
    status: string
    message: string
    flow?: string | null
    momo_reference_id?: string | null
    amount?: number
    currency?: string
    credits?: number
    payer_instruction?: {
      bank_name?: string | null
      account_name?: string | null
      account_number?: string | null
      routing_number?: string | null
      instructions?: string | null
    } | null
  }>('/api/v1/api-access/credit-purchases/initialize', {
    method: 'POST',
    body,
    context,
  })
}

/** Taxpayer MTN MoMo RequestToPay or Orange hosted payment. */
export const initiateLiberiaTaxMobilePayment = async (
  body: {
    assessment_id: string
    amount: number
    payer_phone: string
    payment_provider_id: string
  },
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<{
    flow: string
    momo_reference_id: string
    payment_url: string | null
    message: string
  }>('/api/v1/tax/me/payments/mobile/initiate', {
    method: 'POST',
    body,
    context,
  })
}

/** Taxpayer Paystack checkout (card/bank channels Paystack supports). */
export const initiateLiberiaTaxPaystackPayment = async (
  body: {
    assessment_id: string
    amount: number
    payment_provider_id: string
  },
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<{
    flow: string
    momo_reference_id: string
    payment_url: string | null
    message: string
  }>('/api/v1/tax/me/payments/paystack/initiate', {
    method: 'POST',
    body,
    context,
  })
}

/** Poll MTN RequestToPay status (also used after API credit purchase with flow mtn_momo). */
export const pollLiberiaTaxMobilePaymentStatus = async (
  momoReferenceId: string,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<{
    status: string
    momo_reference_id: string
    momo_status?: string
    reason?: string
    message?: string
  }>(`/api/v1/tax/me/payments/mobile/status/${encodeURIComponent(momoReferenceId)}`, {
    context,
  })
}

export const createLiberiaAdminCreditPackage = async (
  body: {
    name: string
    credits: number
    price_usd: number
    currency?: string
    is_active?: boolean
  },
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaCreditPackage>('/api/v1/admin/setup/credit-packages', {
    method: 'POST',
    body,
    context,
  })
}

export const updateLiberiaAdminCreditPackage = async (
  id: string,
  body: Partial<{
    name: string
    credits: number
    price_usd: number
    currency: string
    is_active: boolean
  }>,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaCreditPackage>(`/api/v1/admin/setup/credit-packages/${id}`, {
    method: 'PATCH',
    body,
    context,
  })
}

export const deleteLiberiaAdminCreditPackage = async (
  id: string,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<{ id: string; deleted: boolean }>(`/api/v1/admin/setup/credit-packages/${id}`, {
    method: 'DELETE',
    context,
  })
}

/** Body fields depend on provider: gateway uses api_key; Orange mobile money uses apco_* */
export interface LiberiaPaymentProviderTestBody {
  api_key?: string
  apco_api_base_url?: string
  apco_bearer_token?: string
  apco_client_id?: string
  apco_client_secret?: string
  apco_signature_secret?: string
}

export const testLiberiaPaymentProviderConfig = async (
  id: string,
  body: LiberiaPaymentProviderTestBody,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<{ valid: boolean; message: string }>(`/api/v1/tax/payment-providers/${id}/test`, {
    method: 'POST',
    body,
    context,
  })
}

export const listLiberiaTaxProperties = async (context: LiberiaApiAuthContext) => {
  return liberiaApiRequest<any[]>('/api/v1/tax/properties', { context })
}

export const listLiberiaTaxpayerOwnedProperties = async (context: LiberiaApiAuthContext) => {
  return liberiaApiRequest<any[]>('/api/v1/tax/my-properties', { context })
}

export const getLiberiaTaxpayerProfile = async (context: LiberiaApiAuthContext) => {
  return liberiaApiRequest<LiberiaTaxpayerProfile>('/api/v1/tax/me/profile', { context })
}

export const updateLiberiaTaxpayerProfile = async (
  body: Partial<Pick<LiberiaTaxpayerProfile, 'full_name' | 'phone_number' | 'national_id' | 'preferred_notification_method'>>,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaTaxpayerProfile>('/api/v1/tax/me/profile', {
    method: 'PATCH',
    body,
    context,
  })
}

export const listLiberiaTaxpayerAssessments = async (context: LiberiaApiAuthContext) => {
  return liberiaApiRequest<LiberiaTaxpayerAssessment[]>('/api/v1/tax/me/assessments', { context })
}

export const listLiberiaTaxpayerBills = async (context: LiberiaApiAuthContext) => {
  return liberiaApiRequest<LiberiaTaxpayerBill[]>('/api/v1/tax/me/bills', { context })
}

export const listLiberiaTaxpayerPayments = async (context: LiberiaApiAuthContext) => {
  return liberiaApiRequest<LiberiaTaxpayerPayment[]>('/api/v1/tax/me/payments', { context })
}

/** Taxpayer-submitted mobile money payment (secured to owned assessments). */
export const submitLiberiaTaxpayerPayment = async (
  body: {
    assessment_id: string
    amount: number
    payment_method: string
    payment_provider_id?: string | null
    transaction_reference?: string | null
    payer_phone_number?: string | null
    notes?: string | null
  },
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaTaxPaymentRecord>('/api/v1/tax/me/payments', {
    method: 'POST',
    body,
    context,
  })
}

export const listLiberiaTaxpayerAppeals = async (context: LiberiaApiAuthContext) => {
  return liberiaApiRequest<LiberiaTaxpayerAppeal[]>('/api/v1/tax/me/appeals', { context })
}

export const listLiberiaAppealableAssessments = async (context: LiberiaApiAuthContext) => {
  return liberiaApiRequest<LiberiaTaxpayerAssessment[]>('/api/v1/tax/me/appealable-assessments', { context })
}

export const createLiberiaTaxpayerAppeal = async (
  body: {
    assessment_id: string
    requested_value: number
    reason: string
  },
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaTaxpayerAppeal>('/api/v1/tax/me/appeals', {
    method: 'POST',
    body,
    context,
  })
}

export const createLiberiaTaxProperty = async (body: Record<string, unknown>, context: LiberiaApiAuthContext) => {
  return liberiaApiRequest<any>('/api/v1/tax/properties', {
    method: 'POST',
    body,
    context,
  })
}

export const updateLiberiaTaxProperty = async (
  propertyId: string,
  body: Record<string, unknown>,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<any>(`/api/v1/tax/properties/${propertyId}`, {
    method: 'PATCH',
    body,
    context,
  })
}

export const listLiberiaTaxPropertiesForAssessment = async (context: LiberiaApiAuthContext) => {
  return liberiaApiRequest<any[]>('/api/v1/tax/properties-for-assessment', { context })
}

export const listLiberiaTaxAssessments = async (context: LiberiaApiAuthContext) => {
  return liberiaApiRequest<any[]>('/api/v1/tax/assessments', { context })
}

export const listLiberiaOutstandingTaxAssessments = async (context: LiberiaApiAuthContext) => {
  return liberiaApiRequest<any[]>('/api/v1/tax/outstanding-assessments', { context })
}

export const createLiberiaTaxAssessment = async (body: Record<string, unknown>, context: LiberiaApiAuthContext) => {
  return liberiaApiRequest<any>('/api/v1/tax/assessments', {
    method: 'POST',
    body,
    context,
  })
}

export const updateLiberiaTaxLandUseType = async (
  landUseTypeId: string,
  body: Record<string, unknown>,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<any>(`/api/v1/tax/land-use-types/${landUseTypeId}`, {
    method: 'PATCH',
    body,
    context,
  })
}

export const getLiberiaRoadInfrastructureStats = async () => {
  return liberiaApiRequest<LiberiaRoadInfrastructureStats>('/api/v1/road/stats')
}

export const listLiberiaRoadSegments = async () => {
  return liberiaApiRequest<LiberiaRoadSegment[]>('/api/v1/road/segments')
}

export const createLiberiaRoadSegment = async (body: Record<string, unknown>) => {
  return liberiaApiRequest<LiberiaRoadSegment>('/api/v1/road/segments', {
    method: 'POST',
    body,
  })
}

export const deleteLiberiaRoadSegment = async (segmentId: string) => {
  return liberiaApiRequest<{ deleted: boolean }>(`/api/v1/road/segments/${encodeURIComponent(segmentId)}`, {
    method: 'DELETE',
  })
}

export const listLiberiaRoadClosures = async () => {
  return liberiaApiRequest<LiberiaRoadWorkOrder[]>('/api/v1/road/closures')
}

export const listLiberiaRoadWorkOrders = async () => {
  return liberiaApiRequest<LiberiaRoadWorkOrder[]>('/api/v1/road/work-orders')
}

export const createLiberiaRoadWorkOrder = async (body: {
  segment_id?: string | null
  address_id?: string | null
  work_type: string
  priority?: 'low' | 'normal' | 'high' | 'critical'
  status?: 'pending' | 'scheduled' | 'in_progress' | 'completed' | 'cancelled'
  description?: string | null
  notes?: string | null
  assigned_crew?: string | null
  budget_usd?: number | null
  scheduled_start_date?: string | null
  scheduled_end_date?: string | null
  closure_required?: boolean
  affected_addresses_count?: number
  affected_geoblocs?: string[]
}) => {
  return liberiaApiRequest<LiberiaRoadWorkOrder>('/api/v1/road/work-orders', {
    method: 'POST',
    body,
  })
}

export const listLiberiaRoadAssets = async () => {
  return liberiaApiRequest<LiberiaRoadAsset[]>('/api/v1/road/assets')
}

export const createLiberiaRoadAsset = async (body: Record<string, unknown>) => {
  return liberiaApiRequest<LiberiaRoadAsset>('/api/v1/road/assets', {
    method: 'POST',
    body,
  })
}

export const deleteLiberiaRoadAsset = async (assetId: string) => {
  return liberiaApiRequest<{ deleted: boolean }>(`/api/v1/road/assets/${encodeURIComponent(assetId)}`, {
    method: 'DELETE',
  })
}

export const listLiberiaRoadInspections = async () => {
  return liberiaApiRequest<LiberiaRoadInspection[]>('/api/v1/road/inspections')
}

export const createLiberiaRoadInspection = async (body: Record<string, unknown>) => {
  return liberiaApiRequest<LiberiaRoadInspection>('/api/v1/road/inspections', {
    method: 'POST',
    body,
  })
}

export const listLiberiaAmbulanceUnits = async ({
  activeOnly,
  includeCrew,
  includeDispatches,
}: {
  activeOnly?: boolean
  includeCrew?: boolean
  includeDispatches?: boolean
} = {}) => {
  const params = new URLSearchParams()
  if (activeOnly) params.set('activeOnly', 'true')
  if (includeCrew) params.set('includeCrew', 'true')
  if (includeDispatches) params.set('includeDispatches', 'true')
  const query = params.toString()
  return liberiaApiRequest<LiberiaAmbulanceUnit[]>(`/api/v1/ems/units${query ? `?${query}` : ''}`)
}

export const createLiberiaAmbulanceUnit = async (body: Record<string, unknown>) => {
  return liberiaApiRequest<LiberiaAmbulanceUnit>('/api/v1/ems/units', {
    method: 'POST',
    body,
  })
}

export const updateLiberiaAmbulanceUnitStatus = async (unitId: string, status: string) => {
  return liberiaApiRequest<LiberiaAmbulanceUnit>(`/api/v1/ems/units/${encodeURIComponent(unitId)}/status`, {
    method: 'PATCH',
    body: { status },
  })
}

export const listLiberiaAmbulanceCrew = async () => {
  return liberiaApiRequest<LiberiaAmbulanceCrew[]>('/api/v1/ems/crew')
}

export const listLiberiaLinkableEmsUsers = async () => {
  return liberiaApiRequest<LiberiaLinkableEmsUser[]>('/api/v1/ems/crew/linkable-users')
}

export const createLiberiaAmbulanceCrew = async (body: Record<string, unknown>) => {
  return liberiaApiRequest<LiberiaAmbulanceCrew>('/api/v1/ems/crew', {
    method: 'POST',
    body,
  })
}

export const assignLiberiaCrewToUnit = async (crewId: string, unitId: string) => {
  return liberiaApiRequest<LiberiaAmbulanceCrew>(`/api/v1/ems/crew/${encodeURIComponent(crewId)}/unit`, {
    method: 'PATCH',
    body: { unitId },
  })
}

export const listLiberiaEmergencyIncidents = async ({
  status,
  priority,
  activeOnly,
  limit,
}: {
  status?: string
  priority?: string
  activeOnly?: boolean
  limit?: number
} = {}) => {
  const params = new URLSearchParams()
  if (status) params.set('status', status)
  if (priority) params.set('priority', priority)
  if (activeOnly) params.set('activeOnly', 'true')
  if (typeof limit === 'number') params.set('limit', String(limit))
  const query = params.toString()
  return liberiaApiRequest<LiberiaEmergencyIncident[]>(`/api/v1/ems/incidents${query ? `?${query}` : ''}`)
}

export const createLiberiaEmergencyIncident = async (body: Record<string, unknown>) => {
  return liberiaApiRequest<LiberiaEmergencyIncident>('/api/v1/ems/incidents', {
    method: 'POST',
    body,
  })
}

export const getLiberiaEmergencyIncident = async (incidentId: string) => {
  return liberiaApiRequest<LiberiaEmergencyIncident>(`/api/v1/ems/incidents/${encodeURIComponent(incidentId)}`)
}

export const updateLiberiaEmergencyIncident = async (
  incidentId: string,
  body: Record<string, unknown>
) => {
  return liberiaApiRequest<LiberiaEmergencyIncident>(`/api/v1/ems/incidents/${encodeURIComponent(incidentId)}`, {
    method: 'PATCH',
    body,
  })
}

export const listLiberiaEmsDispatchAssignments = async () => {
  return liberiaApiRequest<LiberiaEmsDispatchAssignment[]>('/api/v1/ems/dispatches')
}

export const createLiberiaEmsDispatchAssignment = async (body: Record<string, unknown>) => {
  return liberiaApiRequest<LiberiaEmsDispatchAssignment>('/api/v1/ems/dispatches', {
    method: 'POST',
    body,
  })
}

export const updateLiberiaEmsDispatchAssignment = async (
  dispatchId: string,
  body: Record<string, unknown>
) => {
  return liberiaApiRequest<LiberiaEmsDispatchAssignment>(`/api/v1/ems/dispatches/${encodeURIComponent(dispatchId)}`, {
    method: 'PATCH',
    body,
  })
}

export const listLiberiaEmsAlerts = async ({
  type,
  is_read,
}: {
  type?: string
  is_read?: boolean
} = {}) => {
  const params = new URLSearchParams()
  if (type) params.set('type', type)
  if (typeof is_read === 'boolean') params.set('is_read', String(is_read))
  const query = params.toString()
  return liberiaApiRequest<LiberiaEmsAlert[]>(`/api/v1/ems/alerts${query ? `?${query}` : ''}`)
}

export const createLiberiaEmsAlert = async (body: Record<string, unknown>) => {
  return liberiaApiRequest<LiberiaEmsAlert>('/api/v1/ems/alerts', {
    method: 'POST',
    body,
  })
}

export const markLiberiaEmsAlertAsRead = async (alertId: string) => {
  return liberiaApiRequest<LiberiaEmsAlert>(`/api/v1/ems/alerts/${encodeURIComponent(alertId)}/read`, {
    method: 'POST',
  })
}

export const deleteLiberiaEmsAlert = async (alertId: string) => {
  return liberiaApiRequest<{ id: string }>(`/api/v1/ems/alerts/${encodeURIComponent(alertId)}`, {
    method: 'DELETE',
  })
}

export const listLiberiaEmergencyShelters = async ({
  eventId,
  availableOnly,
}: {
  eventId?: string
  availableOnly?: boolean
} = {}) => {
  const params = new URLSearchParams()
  if (eventId) params.set('eventId', eventId)
  if (availableOnly) params.set('availableOnly', 'true')
  const query = params.toString()
  return liberiaApiRequest<LiberiaEmergencyShelter[]>(`/api/v1/ems/shelters${query ? `?${query}` : ''}`)
}

export const createLiberiaEmergencyShelter = async (body: Record<string, unknown>) => {
  return liberiaApiRequest<LiberiaEmergencyShelter>('/api/v1/ems/shelters', {
    method: 'POST',
    body,
  })
}

export const updateLiberiaEmergencyShelter = async (shelterId: string, body: Record<string, unknown>) => {
  return liberiaApiRequest<LiberiaEmergencyShelter>(`/api/v1/ems/shelters/${encodeURIComponent(shelterId)}`, {
    method: 'PATCH',
    body,
  })
}

export interface LiberiaRiskZone {
  id: string
  zone_name: string
  zone_type: string
  risk_level: string
  affected_area: Record<string, unknown> | null
  center_latitude: number | null
  center_longitude: number | null
  radius_km: number | null
  estimated_population: number | null
  event_id: string | null
  is_active: boolean
  created_by: string | null
  created_at: string
  updated_at: string
}

export const listLiberiaRiskZones = async ({
  eventId,
  activeOnly,
}: {
  eventId?: string
  activeOnly?: boolean
} = {}) => {
  const params = new URLSearchParams()
  if (eventId) params.set('eventId', eventId)
  if (activeOnly) params.set('activeOnly', 'true')
  const query = params.toString()
  return liberiaApiRequest<LiberiaRiskZone[]>(`/api/v1/disaster/risk-zones${query ? `?${query}` : ''}`)
}

export const createLiberiaRiskZone = async (body: Record<string, unknown>) => {
  return liberiaApiRequest<LiberiaRiskZone>('/api/v1/disaster/risk-zones', {
    method: 'POST',
    body,
  })
}

export const linkLiberiaShelterToDisasterEvent = async (eventId: string, shelterId: string) => {
  return liberiaApiRequest<{ event_id: string; shelter_id: string }>(
    `/api/v1/disaster/events/${encodeURIComponent(eventId)}/shelter-links`,
    {
      method: 'POST',
      body: { shelter_id: shelterId },
    }
  )
}

/** Removes a row from `emergency_shelter_events` only (does not delete the shelter). */
export const unlinkLiberiaShelterFromDisasterEvent = async (eventId: string, shelterId: string) => {
  return liberiaApiRequest<{ event_id: string; shelter_id: string }>(
    `/api/v1/disaster/events/${encodeURIComponent(eventId)}/shelter-links/${encodeURIComponent(shelterId)}`,
    { method: 'DELETE' }
  )
}

export const updateLiberiaRiskZone = async (zoneId: string, body: Record<string, unknown>) => {
  return liberiaApiRequest<LiberiaRiskZone>(`/api/v1/disaster/risk-zones/${encodeURIComponent(zoneId)}`, {
    method: 'PATCH',
    body,
  })
}

export interface LiberiaAffectedProperty {
  id: string
  event_id: string
  address_id: string
  address_formatted_address?: string | null
  address_geobloc?: string | null
  address_structure_name?: string | null
  address_latitude?: number | null
  address_longitude?: number | null
  owner_name: string | null
  owner_phone: string | null
  owner_email: string | null
  household_size: number | null
  occupants_displaced: boolean | null
  damage_level: string | null
  notes: string | null
  reported_at: string
  created_by: string | null
  created_at: string
  updated_at: string
}

export const listLiberiaAffectedProperties = async (eventId: string) => {
  return liberiaApiRequest<LiberiaAffectedProperty[]>(
    `/api/v1/disaster/events/${encodeURIComponent(eventId)}/affected-properties`
  )
}

export const createLiberiaAffectedProperty = async (eventId: string, body: Record<string, unknown>) => {
  return liberiaApiRequest<LiberiaAffectedProperty>(
    `/api/v1/disaster/events/${encodeURIComponent(eventId)}/affected-properties`,
    {
      method: 'POST',
      body,
    }
  )
}

export interface LiberiaDisasterEvent {
  id: string
  event_name: string
  event_type: string
  severity_level: string
  status: string
  affected_geoblocs: string[] | null
  affected_addresses: string[] | null
  estimated_affected_population: number | null
  response_plan: Record<string, unknown> | null
  started_at: string
  resolved_at: string | null
  created_by: string | null
  created_at: string
  updated_at: string
}

export const listLiberiaDisasterEvents = async ({
  status,
}: {
  status?: string
} = {}) => {
  const params = new URLSearchParams()
  if (status) params.set('status', status)
  const query = params.toString()
  return liberiaApiRequest<LiberiaDisasterEvent[]>(`/api/v1/disaster/events${query ? `?${query}` : ''}`)
}

export const createLiberiaDisasterEvent = async (body: Record<string, unknown>) => {
  return liberiaApiRequest<LiberiaDisasterEvent>('/api/v1/disaster/events', {
    method: 'POST',
    body,
  })
}

export const updateLiberiaDisasterEvent = async (eventId: string, body: Record<string, unknown>) => {
  return liberiaApiRequest<LiberiaDisasterEvent>(`/api/v1/disaster/events/${encodeURIComponent(eventId)}`, {
    method: 'PATCH',
    body,
  })
}

/** Social Services portal — welfare cases (Postgres via Liberia API). */
export interface LiberiaSocialCaseEnrolledProgram {
  program_id: string
  program_name: string
  program_type: string
  enrollment_status: string
  enrolled_at: string
}

/** A planned delivery route linked to a case or program (junction metadata). */
export interface LiberiaSocialLinkedDeliveryRoute {
  route_id: string
  route_date: string
  vehicle_id: string
  vehicle_name: string
  status: string
  linked_at: string
}

export interface LiberiaSocialServiceCase {
  id: string
  case_number: string
  address_id: string
  case_type: string
  client_name: string
  client_contact: string | null
  status: string
  priority: string
  case_worker_id: string | null
  /** Joined from `user_profiles` when `case_worker_id` is set. */
  case_worker_full_name?: string | null
  case_worker_email?: string | null
  opened_date: string
  closed_date: string | null
  notes: string | null
  created_at: string
  updated_at: string
  /** Joined from `generated_addresses_unified` for display and home visits. */
  address_formatted_address?: string | null
  address_geobloc?: string | null
  address_latitude?: number | null
  address_longitude?: number | null
  /** Active program enrollments (see `social_service_case_programs`). */
  enrolled_programs?: LiberiaSocialCaseEnrolledProgram[]
  /** Optional links to planned delivery routes (`social_service_case_delivery_routes`). */
  linked_delivery_routes?: LiberiaSocialLinkedDeliveryRoute[]
}

export const listLiberiaSocialServiceCases = async () => {
  return liberiaApiRequest<LiberiaSocialServiceCase[]>('/api/v1/social-services/cases')
}

export const getLiberiaSocialServiceCase = async (caseId: string) => {
  return liberiaApiRequest<LiberiaSocialServiceCase>(
    `/api/v1/social-services/cases/${encodeURIComponent(caseId)}`
  )
}

export const createLiberiaSocialServiceCase = async (body: Record<string, unknown>) => {
  return liberiaApiRequest<LiberiaSocialServiceCase>('/api/v1/social-services/cases', {
    method: 'POST',
    body,
  })
}

export const updateLiberiaSocialServiceCase = async (caseId: string, body: Record<string, unknown>) => {
  return liberiaApiRequest<LiberiaSocialServiceCase>(
    `/api/v1/social-services/cases/${encodeURIComponent(caseId)}`,
    {
      method: 'PATCH',
      body,
    }
  )
}

export const enrollLiberiaCaseInProgram = async (caseId: string, programId: string) => {
  return liberiaApiRequest<LiberiaSocialServiceCase>(
    `/api/v1/social-services/cases/${encodeURIComponent(caseId)}/programs`,
    {
      method: 'POST',
      body: { program_id: programId },
    }
  )
}

export const withdrawLiberiaCaseFromProgram = async (caseId: string, programId: string) => {
  return liberiaApiRequest<LiberiaSocialServiceCase>(
    `/api/v1/social-services/cases/${encodeURIComponent(caseId)}/programs/${encodeURIComponent(programId)}`,
    { method: 'DELETE' }
  )
}

export const listLiberiaCaseLinkedDeliveryRoutes = async (caseId: string) => {
  return liberiaApiRequest<LiberiaSocialLinkedDeliveryRoute[]>(
    `/api/v1/social-services/cases/${encodeURIComponent(caseId)}/delivery-routes`
  )
}

export const linkLiberiaCaseToDeliveryRoute = async (caseId: string, routeId: string) => {
  return liberiaApiRequest<LiberiaSocialLinkedDeliveryRoute[]>(
    `/api/v1/social-services/cases/${encodeURIComponent(caseId)}/delivery-routes`,
    { method: 'POST', body: { route_id: routeId } }
  )
}

export const unlinkLiberiaCaseFromDeliveryRoute = async (caseId: string, routeId: string) => {
  return liberiaApiRequest<LiberiaSocialLinkedDeliveryRoute[]>(
    `/api/v1/social-services/cases/${encodeURIComponent(caseId)}/delivery-routes/${encodeURIComponent(routeId)}`,
    { method: 'DELETE' }
  )
}

export interface LiberiaSocialProgramEligibilityPreview {
  program_id: string
  address_id: string
  address_geobloc: string | null
  address_formatted_address: string
  program_geoblocs_covered: string[]
  eligible: boolean
  reason: string
}

export const previewLiberiaSocialProgramEligibility = async ({
  programId,
  addressId,
}: {
  programId: string
  addressId: string
}) => {
  const q = new URLSearchParams({ address_id: addressId })
  return liberiaApiRequest<LiberiaSocialProgramEligibilityPreview>(
    `/api/v1/social-services/programs/${encodeURIComponent(programId)}/eligibility-preview?${q.toString()}`
  )
}

export interface LiberiaSocialAssistanceProgram {
  id: string
  program_name: string
  program_type: string
  description: string | null
  eligibility_criteria: unknown | null
  benefit_amount_usd: number | null
  geoblocs_covered: unknown | null
  is_active: boolean
  created_at: string
  updated_at: string
  /** Optional links to planned delivery routes (`social_service_program_delivery_routes`). */
  linked_delivery_routes?: LiberiaSocialLinkedDeliveryRoute[]
}

export const listLiberiaSocialAssistancePrograms = async () => {
  return liberiaApiRequest<LiberiaSocialAssistanceProgram[]>('/api/v1/social-services/programs')
}

export const createLiberiaSocialAssistanceProgram = async (body: Record<string, unknown>) => {
  return liberiaApiRequest<LiberiaSocialAssistanceProgram>('/api/v1/social-services/programs', {
    method: 'POST',
    body,
  })
}

export const updateLiberiaSocialAssistanceProgram = async (programId: string, body: Record<string, unknown>) => {
  return liberiaApiRequest<LiberiaSocialAssistanceProgram>(
    `/api/v1/social-services/programs/${encodeURIComponent(programId)}`,
    {
      method: 'PATCH',
      body,
    }
  )
}

export const listLiberiaProgramLinkedDeliveryRoutes = async (programId: string) => {
  return liberiaApiRequest<LiberiaSocialLinkedDeliveryRoute[]>(
    `/api/v1/social-services/programs/${encodeURIComponent(programId)}/delivery-routes`
  )
}

export const linkLiberiaProgramToDeliveryRoute = async (programId: string, routeId: string) => {
  return liberiaApiRequest<LiberiaSocialLinkedDeliveryRoute[]>(
    `/api/v1/social-services/programs/${encodeURIComponent(programId)}/delivery-routes`,
    { method: 'POST', body: { route_id: routeId } }
  )
}

export const unlinkLiberiaProgramFromDeliveryRoute = async (programId: string, routeId: string) => {
  return liberiaApiRequest<LiberiaSocialLinkedDeliveryRoute[]>(
    `/api/v1/social-services/programs/${encodeURIComponent(programId)}/delivery-routes/${encodeURIComponent(routeId)}`,
    { method: 'DELETE' }
  )
}

/** Staff roster for assigning `case_worker_id` (portal users with Social Services or admin access). */
export interface LiberiaSocialPortalStaffMember {
  id: string
  email: string
  full_name: string | null
}

export const listLiberiaSocialPortalStaff = async () => {
  return liberiaApiRequest<LiberiaSocialPortalStaffMember[]>('/api/v1/social-services/staff')
}

export interface LiberiaSocialCaseNote {
  id: string
  case_id: string
  author_id: string
  author_full_name?: string | null
  author_email?: string | null
  body: string
  note_type: string
  created_at: string
}

export const listLiberiaSocialCaseNotes = async (caseId: string) => {
  return liberiaApiRequest<LiberiaSocialCaseNote[]>(
    `/api/v1/social-services/cases/${encodeURIComponent(caseId)}/notes`
  )
}

export const createLiberiaSocialCaseNote = async (
  caseId: string,
  body: { body: string; note_type?: string }
) => {
  return liberiaApiRequest<LiberiaSocialCaseNote>(
    `/api/v1/social-services/cases/${encodeURIComponent(caseId)}/notes`,
    { method: 'POST', body }
  )
}

export interface LiberiaSocialCaseReferral {
  id: string
  case_id: string
  agency_name: string
  agency_contact_name: string | null
  agency_phone: string | null
  agency_email: string | null
  status: string
  notes: string | null
  referred_at: string
  closed_at: string | null
  created_by: string | null
}

export const listLiberiaSocialCaseReferrals = async (caseId: string) => {
  return liberiaApiRequest<LiberiaSocialCaseReferral[]>(
    `/api/v1/social-services/cases/${encodeURIComponent(caseId)}/referrals`
  )
}

export const createLiberiaSocialCaseReferral = async (caseId: string, body: Record<string, unknown>) => {
  return liberiaApiRequest<LiberiaSocialCaseReferral>(
    `/api/v1/social-services/cases/${encodeURIComponent(caseId)}/referrals`,
    { method: 'POST', body }
  )
}

export const updateLiberiaSocialCaseReferral = async (
  caseId: string,
  referralId: string,
  body: Record<string, unknown>
) => {
  return liberiaApiRequest<LiberiaSocialCaseReferral>(
    `/api/v1/social-services/cases/${encodeURIComponent(caseId)}/referrals/${encodeURIComponent(referralId)}`,
    { method: 'PATCH', body }
  )
}

export interface LiberiaSocialCaseDocumentMeta {
  id: string
  case_id: string
  doc_kind: string
  title: string | null
  original_filename: string
  mime_type: string | null
  file_size: number
  uploaded_by: string | null
  created_at: string
}

export const listLiberiaSocialCaseDocuments = async (caseId: string) => {
  return liberiaApiRequest<LiberiaSocialCaseDocumentMeta[]>(
    `/api/v1/social-services/cases/${encodeURIComponent(caseId)}/documents`
  )
}

async function liberiaApiFormDataRequest<T>(path: string, formData: FormData): Promise<T> {
  if (!liberiaApiBaseUrl) {
    throw new Error('QLS API base URL is not configured')
  }
  const response = await fetch(`${liberiaApiBaseUrl}${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: buildLiberiaApiHeadersWithoutContentType(),
    body: formData,
  })
  const payload = await response.json().catch(() => null)
  if (!response.ok) {
    throw new Error(payload?.error?.message || `QLS API request failed (${response.status})`)
  }
  return payload?.data as T
}

export const uploadLiberiaSocialCaseDocument = async (
  caseId: string,
  formData: FormData
): Promise<LiberiaSocialCaseDocumentMeta> => {
  return liberiaApiFormDataRequest<LiberiaSocialCaseDocumentMeta>(
    `/api/v1/social-services/cases/${encodeURIComponent(caseId)}/documents`,
    formData
  )
}

export const downloadLiberiaSocialCaseDocumentBlob = async (documentId: string): Promise<Blob> => {
  if (!liberiaApiBaseUrl) {
    throw new Error('QLS API base URL is not configured')
  }
  const response = await fetch(
    `${liberiaApiBaseUrl}/api/v1/social-services/documents/${encodeURIComponent(documentId)}/download`,
    {
      method: 'GET',
      credentials: 'include',
      headers: buildLiberiaApiHeadersWithoutContentType(),
    }
  )
  if (!response.ok) {
    const text = await response.text().catch(() => '')
    throw new Error(text || `QLS API download failed (${response.status})`)
  }
  return response.blob()
}

export interface LiberiaSocialFleetVehicle {
  id: string
  name: string
  capacity_passengers: number | null
  home_base_address_id: string | null
  home_base_formatted_address?: string | null
  home_base_latitude?: number | null
  home_base_longitude?: number | null
  is_active: boolean
  created_at: string
  updated_at: string
}

export const listLiberiaSocialFleetVehicles = async () => {
  return liberiaApiRequest<LiberiaSocialFleetVehicle[]>('/api/v1/social-services/fleet/vehicles')
}

export const createLiberiaSocialFleetVehicle = async (body: Record<string, unknown>) => {
  return liberiaApiRequest<LiberiaSocialFleetVehicle>('/api/v1/social-services/fleet/vehicles', {
    method: 'POST',
    body,
  })
}

export const updateLiberiaSocialFleetVehicle = async (vehicleId: string, body: Record<string, unknown>) => {
  return liberiaApiRequest<LiberiaSocialFleetVehicle>(
    `/api/v1/social-services/fleet/vehicles/${encodeURIComponent(vehicleId)}`,
    { method: 'PATCH', body }
  )
}

export interface LiberiaSocialDeliveryRouteStop {
  id: string
  route_id: string
  case_id: string
  case_number: string
  client_name: string
  sequence_order: number
  latitude: number
  longitude: number
  formatted_address: string | null
}

export interface LiberiaSocialDeliveryRoute {
  id: string
  vehicle_id: string
  vehicle_name: string
  route_date: string
  status: string
  algorithm: string
  total_estimated_km: number | null
  created_by: string | null
  created_at: string
  updated_at: string
  stops?: LiberiaSocialDeliveryRouteStop[]
}

export const listLiberiaSocialDeliveryRoutes = async (routeDate?: string | null) => {
  const q = routeDate ? `?route_date=${encodeURIComponent(routeDate)}` : ''
  return liberiaApiRequest<LiberiaSocialDeliveryRoute[]>(`/api/v1/social-services/delivery-routes${q}`)
}

export const getLiberiaSocialDeliveryRoute = async (routeId: string) => {
  return liberiaApiRequest<LiberiaSocialDeliveryRoute>(
    `/api/v1/social-services/delivery-routes/${encodeURIComponent(routeId)}`
  )
}

export const optimizeLiberiaSocialDeliveryRoute = async (body: {
  vehicle_id: string
  case_ids: string[]
  route_date?: string
}) => {
  return liberiaApiRequest<LiberiaSocialDeliveryRoute>('/api/v1/social-services/delivery-routes/optimize', {
    method: 'POST',
    body,
  })
}

/** Advance a delivery route through its lifecycle (planned → in_progress → completed / cancelled). */
export const updateLiberiaSocialDeliveryRoute = async (
  routeId: string,
  body: { status: string }
) => {
  return liberiaApiRequest<LiberiaSocialDeliveryRoute>(
    `/api/v1/social-services/delivery-routes/${encodeURIComponent(routeId)}`,
    {
      method: 'PATCH',
      body,
    }
  )
}

/** Integration lookup: resolve a QLS address to the social services that serve it (session or x-api-key). */
export interface LiberiaSocialServicesByAddress {
  address_id: string
  geobloc: string | null
  formatted_address: string | null
  cases: Array<{
    id: string
    case_number: string
    case_type: string
    client_name: string
    status: string
    priority: string
    opened_date: string | null
  }>
  eligible_programs: Array<{
    id: string
    program_name: string
    program_type: string
    description: string | null
    benefit_amount_usd: number | null
  }>
}

export const lookupLiberiaSocialServicesByAddress = async (addressId: string) => {
  const q = new URLSearchParams({ address_id: addressId })
  return liberiaApiRequest<LiberiaSocialServicesByAddress>(
    `/api/v1/social-services/by-address?${q.toString()}`
  )
}

/** Health Services — national facility registry linked to QLS addresses (location layer, not EMR). */
export interface LiberiaHealthFacility {
  id: string
  facility_code: string
  facility_name: string
  facility_type: string
  address_id: string
  services_offered: string[] | null
  bed_capacity: number | null
  contact_phone: string | null
  operating_hours: Record<string, string> | null
  is_active: boolean
  created_by: string | null
  created_at: string
  updated_at: string
  address_formatted_address?: string | null
  address_geobloc?: string | null
  address_latitude?: number | null
  address_longitude?: number | null
  address_city?: string | null
  generated_addresses?: {
    formatted_address: string
    city: string
    latitude: number | null
    longitude: number | null
  } | null
}

export interface LiberiaHealthServicesStats {
  totalFacilities: number
  activeFacilities: number
  totalOutreachVisits: number
  peopleReached: number
  facilityTypes: Record<string, number>
  visitTypes: Record<string, number>
}

export const listLiberiaHealthFacilities = async (params?: {
  geobloc?: string
  facility_type?: string
  include_inactive?: boolean
  limit?: number
}) => {
  const q = new URLSearchParams()
  if (params?.geobloc) q.set('geobloc', params.geobloc)
  if (params?.facility_type) q.set('facility_type', params.facility_type)
  if (params?.include_inactive) q.set('include_inactive', 'true')
  if (params?.limit != null) q.set('limit', String(params.limit))
  const query = q.toString()
  return liberiaApiRequest<LiberiaHealthFacility[]>(
    `/api/v1/health-services/facilities${query ? `?${query}` : ''}`
  )
}

export const getLiberiaHealthFacility = async (facilityId: string) => {
  return liberiaApiRequest<LiberiaHealthFacility>(
    `/api/v1/health-services/facilities/${encodeURIComponent(facilityId)}`
  )
}

export const getLiberiaHealthFacilityByCode = async (facilityCode: string) => {
  return liberiaApiRequest<LiberiaHealthFacility>(
    `/api/v1/health-services/facilities/by-code/${encodeURIComponent(facilityCode)}`
  )
}

export const createLiberiaHealthFacility = async (body: Record<string, unknown>) => {
  return liberiaApiRequest<LiberiaHealthFacility>('/api/v1/health-services/facilities', {
    method: 'POST',
    body,
  })
}

export const updateLiberiaHealthFacility = async (facilityId: string, body: Record<string, unknown>) => {
  return liberiaApiRequest<LiberiaHealthFacility>(
    `/api/v1/health-services/facilities/${encodeURIComponent(facilityId)}`,
    {
      method: 'PATCH',
      body,
    }
  )
}

export const getLiberiaHealthServicesStats = async () => {
  return liberiaApiRequest<LiberiaHealthServicesStats>('/api/v1/health-services/stats')
}

/** Education Services — school registry and catchment zones. */
export interface LiberiaEducationSchool {
  id: string
  name: string
  school_type: 'primary' | 'secondary' | 'tertiary' | 'vocational'
  address_id: string | null
  catchment_zone_id: string | null
  catchment_zone_name?: string | null
  catchment_zone_geobloc?: string | null
  latitude: number
  longitude: number
  capacity: number
  current_enrollment: number
  contact_phone: string | null
  contact_email: string | null
  principal_name: string | null
  is_active: boolean
  created_at: string
  updated_at?: string
  address_formatted_address?: string | null
  address_geobloc?: string | null
}

export interface LiberiaEducationCatchmentZone {
  id: string
  zone_name: string
  geobloc?: string | null
  geoblocs_covered?: string[] | null
  boundary_geojson: unknown
  population_estimate: number | null
  households_count: number | null
  school_count?: number
  created_at: string
}

export interface LiberiaEducationStats {
  totalSchools: number
  totalEnrollment: number
  totalCapacity: number
  catchmentZones: number
  schoolsByType: { type: string; count: number }[]
  utilizationRate: number
}

export const listLiberiaEducationSchools = async (params?: {
  include_inactive?: boolean
  catchment_zone_id?: string
}) => {
  const search = new URLSearchParams()
  if (params?.include_inactive) search.set('include_inactive', 'true')
  if (params?.catchment_zone_id) search.set('catchment_zone_id', params.catchment_zone_id)
  const q = search.toString() ? `?${search.toString()}` : ''
  return liberiaApiRequest<LiberiaEducationSchool[]>(`/api/v1/education-services/schools${q}`)
}

export const createLiberiaEducationSchool = async (body: Record<string, unknown>) => {
  return liberiaApiRequest<LiberiaEducationSchool>('/api/v1/education-services/schools', {
    method: 'POST',
    body,
  })
}

export const updateLiberiaEducationSchool = async (schoolId: string, body: Record<string, unknown>) => {
  return liberiaApiRequest<LiberiaEducationSchool>(
    `/api/v1/education-services/schools/${encodeURIComponent(schoolId)}`,
    { method: 'PATCH', body }
  )
}

export const listLiberiaEducationCatchmentZones = async () => {
  return liberiaApiRequest<LiberiaEducationCatchmentZone[]>(
    '/api/v1/education-services/catchment-zones'
  )
}

export const createLiberiaEducationCatchmentZone = async (body: Record<string, unknown>) => {
  return liberiaApiRequest<LiberiaEducationCatchmentZone>('/api/v1/education-services/catchment-zones', {
    method: 'POST',
    body,
  })
}

export const getLiberiaEducationStats = async () => {
  return liberiaApiRequest<LiberiaEducationStats>('/api/v1/education-services/stats')
}

export const lookupLiberiaEducationCatchmentZone = async (addressId: string) => {
  const q = new URLSearchParams({ address_id: addressId })
  return liberiaApiRequest<Record<string, unknown>>(`/api/v1/education-services/zone?${q}`)
}

export const listLiberiaEducationGeoblocs = async (params?: { query?: string; limit?: number }) => {
  const search = new URLSearchParams()
  if (params?.query) search.set('query', params.query)
  if (params?.limit != null) search.set('limit', String(params.limit))
  const q = search.toString() ? `?${search.toString()}` : ''
  return liberiaApiRequest<string[]>(`/api/v1/education-services/geoblocs${q}`)
}

/** QLS Logistics Services — parcel deliveries (portal session). */
export interface LiberiaLogisticsDelivery {
  id: string
  tracking_number: string
  route_id: string | null
  delivery_address_id: string | null
  delivery_address_text: string | null
  recipient_name: string
  recipient_phone: string | null
  status: string | null
  scheduled_delivery_date: string | null
  actual_delivery_date: string | null
  delivery_time: string | null
  driver_id: string | null
  signature_data: string | null
  notes: string | null
  priority: string | null
  package_weight_kg: number | null
  package_description: string | null
  special_instructions: string | null
  sender_name: string | null
  sender_phone: string | null
  estimated_delivery_time: string | null
  attempted_deliveries: number | null
  failure_reason: string | null
  customer_rating: number | null
  created_by: string | null
  created_at: string
  updated_at: string | null
  package_photo_id?: string | null
  proof_photo_id?: string | null
  has_package_photo?: boolean
  has_proof_photo?: boolean
  delivery_fee_amount?: number | null
  delivery_fee_currency?: string
  payment_status?: string
  delivery_service_id?: string | null
  ready_for_agent_at?: string | null
  assignment_status?: string
    assigned_agent_id?: string | null
  assigned_agent_name?: string | null
  assigned_agent_momo_msisdn?: string | null
  assigned_agent_momo_provider?: string | null
  claimed_at?: string | null
  agent_payout_amount?: number | null
  agent_payout_currency?: string
  agent_payout_status?: string
  delivery_address_formatted?: string | null
  delivery_address_plus_code?: string | null
  delivery_address_latitude?: number | null
  delivery_address_longitude?: number | null
  driver_display_name?: string | null
  route_number?: string | null
  route_name?: string | null
  created_by_name?: string | null
  created_by_email?: string | null
  public_access_code?: string
  country_id?: string | null
  pickup_service_id?: string | null
  handoff_type?: 'doorstep' | 'pickup_point' | 'hub_transfer' | null
  return_of_delivery_id?: string | null
  return_of_tracking_number?: string | null
}

export interface LiberiaLogisticsPickupPoint {
  id: string
  trade_name: string
  legal_name: string
  hq_address_id: string | null
  hq_address_label?: string | null
  network_role: 'pickup_point'
  country_id?: string | null
}

export interface LiberiaDeliverabilityFactor {
  key: string
  label: string
  score: number
  detail: string
}

export interface LiberiaDeliverabilityScore {
  address_id: string
  score: number
  band: 'high' | 'moderate' | 'low'
  factors: LiberiaDeliverabilityFactor[]
  scored_at: string
}

export interface LiberiaLogisticsDeliveryCreateResult extends LiberiaLogisticsDelivery {
  public_access_code?: string
}

export interface LiberiaLogisticsDeliveryFeeQuote {
  priority: string
  amount: number
  currency: string
  simulate_payment_allowed: boolean
}

export interface LiberiaPublicDeliveryStatus {
  tracking_number: string
  status: string | null
  recipient_name: string
  priority: string | null
  scheduled_delivery_date: string | null
  actual_delivery_date: string | null
  delivery_address_formatted: string | null
  payment_status: string
  delivery_fee_amount: number | null
  delivery_fee_currency: string
  has_package_photo: boolean
  has_proof_photo: boolean
  driver_display_name: string | null
  route_number: string | null
  route_name: string | null
  updated_at: string | null
}

export type LiberiaLogisticsDeliveryPhotoType = 'package' | 'proof_of_delivery'

export interface LiberiaLogisticsDeliveryPhoto {
  id: string
  delivery_id: string
  photo_type: LiberiaLogisticsDeliveryPhotoType
  original_filename: string
  mime_type: string | null
  file_size: number
  uploaded_by: string | null
  created_at: string
  updated_at: string
}

export interface LiberiaLogisticsDeliveryStats {
  total: number
  pending: number
  inTransit: number
  delivered: number
  failed: number
  todayScheduled: number
  todayDelivered: number
  returnRequested?: number
}

export interface LiberiaLogisticsPartnerSummary {
  id: string
  trade_name: string
  legal_name: string
  network_role: string
  status: string
  subscription_status: string
  delivery_count: number
  delivered_count: number
  return_requested_count: number
  return_job_count: number
  fee_total: number
  payout_event_count: number
  payout_completed_total: number
}

export const listLiberiaLogisticsDeliveries = async () => {
  return liberiaApiRequest<LiberiaLogisticsDelivery[]>('/api/v1/delivery-logistics/deliveries')
}

export const getLiberiaLogisticsDelivery = async (deliveryId: string) => {
  return liberiaApiRequest<LiberiaLogisticsDelivery>(
    `/api/v1/delivery-logistics/deliveries/${encodeURIComponent(deliveryId)}`
  )
}

export const createLiberiaLogisticsReturn = async (deliveryId: string) => {
  return liberiaApiRequest<LiberiaLogisticsDeliveryCreateResult>(
    `/api/v1/delivery-logistics/deliveries/${encodeURIComponent(deliveryId)}/return`,
    { method: 'POST' }
  )
}

export const createLiberiaLogisticsDelivery = async (body: Record<string, unknown>) => {
  return liberiaApiRequest<LiberiaLogisticsDeliveryCreateResult>('/api/v1/delivery-logistics/deliveries', {
    method: 'POST',
    body,
  })
}

export const getLiberiaLogisticsDeliveryFeeQuote = async (priority: string) => {
  return liberiaApiRequest<LiberiaLogisticsDeliveryFeeQuote>(
    `/api/v1/delivery-logistics/fees?priority=${encodeURIComponent(priority)}`
  )
}

export const simulateLiberiaLogisticsDeliveryPayment = async (deliveryId: string) => {
  return liberiaApiRequest<LiberiaLogisticsDelivery>(
    `/api/v1/delivery-logistics/deliveries/${encodeURIComponent(deliveryId)}/payments/simulate`,
    { method: 'POST', body: {} }
  )
}

export const patchLiberiaLogisticsDelivery = async (deliveryId: string, body: Record<string, unknown>) => {
  return liberiaApiRequest<LiberiaLogisticsDelivery>(
    `/api/v1/delivery-logistics/deliveries/${encodeURIComponent(deliveryId)}`,
    {
      method: 'PATCH',
      body,
    }
  )
}

export const getLiberiaLogisticsDeliveryStats = async () => {
  return liberiaApiRequest<LiberiaLogisticsDeliveryStats>('/api/v1/delivery-logistics/stats')
}

export const listLiberiaLogisticsDeliveryPhotos = async (deliveryId: string) => {
  return liberiaApiRequest<LiberiaLogisticsDeliveryPhoto[]>(
    `/api/v1/delivery-logistics/deliveries/${encodeURIComponent(deliveryId)}/photos`
  )
}

export const uploadLiberiaLogisticsDeliveryPhoto = async (
  deliveryId: string,
  photoType: LiberiaLogisticsDeliveryPhotoType,
  file: File
) => {
  if (!liberiaApiBaseUrl) {
    throw new Error('QLS API base URL is not configured')
  }
  const formData = new FormData()
  formData.append('file', file)
  formData.append('photo_type', photoType)

  const response = await fetch(
    `${liberiaApiBaseUrl}/api/v1/delivery-logistics/deliveries/${encodeURIComponent(
      deliveryId
    )}/photos`,
    {
      method: 'POST',
      credentials: 'include',
      headers: buildLiberiaApiHeadersWithoutContentType(),
      body: formData,
    }
  )

  const payload = await response.json().catch(() => null)
  if (!response.ok) {
    throw new Error(payload?.error?.message || `QLS API request failed (${response.status})`)
  }
  return payload?.data as LiberiaLogisticsDeliveryPhoto
}

export const getLiberiaLogisticsDeliveryPhotoDownloadUrl = (photoId: string) => {
  if (!liberiaApiBaseUrl) return null
  return `${liberiaApiBaseUrl}/api/v1/delivery-logistics/delivery-photos/${encodeURIComponent(
    photoId
  )}/download`
}

export const downloadLiberiaLogisticsDeliveryPhoto = async (photoId: string) => {
  const url = getLiberiaLogisticsDeliveryPhotoDownloadUrl(photoId)
  if (!url) {
    throw new Error('QLS API base URL is not configured')
  }
  const response = await fetch(url, {
    credentials: 'include',
    headers: buildLiberiaApiHeadersWithoutContentType(),
  })
  if (!response.ok) {
    const text = await response.text().catch(() => '')
    throw new Error(text || `QLS API download failed (${response.status})`)
  }
  return response.blob()
}

/** Fleet vehicle (portal session). */
export interface LiberiaLogisticsVehicle {
  id: string
  vehicle_number: string
  vehicle_type: string
  make: string | null
  model: string | null
  year: number | null
  license_plate: string | null
  capacity_kg: number
  capacity_volume_m3: number
  fuel_type: string
  current_fuel_level: number
  current_latitude: number | null
  current_longitude: number | null
  last_location_update: string | null
  current_status: string
  current_driver_id: string | null
  current_route_id: string | null
  odometer_km: number
  last_maintenance_date: string | null
  next_maintenance_date: string | null
  insurance_expiry: string | null
  registration_expiry: string | null
  notes: string | null
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface LiberiaLogisticsVehicleStats {
  total: number
  available: number
  inTransit: number
  maintenance: number
  outOfService: number
  active: number
}

export const listLiberiaLogisticsVehicles = async () => {
  return liberiaApiRequest<LiberiaLogisticsVehicle[]>('/api/v1/delivery-logistics/vehicles')
}

export const createLiberiaLogisticsVehicle = async (body: Record<string, unknown>) => {
  return liberiaApiRequest<LiberiaLogisticsVehicle>('/api/v1/delivery-logistics/vehicles', {
    method: 'POST',
    body,
  })
}

export const patchLiberiaLogisticsVehicle = async (vehicleId: string, body: Record<string, unknown>) => {
  return liberiaApiRequest<LiberiaLogisticsVehicle>(
    `/api/v1/delivery-logistics/vehicles/${encodeURIComponent(vehicleId)}`,
    { method: 'PATCH', body }
  )
}

export const getLiberiaLogisticsVehicleStats = async () => {
  return liberiaApiRequest<LiberiaLogisticsVehicleStats>('/api/v1/delivery-logistics/vehicle-stats')
}

/** Driver roster (portal session). */
export interface LiberiaLogisticsDriver {
  id: string
  user_id: string
  display_name?: string | null
  driver_license_number: string | null
  driver_license_expiry: string | null
  license_class: string
  phone_number: string | null
  emergency_contact_name: string | null
  emergency_contact_phone: string | null
  current_vehicle_id: string | null
  current_latitude: number | null
  current_longitude: number | null
  last_location_update: string | null
  is_on_duty: boolean
  shift_start_time: string | null
  shift_end_time: string | null
  total_deliveries_completed: number
  rating: number
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface LiberiaLogisticsDriverStats {
  total: number
  onDuty: number
  offDuty: number
  active: number
  assigned: number
  totalDeliveries: number
  avgRating: string
}

export const listLiberiaLogisticsDrivers = async () => {
  return liberiaApiRequest<LiberiaLogisticsDriver[]>('/api/v1/delivery-logistics/drivers')
}

export const createLiberiaLogisticsDriver = async (body: Record<string, unknown>) => {
  return liberiaApiRequest<LiberiaLogisticsDriver>('/api/v1/delivery-logistics/drivers', {
    method: 'POST',
    body,
  })
}

export const patchLiberiaLogisticsDriver = async (driverId: string, body: Record<string, unknown>) => {
  return liberiaApiRequest<LiberiaLogisticsDriver>(
    `/api/v1/delivery-logistics/drivers/${encodeURIComponent(driverId)}`,
    { method: 'PATCH', body }
  )
}

export const getLiberiaLogisticsDriverStats = async () => {
  return liberiaApiRequest<LiberiaLogisticsDriverStats>('/api/v1/delivery-logistics/driver-stats')
}

/** Delivery route planning (portal session). */
export interface LiberiaLogisticsRoute {
  id: string
  route_number: string
  route_name: string
  geoblocs_covered: unknown | null
  addresses_covered: unknown | null
  assigned_driver: string | null
  assigned_driver_id?: string | null
  vehicle_id: string | null
  route_optimization_data: unknown | null
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface LiberiaLogisticsRouteStats {
  total: number
  active: number
  assigned: number
  unassigned: number
}

export const listLiberiaLogisticsRoutes = async () => {
  return liberiaApiRequest<LiberiaLogisticsRoute[]>('/api/v1/delivery-logistics/routes')
}

export const createLiberiaLogisticsRoute = async (body: Record<string, unknown>) => {
  return liberiaApiRequest<LiberiaLogisticsRouteDetail>('/api/v1/delivery-logistics/routes', {
    method: 'POST',
    body,
  })
}

export interface LiberiaLogisticsRouteDetail extends LiberiaLogisticsRoute {
  vehicle_number?: string | null
  deliveries?: LiberiaLogisticsDelivery[]
}

export const getLiberiaLogisticsRoute = async (routeId: string) => {
  return liberiaApiRequest<LiberiaLogisticsRouteDetail>(
    `/api/v1/delivery-logistics/routes/${encodeURIComponent(routeId)}`
  )
}

export const patchLiberiaLogisticsRoute = async (routeId: string, body: Record<string, unknown>) => {
  return liberiaApiRequest<LiberiaLogisticsRouteDetail>(
    `/api/v1/delivery-logistics/routes/${encodeURIComponent(routeId)}`,
    { method: 'PATCH', body }
  )
}

export const getPublicLiberiaDeliveryFeeQuote = async (priority: string) => {
  return liberiaApiRequest<LiberiaLogisticsDeliveryFeeQuote>(
    `/api/v1/public/delivery-logistics/fees?priority=${encodeURIComponent(priority)}`
  )
}

export const lookupPublicLiberiaDeliveryStatus = async (trackingNumber: string, accessCode: string) => {
  const q = new URLSearchParams({
    tracking_number: trackingNumber,
    access_code: accessCode,
  })
  return liberiaApiRequest<LiberiaPublicDeliveryStatus>(`/api/v1/public/delivery-logistics/status?${q}`)
}

/** Delivery marketplace — public applications */
export const submitPublicDeliveryServiceApplication = async (body: Record<string, unknown>) => {
  return liberiaApiRequest<{
    application_reference: string
    access_code: string
    status: string
  }>('/api/v1/public/delivery-logistics/marketplace/service-applications', {
    method: 'POST',
    body,
  })
}

export const lookupPublicDeliveryServiceApplication = async (reference: string, accessCode: string) => {
  const q = new URLSearchParams({ reference, access_code: accessCode })
  return liberiaApiRequest<Record<string, unknown>>(
    `/api/v1/public/delivery-logistics/marketplace/service-applications/status?${q}`
  )
}

export const submitPublicDeliveryAgentApplication = async (body: Record<string, unknown>) => {
  return liberiaApiRequest<{
    application_reference: string
    access_code: string
    status: string
  }>('/api/v1/public/delivery-logistics/marketplace/agent-applications', {
    method: 'POST',
    body,
  })
}

export const lookupPublicDeliveryAgentApplication = async (reference: string, accessCode: string) => {
  const q = new URLSearchParams({ reference, access_code: accessCode })
  return liberiaApiRequest<Record<string, unknown>>(
    `/api/v1/public/delivery-logistics/marketplace/agent-applications/status?${q}`
  )
}

export const getPublicDeliveryMarketplaceSubscriptionConfig = async () => {
  return liberiaApiRequest<{
    monthly_fee_lrd: number
    currency: string
    simulate_payment_allowed?: boolean
    simulate_momo_payout_allowed?: boolean
    momo_payout_configured?: boolean
    momo_disbursement_configured?: boolean
  }>('/api/v1/public/delivery-logistics/marketplace/subscription-config')
}

export const getDeliveryMarketplaceSubscriptionConfig = async () => {
  return liberiaApiRequest<{
    monthly_fee_lrd: number
    currency: string
    simulate_payment_allowed?: boolean
    simulate_momo_payout_allowed?: boolean
    momo_payout_configured?: boolean
    momo_collection_configured?: boolean
    momo_disbursement_configured?: boolean
  }>('/api/v1/delivery-logistics/marketplace/subscription-config')
}

export const listDeliveryMarketplacePaymentProviders = async () => {
  return liberiaApiRequest<LiberiaPublicPaymentProvider[]>(
    '/api/v1/delivery-logistics/marketplace/payment-providers'
  )
}

export const initiateDeliverySubscriptionMobilePayment = async (body: {
  payer_phone: string
  payment_provider_id: string
}) => {
  return liberiaApiRequest<LiberiaPermitFilingMobilePaymentInit>(
    '/api/v1/delivery-logistics/marketplace/subscription/mobile/initiate',
    { method: 'POST', body }
  )
}

export const pollDeliveryMarketplaceMobilePaymentStatus = async (momoReferenceId: string) => {
  return liberiaApiRequest<{
    status: string
    momo_reference_id: string
    reason?: string
    message?: string
  }>(
    `/api/v1/delivery-logistics/marketplace/payments/mobile/status/${encodeURIComponent(momoReferenceId)}`
  )
}

export const initiateDeliveryAgentPayoutMobilePayment = async (
  deliveryId: string,
  body: { payer_phone?: string; payment_provider_id: string }
) => {
  return liberiaApiRequest<LiberiaPermitFilingMobilePaymentInit>(
    `/api/v1/delivery-logistics/deliveries/${encodeURIComponent(deliveryId)}/agent-payout/mobile/initiate`,
    { method: 'POST', body }
  )
}

/** Delivery marketplace — authenticated */
export const listDeliveryServiceApplications = async (status?: string) => {
  const q = status ? `?status=${encodeURIComponent(status)}` : ''
  return liberiaApiRequest<Record<string, unknown>[]>(
    `/api/v1/delivery-logistics/marketplace/service-applications${q}`
  )
}

export const reviewDeliveryServiceApplication = async (
  id: string,
  body: { action: string; reason?: string; notes?: string }
) => {
  return liberiaApiRequest<Record<string, unknown>>(
    `/api/v1/delivery-logistics/marketplace/service-applications/${encodeURIComponent(id)}/review`,
    { method: 'POST', body }
  )
}

export const listDeliveryAgentApplications = async (status?: string) => {
  const q = status ? `?status=${encodeURIComponent(status)}` : ''
  return liberiaApiRequest<Record<string, unknown>[]>(
    `/api/v1/delivery-logistics/marketplace/agent-applications${q}`
  )
}

export const reviewDeliveryAgentApplication = async (
  id: string,
  body: { action: string; reason?: string; notes?: string }
) => {
  return liberiaApiRequest<Record<string, unknown>>(
    `/api/v1/delivery-logistics/marketplace/agent-applications/${encodeURIComponent(id)}/review`,
    { method: 'POST', body }
  )
}

export const getMyDeliveryService = async () => {
  return liberiaApiRequest<Record<string, unknown> | null>('/api/v1/delivery-logistics/marketplace/my-service')
}

export const updateMyDeliveryServiceMomoWallet = async (body: {
  momo_msisdn: string
  momo_provider: 'mtn' | 'orange'
}) => {
  return liberiaApiRequest<Record<string, unknown>>(
    '/api/v1/delivery-logistics/marketplace/my-service/momo-wallet',
    { method: 'PATCH', body }
  )
}

export const simulateDeliveryServiceSubscription = async () => {
  return liberiaApiRequest<Record<string, unknown>>(
    '/api/v1/delivery-logistics/marketplace/subscription/simulate-pay',
    { method: 'POST', body: {} }
  )
}

export const listDeliveryAffiliationRequests = async (status?: string) => {
  const q = status ? `?status=${encodeURIComponent(status)}` : ''
  return liberiaApiRequest<Record<string, unknown>[]>(
    `/api/v1/delivery-logistics/marketplace/affiliation-requests${q}`
  )
}

export const decideDeliveryAffiliationRequest = async (
  id: string,
  body: { action: string; default_payout_amount?: number; notes?: string }
) => {
  return liberiaApiRequest<Record<string, unknown>>(
    `/api/v1/delivery-logistics/marketplace/affiliation-requests/${encodeURIComponent(id)}/decide`,
    { method: 'POST', body }
  )
}

export const markDeliveryReadyForAgent = async (deliveryId: string) => {
  return liberiaApiRequest<Record<string, unknown>>(
    `/api/v1/delivery-logistics/deliveries/${encodeURIComponent(deliveryId)}/ready-for-agent`,
    { method: 'POST', body: {} }
  )
}

export const recordDeliveryAgentPayout = async (
  deliveryId: string,
  body: { method: 'manual' | 'momo'; amount?: number; notes?: string; momo_reference_id?: string }
) => {
  return liberiaApiRequest<Record<string, unknown>>(
    `/api/v1/delivery-logistics/deliveries/${encodeURIComponent(deliveryId)}/agent-payout`,
    { method: 'POST', body }
  )
}

/** Staging/local: simulate MTN MoMo disbursement to agent (requires DELIVERY_ALLOW_SIMULATED_PAYMENT). */
export const simulateDeliveryAgentPayoutMomo = async (
  deliveryId: string,
  body?: { notes?: string }
) => {
  return liberiaApiRequest<Record<string, unknown>>(
    `/api/v1/delivery-logistics/deliveries/${encodeURIComponent(deliveryId)}/agent-payout/momo/simulate`,
    { method: 'POST', body: body || {} }
  )
}

export const getDeliveryAgentProfile = async () => {
  return liberiaApiRequest<Record<string, unknown>>('/api/v1/delivery-logistics/marketplace/agent-profile')
}

export const updateDeliveryAgentMomoWallet = async (body: {
  momo_msisdn: string
  momo_provider: 'mtn' | 'orange'
}) => {
  return liberiaApiRequest<Record<string, unknown>>(
    '/api/v1/delivery-logistics/marketplace/agent-profile/momo-wallet',
    { method: 'PATCH', body }
  )
}

export const listDeliveryServicesForAgent = async () => {
  return liberiaApiRequest<Record<string, unknown>[]>('/api/v1/delivery-logistics/marketplace/services')
}

export const listLogisticsPickupPoints = async () => {
  return liberiaApiRequest<LiberiaLogisticsPickupPoint[]>(
    '/api/v1/delivery-logistics/marketplace/pickup-points'
  )
}

export const listLogisticsPartnerSummaries = async () => {
  return liberiaApiRequest<LiberiaLogisticsPartnerSummary[]>(
    '/api/v1/delivery-logistics/marketplace/partners'
  )
}

export const requestDeliveryServiceAffiliation = async (serviceId: string, notes?: string) => {
  return liberiaApiRequest<Record<string, unknown>>(
    `/api/v1/delivery-logistics/marketplace/services/${encodeURIComponent(serviceId)}/affiliation-request`,
    { method: 'POST', body: { notes } }
  )
}

export const listDeliveryJobsForService = async (serviceId: string) => {
  return liberiaApiRequest<Record<string, unknown>[]>(
    `/api/v1/delivery-logistics/marketplace/services/${encodeURIComponent(serviceId)}/jobs`
  )
}

export const claimDeliveryJob = async (deliveryId: string) => {
  return liberiaApiRequest<LiberiaLogisticsDelivery>(
    `/api/v1/delivery-logistics/deliveries/${encodeURIComponent(deliveryId)}/claim`,
    { method: 'POST', body: {} }
  )
}

export const listDeliveryPayoutAudit = async () => {
  return liberiaApiRequest<Record<string, unknown>[]>('/api/v1/delivery-logistics/marketplace/payout-audit')
}

export const listMyDeliveryPayouts = async () => {
  return liberiaApiRequest<Record<string, unknown>[]>('/api/v1/delivery-logistics/marketplace/my-payouts')
}

export const listMyDeliverySubscriptionPayments = async () => {
  return liberiaApiRequest<Record<string, unknown>[]>(
    '/api/v1/delivery-logistics/marketplace/my-subscription-payments'
  )
}

export const getLiberiaLogisticsRouteStats = async () => {
  return liberiaApiRequest<LiberiaLogisticsRouteStats>('/api/v1/delivery-logistics/route-stats')
}

/** Community outreach visit logged at a QLS address (not patient-level EMR data). */
export interface LiberiaHealthOutreachVisit {
  id: string
  visit_number: string
  address_id: string
  facility_id: string | null
  visit_type: string
  visit_date: string
  health_worker_id: string | null
  people_reached: number | null
  services_provided: string[] | null
  notes: string | null
  created_by: string | null
  created_at: string
  updated_at: string
  address_formatted_address?: string | null
  address_geobloc?: string | null
  address_latitude?: number | null
  address_longitude?: number | null
  address_city?: string | null
  facility_code?: string | null
  facility_name?: string | null
  generated_addresses?: {
    formatted_address: string
    city: string
    latitude: number | null
    longitude: number | null
  } | null
}

export const listLiberiaHealthOutreachVisits = async (params?: {
  geobloc?: string
  visit_type?: string
  visit_date_from?: string
  visit_date_to?: string
  facility_id?: string
  limit?: number
}) => {
  const q = new URLSearchParams()
  if (params?.geobloc) q.set('geobloc', params.geobloc)
  if (params?.visit_type) q.set('visit_type', params.visit_type)
  if (params?.visit_date_from) q.set('visit_date_from', params.visit_date_from)
  if (params?.visit_date_to) q.set('visit_date_to', params.visit_date_to)
  if (params?.facility_id) q.set('facility_id', params.facility_id)
  if (params?.limit != null) q.set('limit', String(params.limit))
  const query = q.toString()
  return liberiaApiRequest<LiberiaHealthOutreachVisit[]>(
    `/api/v1/health-services/outreach-visits${query ? `?${query}` : ''}`
  )
}

export const getLiberiaHealthOutreachVisit = async (visitId: string) => {
  return liberiaApiRequest<LiberiaHealthOutreachVisit>(
    `/api/v1/health-services/outreach-visits/${encodeURIComponent(visitId)}`
  )
}

export const getLiberiaHealthOutreachVisitByNumber = async (visitNumber: string) => {
  return liberiaApiRequest<LiberiaHealthOutreachVisit>(
    `/api/v1/health-services/outreach-visits/by-number/${encodeURIComponent(visitNumber)}`
  )
}

export const createLiberiaHealthOutreachVisit = async (body: Record<string, unknown>) => {
  return liberiaApiRequest<LiberiaHealthOutreachVisit>('/api/v1/health-services/outreach-visits', {
    method: 'POST',
    body,
  })
}

/** Real Estate — valuations and transactions on QLS addresses. */
export interface LiberiaRealEstateValuation {
  id: string
  valuation_number: string
  address_id: string
  appraisal_id?: string | null
  appraisal_number?: string | null
  linked_appraisal_date?: string | null
  linked_appraisal_verification_status?: string | null
  property_type: string | null
  structure_type_snapshot: string | null
  geobloc_snapshot: string | null
  valuation_date: string
  valuation_amount: number
  currency: string
  size_sqm: number | null
  valuation_method: string
  notes: string | null
  created_by: string | null
  created_at: string
  updated_at: string
  address_formatted_address?: string | null
  address_geobloc?: string | null
  address_geobloc_id?: string | null
  address_latitude?: number | null
  address_longitude?: number | null
  address_city?: string | null
  address_district?: string | null
  address_county?: string | null
  address_structure_type?: string | null
  address_structure_name?: string | null
  address_plus_code?: string | null
  address_validation_status?: string | null
  generated_addresses?: {
    formatted_address: string
    city: string
    latitude: number | null
    longitude: number | null
    structure_type?: string | null
    structure_name?: string | null
    geobloc?: string | null
    plus_code?: string | null
    validation_status?: string | null
  } | null
}

export interface LiberiaRealEstateTransaction {
  id: string
  transaction_number: string
  address_id: string
  valuation_id?: string | null
  linked_valuation_number?: string | null
  linked_valuation_date?: string | null
  linked_valuation_amount?: number | null
  linked_valuation_currency?: string | null
  transaction_type: string
  structure_type_snapshot: string | null
  geobloc_snapshot: string | null
  transaction_date: string
  price_amount: number
  currency: string
  size_sqm: number | null
  notes: string | null
  created_by: string | null
  created_at: string
  updated_at: string
  address_formatted_address?: string | null
  address_geobloc?: string | null
  address_latitude?: number | null
  address_longitude?: number | null
  address_structure_type?: string | null
  address_structure_name?: string | null
  address_plus_code?: string | null
  address_validation_status?: string | null
  generated_addresses?: LiberiaRealEstateValuation['generated_addresses']
}

export interface LiberiaRealEstateAppraisal {
  id: string
  appraisal_number: string
  address_id: string
  structure_type_snapshot: string | null
  geobloc_snapshot: string | null
  appraisal_date: string
  verification_status: string
  notes: string | null
  created_by: string | null
  created_at: string
  updated_at: string
  address_formatted_address?: string | null
  address_geobloc?: string | null
  address_geobloc_id?: string | null
  address_latitude?: number | null
  address_longitude?: number | null
  address_city?: string | null
  address_district?: string | null
  address_county?: string | null
  address_structure_type?: string | null
  address_structure_name?: string | null
  address_plus_code?: string | null
  address_validation_status?: string | null
  generated_addresses?: LiberiaRealEstateValuation['generated_addresses']
}

export interface LiberiaRealEstateStats {
  totalAppraisals?: number
  appraisalsThisMonth?: number
  totalValuations: number
  valuationsThisMonth: number
  totalTransactions: number
  salesCount: number
  geoblocsWithAddresses: number
  avgPricePerSqm: number | null
  totalValuationAmount: number
  totalTransactionAmount: number
  valuationMethods: Record<string, number>
  transactionTypes: Record<string, number>
}

export interface LiberiaRealEstateComparables {
  valuations: Array<{
    geobloc: string
    structure_type: string
    count: number
    avg_amount: number | null
    median_amount: number | null
    avg_per_sqm: number | null
  }>
  transactions: Array<{
    geobloc: string
    structure_type: string
    count: number
    avg_amount: number | null
    median_amount: number | null
    avg_per_sqm: number | null
  }>
}

export interface LiberiaRealEstateMarketTrends {
  months: number
  geobloc: string | null
  transactions: Array<{
    month: string
    count: number
    avg_amount: number | null
    median_amount: number | null
  }>
  valuations: Array<{
    month: string
    count: number
    avg_amount: number | null
    median_amount: number | null
  }>
}

export interface LiberiaRealEstateInvestment {
  neighborhoods: Array<{
    geobloc: string
    qls_address_count: number
    valuation_count: number
    transaction_count: number
    median_valuation: number | null
    median_valuation_per_sqm: number | null
    median_price: number | null
    median_price_per_sqm: number | null
    value_per_address: number | null
  }>
}

export const listLiberiaRealEstateValuations = async (params?: {
  geobloc?: string
  address_id?: string
  limit?: number
}) => {
  const q = new URLSearchParams()
  if (params?.geobloc) q.set('geobloc', params.geobloc)
  if (params?.address_id) q.set('address_id', params.address_id)
  if (params?.limit != null) q.set('limit', String(params.limit))
  const query = q.toString()
  return liberiaApiRequest<LiberiaRealEstateValuation[]>(
    `/api/v1/real-estate/valuations${query ? `?${query}` : ''}`
  )
}

export const getLiberiaRealEstateValuation = async (valuationId: string) => {
  return liberiaApiRequest<LiberiaRealEstateValuation>(
    `/api/v1/real-estate/valuations/${encodeURIComponent(valuationId)}`
  )
}

export const getLiberiaRealEstateValuationByNumber = async (valuationNumber: string) => {
  return liberiaApiRequest<LiberiaRealEstateValuation>(
    `/api/v1/real-estate/valuations/by-number/${encodeURIComponent(valuationNumber)}`
  )
}

export const createLiberiaRealEstateValuation = async (body: Record<string, unknown>) => {
  return liberiaApiRequest<LiberiaRealEstateValuation>('/api/v1/real-estate/valuations', {
    method: 'POST',
    body,
  })
}

export const updateLiberiaRealEstateValuation = async (
  valuationId: string,
  body: Record<string, unknown>
) => {
  return liberiaApiRequest<LiberiaRealEstateValuation>(
    `/api/v1/real-estate/valuations/${encodeURIComponent(valuationId)}`,
    { method: 'PATCH', body }
  )
}

export const listLiberiaRealEstateTransactions = async (params?: {
  geobloc?: string
  address_id?: string
  transaction_type?: string
  limit?: number
}) => {
  const q = new URLSearchParams()
  if (params?.geobloc) q.set('geobloc', params.geobloc)
  if (params?.address_id) q.set('address_id', params.address_id)
  if (params?.transaction_type) q.set('transaction_type', params.transaction_type)
  if (params?.limit != null) q.set('limit', String(params.limit))
  const query = q.toString()
  return liberiaApiRequest<LiberiaRealEstateTransaction[]>(
    `/api/v1/real-estate/transactions${query ? `?${query}` : ''}`
  )
}

export const getLiberiaRealEstateTransaction = async (transactionId: string) => {
  return liberiaApiRequest<LiberiaRealEstateTransaction>(
    `/api/v1/real-estate/transactions/${encodeURIComponent(transactionId)}`
  )
}

export const getLiberiaRealEstateTransactionByNumber = async (transactionNumber: string) => {
  return liberiaApiRequest<LiberiaRealEstateTransaction>(
    `/api/v1/real-estate/transactions/by-number/${encodeURIComponent(transactionNumber)}`
  )
}

export const createLiberiaRealEstateTransaction = async (body: Record<string, unknown>) => {
  return liberiaApiRequest<LiberiaRealEstateTransaction>('/api/v1/real-estate/transactions', {
    method: 'POST',
    body,
  })
}

export const updateLiberiaRealEstateTransaction = async (
  transactionId: string,
  body: Record<string, unknown>
) => {
  return liberiaApiRequest<LiberiaRealEstateTransaction>(
    `/api/v1/real-estate/transactions/${encodeURIComponent(transactionId)}`,
    { method: 'PATCH', body }
  )
}

export const listLiberiaRealEstateAppraisals = async (params?: {
  geobloc?: string
  address_id?: string
  limit?: number
}) => {
  const q = new URLSearchParams()
  if (params?.geobloc) q.set('geobloc', params.geobloc)
  if (params?.address_id) q.set('address_id', params.address_id)
  if (params?.limit != null) q.set('limit', String(params.limit))
  const query = q.toString()
  return liberiaApiRequest<LiberiaRealEstateAppraisal[]>(
    `/api/v1/real-estate/appraisals${query ? `?${query}` : ''}`
  )
}

export const getLiberiaRealEstateAppraisal = async (appraisalId: string) => {
  return liberiaApiRequest<LiberiaRealEstateAppraisal>(
    `/api/v1/real-estate/appraisals/${encodeURIComponent(appraisalId)}`
  )
}

export const createLiberiaRealEstateAppraisal = async (body: Record<string, unknown>) => {
  return liberiaApiRequest<LiberiaRealEstateAppraisal>('/api/v1/real-estate/appraisals', {
    method: 'POST',
    body,
  })
}

export const updateLiberiaRealEstateAppraisal = async (
  appraisalId: string,
  body: Record<string, unknown>
) => {
  return liberiaApiRequest<LiberiaRealEstateAppraisal>(
    `/api/v1/real-estate/appraisals/${encodeURIComponent(appraisalId)}`,
    { method: 'PATCH', body }
  )
}

export const getLiberiaRealEstateStats = async () => {
  return liberiaApiRequest<LiberiaRealEstateStats>('/api/v1/real-estate/stats')
}

export const getLiberiaRealEstateComparables = async (params?: {
  geobloc?: string
  validated_only?: boolean
}) => {
  const q = new URLSearchParams()
  if (params?.geobloc) q.set('geobloc', params.geobloc)
  if (params?.validated_only) q.set('validated_only', 'true')
  const query = q.toString()
  return liberiaApiRequest<LiberiaRealEstateComparables>(
    `/api/v1/real-estate/analytics/comparables${query ? `?${query}` : ''}`
  )
}

export const getLiberiaRealEstateMarketTrends = async (params?: {
  geobloc?: string
  months?: number
  validated_only?: boolean
}) => {
  const q = new URLSearchParams()
  if (params?.geobloc) q.set('geobloc', params.geobloc)
  if (params?.months != null) q.set('months', String(params.months))
  if (params?.validated_only) q.set('validated_only', 'true')
  const query = q.toString()
  return liberiaApiRequest<LiberiaRealEstateMarketTrends>(
    `/api/v1/real-estate/analytics/market-trends${query ? `?${query}` : ''}`
  )
}

export const getLiberiaRealEstateInvestment = async (params?: { validated_only?: boolean }) => {
  const q = new URLSearchParams()
  if (params?.validated_only) q.set('validated_only', 'true')
  const query = q.toString()
  return liberiaApiRequest<LiberiaRealEstateInvestment>(
    `/api/v1/real-estate/analytics/investment${query ? `?${query}` : ''}`
  )
}

// ----- Permit & Licensing -----

export interface LiberiaPermitApplication {
  id: string
  application_number: string
  permit_type: string
  applicant_name: string
  applicant_first_name?: string | null
  applicant_last_name?: string | null
  applicant_email?: string | null
  applicant_phone?: string | null
  applicant_address_id?: string | null
  applicant_organization?: string | null
  applicant_id_number?: string | null
  address_id?: string | null
  property_address?: string | null
  property_owner_name?: string | null
  property_owner_phone?: string | null
  property_owner_email?: string | null
  contractor_name?: string | null
  contractor_license_number?: string | null
  contractor_phone?: string | null
  contractor_email?: string | null
  project_description?: string | null
  estimated_cost?: number | null
  project_square_footage?: number | null
  number_of_stories?: number | null
  zoning_classification?: string | null
  type_specific_data?: Record<string, unknown> | null
  status?: string | null
  submitted_at?: string | null
  reviewed_at?: string | null
  reviewed_by?: string | null
  approved_at?: string | null
  approved_by?: string | null
  rejected_reason?: string | null
  reviewer_notes?: string | null
  return_to_applicant_reason?: string | null
  fee_amount?: number | null
  fee_paid_at?: string | null
  fee_payment_reference?: string | null
  provisional_approved_at?: string | null
  final_issued_at?: string | null
  work_stopped_at?: string | null
  work_stopped_reason?: string | null
  notes?: string | null
  documents?: unknown[]
  created_at?: string | null
  updated_at?: string | null
  address_formatted_address?: string | null
  address_geobloc?: string | null
  address_latitude?: number | null
  address_longitude?: number | null
  address_structure_type?: string | null
  address_validation_status?: string | null
  generated_addresses?: { formatted_address?: string; latitude?: number | null; longitude?: number | null }
}

export interface LiberiaBuildingPermit {
  id: string
  permit_number: string
  application_id?: string | null
  linked_application_number?: string | null
  permit_type: string
  property_address?: string | null
  address_id?: string | null
  owner_name: string
  owner_contact?: string | null
  contractor_name?: string | null
  contractor_license?: string | null
  project_description?: string | null
  estimated_value?: number | null
  issue_date: string
  expiry_date: string
  status?: string | null
  inspection_status?: string | null
  last_inspection_date?: string | null
  next_inspection_date?: string | null
  notes?: string | null
  created_at?: string | null
  updated_at?: string | null
  address_formatted_address?: string | null
  address_geobloc?: string | null
  address_latitude?: number | null
  address_longitude?: number | null
  generated_addresses?: { formatted_address?: string; latitude?: number | null; longitude?: number | null }
}

export interface LiberiaPermitInspection {
  id: string
  permit_id: string
  permit_number?: string | null
  inspection_type: string
  scheduled_date?: string | null
  completed_date?: string | null
  inspector_id?: string | null
  result?: string | null
  notes?: string | null
  deficiencies?: unknown[]
  photos?: unknown[]
  created_at?: string | null
  updated_at?: string | null
  permit_address_id?: string | null
  address_formatted_address?: string | null
  address_latitude?: number | null
  address_longitude?: number | null
  address_geobloc?: string | null
}

export interface LiberiaOccupancyCertificate {
  id: string
  certificate_number: string
  permit_id?: string | null
  linked_permit_number?: string | null
  property_address?: string | null
  address_id?: string | null
  owner_name: string
  occupancy_type: string
  max_occupancy?: number | null
  issue_date: string
  status?: string | null
  conditions?: string | null
  issued_by?: string | null
  created_at?: string | null
  updated_at?: string | null
  address_formatted_address?: string | null
  address_latitude?: number | null
  address_longitude?: number | null
}

export interface LiberiaPermitVehicleLicense {
  id: string
  license_number: string
  license_type: string
  owner_name: string
  owner_id_number?: string | null
  owner_phone?: string | null
  owner_email?: string | null
  owner_address_id?: string | null
  owner_organization?: string | null
  vehicle_make: string
  vehicle_model: string
  vehicle_year?: number | null
  vehicle_color?: string | null
  vin_number?: string | null
  expiry_date: string
  status?: string | null
  notes?: string | null
  created_at?: string | null
  updated_at?: string | null
  owner_address_formatted?: string | null
  owner_address_latitude?: number | null
  owner_address_longitude?: number | null
}

export interface LiberiaPermitLicensingStats {
  activePermits: number
  pendingApplications: number
  scheduledInspections: number
  activeCertificates: number
  activeVehicleLicenses: number
  approvedToday: number
  totalApplications: number
  totalPermits: number
}

const permitLicensingPath = (suffix: string) =>
  `/api/v1/permit-licensing${suffix}`

export const listLiberiaPermitApplications = async (params?: {
  status?: string
  address_id?: string
  geobloc?: string
  limit?: number
}) => {
  const q = new URLSearchParams()
  if (params?.status) q.set('status', params.status)
  if (params?.address_id) q.set('address_id', params.address_id)
  if (params?.geobloc) q.set('geobloc', params.geobloc)
  if (params?.limit != null) q.set('limit', String(params.limit))
  const query = q.toString()
  return liberiaApiRequest<LiberiaPermitApplication[]>(
    permitLicensingPath(`/applications${query ? `?${query}` : ''}`)
  )
}

export const getLiberiaPermitApplication = async (applicationId: string) =>
  liberiaApiRequest<LiberiaPermitApplication>(permitLicensingPath(`/applications/${encodeURIComponent(applicationId)}`))

export const createLiberiaPermitApplication = async (body: Record<string, unknown>) =>
  liberiaApiRequest<LiberiaPermitApplication>(permitLicensingPath('/applications'), { method: 'POST', body })

export const updateLiberiaPermitApplication = async (applicationId: string, body: Record<string, unknown>) =>
  liberiaApiRequest<LiberiaPermitApplication>(permitLicensingPath(`/applications/${encodeURIComponent(applicationId)}`), {
    method: 'PATCH',
    body,
  })

export interface LiberiaPermitApplicationWorkspace {
  application: LiberiaPermitApplication
  building_permit: {
    id: string
    permit_number: string
    issue_date: string
    expiry_date: string
    status?: string | null
  } | null
  inspections: Array<{
    id: string
    inspection_type: string
    scheduled_date?: string | null
    completed_date?: string | null
    result?: string | null
    notes?: string | null
    created_at?: string | null
  }>
  certificate: {
    id: string
    certificate_number: string
    issue_date: string
    status?: string | null
    occupancy_type?: string | null
    conditions?: string | null
  } | null
}

export const getLiberiaPermitApplicationWorkspace = async (applicationId: string) =>
  liberiaApiRequest<LiberiaPermitApplicationWorkspace>(
    permitLicensingPath(`/applications/${encodeURIComponent(applicationId)}/workspace`)
  )

export const applyLiberiaPermitApplicationWorkflow = async (
  applicationId: string,
  body: Record<string, unknown>
) =>
  liberiaApiRequest<LiberiaPermitApplicationWorkspace>(
    permitLicensingPath(`/applications/${encodeURIComponent(applicationId)}/workflow`),
    { method: 'POST', body }
  )

export const listLiberiaBuildingPermits = async (params?: {
  status?: string
  address_id?: string
  geobloc?: string
  limit?: number
}) => {
  const q = new URLSearchParams()
  if (params?.status) q.set('status', params.status)
  if (params?.address_id) q.set('address_id', params.address_id)
  if (params?.geobloc) q.set('geobloc', params.geobloc)
  if (params?.limit != null) q.set('limit', String(params.limit))
  const query = q.toString()
  return liberiaApiRequest<LiberiaBuildingPermit[]>(permitLicensingPath(`/permits${query ? `?${query}` : ''}`))
}

export const createLiberiaBuildingPermit = async (body: Record<string, unknown>) =>
  liberiaApiRequest<LiberiaBuildingPermit>(permitLicensingPath('/permits'), { method: 'POST', body })

export const updateLiberiaBuildingPermit = async (permitId: string, body: Record<string, unknown>) =>
  liberiaApiRequest<LiberiaBuildingPermit>(permitLicensingPath(`/permits/${encodeURIComponent(permitId)}`), {
    method: 'PATCH',
    body,
  })

export const listLiberiaPermitInspections = async (params?: { permit_id?: string; limit?: number }) => {
  const q = new URLSearchParams()
  if (params?.permit_id) q.set('permit_id', params.permit_id)
  if (params?.limit != null) q.set('limit', String(params.limit))
  const query = q.toString()
  return liberiaApiRequest<LiberiaPermitInspection[]>(
    permitLicensingPath(`/inspections${query ? `?${query}` : ''}`)
  )
}

export const createLiberiaPermitInspection = async (body: Record<string, unknown>) =>
  liberiaApiRequest<LiberiaPermitInspection>(permitLicensingPath('/inspections'), { method: 'POST', body })

export const updateLiberiaPermitInspection = async (inspectionId: string, body: Record<string, unknown>) =>
  liberiaApiRequest<LiberiaPermitInspection>(
    permitLicensingPath(`/inspections/${encodeURIComponent(inspectionId)}`),
    { method: 'PATCH', body }
  )

export const listLiberiaOccupancyCertificates = async (params?: {
  status?: string
  address_id?: string
  geobloc?: string
  limit?: number
}) => {
  const q = new URLSearchParams()
  if (params?.status) q.set('status', params.status)
  if (params?.address_id) q.set('address_id', params.address_id)
  if (params?.geobloc) q.set('geobloc', params.geobloc)
  if (params?.limit != null) q.set('limit', String(params.limit))
  const query = q.toString()
  return liberiaApiRequest<LiberiaOccupancyCertificate[]>(
    permitLicensingPath(`/certificates${query ? `?${query}` : ''}`)
  )
}

export const createLiberiaOccupancyCertificate = async (body: Record<string, unknown>) =>
  liberiaApiRequest<LiberiaOccupancyCertificate>(permitLicensingPath('/certificates'), { method: 'POST', body })

export const updateLiberiaOccupancyCertificate = async (certificateId: string, body: Record<string, unknown>) =>
  liberiaApiRequest<LiberiaOccupancyCertificate>(
    permitLicensingPath(`/certificates/${encodeURIComponent(certificateId)}`),
    { method: 'PATCH', body }
  )

export const listLiberiaPermitVehicleLicenses = async (params?: { status?: string; limit?: number }) => {
  const q = new URLSearchParams()
  if (params?.status) q.set('status', params.status)
  if (params?.limit != null) q.set('limit', String(params.limit))
  const query = q.toString()
  return liberiaApiRequest<LiberiaPermitVehicleLicense[]>(
    permitLicensingPath(`/vehicle-licenses${query ? `?${query}` : ''}`)
  )
}

export const createLiberiaPermitVehicleLicense = async (body: Record<string, unknown>) =>
  liberiaApiRequest<LiberiaPermitVehicleLicense>(permitLicensingPath('/vehicle-licenses'), { method: 'POST', body })

export const updateLiberiaPermitVehicleLicense = async (licenseId: string, body: Record<string, unknown>) =>
  liberiaApiRequest<LiberiaPermitVehicleLicense>(
    permitLicensingPath(`/vehicle-licenses/${encodeURIComponent(licenseId)}`),
    { method: 'PATCH', body }
  )

export const getLiberiaPermitLicensingStats = async () =>
  liberiaApiRequest<LiberiaPermitLicensingStats>(permitLicensingPath('/stats'))

/** Public permit application (no login). */
export interface LiberiaPublicPermitApplicationStatus {
  application_number: string
  permit_type: string
  status: string
  status_message?: string | null
  submitted_at?: string | null
  reviewed_at?: string | null
  approved_at?: string | null
  rejected_reason?: string | null
  applicant_message?: string | null
  work_stopped_reason?: string | null
  property_address?: string | null
  permit_number?: string | null
  permit_issue_date?: string | null
  permit_expiry_date?: string | null
  permit_status?: string | null
  fee_amount?: number | null
  fee_paid?: boolean
  certificate_number?: string | null
  certificate_issue_date?: string | null
  inspections?: Array<{
    inspection_type: string
    scheduled_date?: string | null
    completed_date?: string | null
    result?: string | null
  }>
  documents?: Array<{ type: string; label: string; available: boolean }>
}

export interface LiberiaPublicPermitApplicationSubmitResult {
  access_code: string
  application: LiberiaPublicPermitApplicationStatus
}

export interface LiberiaPermitFilingFeeQuote {
  permit_type: string
  label: string
  amount_lrd: number
  revenue_code: string
  currency: string
  dummy_fee?: boolean
  /** True when server allows test payment without MTN/Orange (staging). */
  simulate_payment_allowed?: boolean
}

export interface LiberiaPublicPaymentProvider {
  id: string
  provider_name: string
  provider_type: string
  is_active: boolean
  test_mode: boolean
}

export interface LiberiaPermitFilingMobilePaymentInit {
  flow: string
  momo_reference_id: string | null
  payment_url: string | null
  message?: string
  amount_lrd: number
  currency: string
  payee_msisdn?: string | null
}

export const fetchPublicPermitFilingFee = async (permitType: string) => {
  if (!liberiaApiBaseUrl) {
    throw new Error('QLS API base URL is not configured')
  }
  const q = new URLSearchParams({ permit_type: permitType.trim() })
  const response = await fetch(
    `${liberiaApiBaseUrl}/api/v1/public/permit-applications/fees?${q.toString()}`
  )
  const payload = await response.json().catch(() => null)
  if (!response.ok) {
    throw new Error(payload?.error?.message || 'Failed to load filing fee')
  }
  return payload?.data as LiberiaPermitFilingFeeQuote
}

export const listPublicPermitPaymentProviders = async () => {
  if (!liberiaApiBaseUrl) {
    throw new Error('QLS API base URL is not configured')
  }
  const response = await fetch(`${liberiaApiBaseUrl}/api/v1/public/permit-applications/payment-providers`)
  const payload = await response.json().catch(() => null)
  if (!response.ok) {
    throw new Error(payload?.error?.message || 'Failed to load payment providers')
  }
  return (payload?.data || []) as LiberiaPublicPaymentProvider[]
}

export const simulatePublicPermitFilingFeePayment = async (permitType: string) => {
  if (!liberiaApiBaseUrl) {
    throw new Error('QLS API base URL is not configured')
  }
  const response = await fetch(`${liberiaApiBaseUrl}/api/v1/public/permit-applications/fees/simulate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ permit_type: permitType.trim() }),
  })
  const payload = await response.json().catch(() => null)
  if (!response.ok) {
    throw new Error(payload?.error?.message || 'Failed to record test payment')
  }
  return payload?.data as LiberiaPermitFilingMobilePaymentInit & { status: string }
}

export const initiatePublicPermitFilingFeePaystack = async (body: {
  permit_type: string
  payment_provider_id: string
}) => {
  if (!liberiaApiBaseUrl) {
    throw new Error('QLS API base URL is not configured')
  }
  const response = await fetch(
    `${liberiaApiBaseUrl}/api/v1/public/permit-applications/fees/paystack/initiate`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }
  )
  const payload = await response.json().catch(() => null)
  if (!response.ok) {
    throw new Error(payload?.error?.message || 'Failed to start Paystack payment')
  }
  return payload?.data as LiberiaPermitFilingMobilePaymentInit
}

export const initiatePublicPermitFilingFeeMobile = async (body: {
  permit_type: string
  payer_phone: string
  payment_provider_id: string
}) => {
  if (!liberiaApiBaseUrl) {
    throw new Error('QLS API base URL is not configured')
  }
  const response = await fetch(
    `${liberiaApiBaseUrl}/api/v1/public/permit-applications/fees/mobile/initiate`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }
  )
  const payload = await response.json().catch(() => null)
  if (!response.ok) {
    throw new Error(payload?.error?.message || 'Failed to start mobile payment')
  }
  return payload?.data as LiberiaPermitFilingMobilePaymentInit
}

export const pollPublicPermitFilingPaymentStatus = async (momoReferenceId: string) => {
  if (!liberiaApiBaseUrl) {
    throw new Error('QLS API base URL is not configured')
  }
  const response = await fetch(
    `${liberiaApiBaseUrl}/api/v1/public/permit-applications/payments/mobile/status/${encodeURIComponent(momoReferenceId)}`
  )
  const payload = await response.json().catch(() => null)
  if (!response.ok) {
    throw new Error(payload?.error?.message || 'Failed to check payment status')
  }
  return payload?.data as { status: string; momo_reference_id: string; reason?: string; message?: string }
}

export const submitPublicLiberiaPermitApplication = async (body: Record<string, unknown>) => {
  if (!liberiaApiBaseUrl) {
    throw new Error('QLS API base URL is not configured')
  }
  const response = await fetch(`${liberiaApiBaseUrl}/api/v1/public/permit-applications`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  const payload = await response.json().catch(() => null)
  if (!response.ok) {
    throw new Error(payload?.error?.message || 'Failed to submit permit application')
  }
  return payload?.data as LiberiaPublicPermitApplicationSubmitResult
}

export const lookupPublicLiberiaPermitApplicationStatus = async (
  applicationNumber: string,
  accessCode: string
) => {
  if (!liberiaApiBaseUrl) {
    throw new Error('QLS API base URL is not configured')
  }
  const q = new URLSearchParams({
    application_number: applicationNumber.trim(),
    access_code: accessCode.trim(),
  })
  const response = await fetch(
    `${liberiaApiBaseUrl}/api/v1/public/permit-applications/status?${q.toString()}`,
    { headers: { 'Content-Type': 'application/json' } }
  )
  const payload = await response.json().catch(() => null)
  if (!response.ok) {
    throw new Error(payload?.error?.message || 'Application not found')
  }
  return payload?.data as LiberiaPublicPermitApplicationStatus
}

/** Registration dossier (draft JSON evolves through phased applicant journey). */
export interface LiberiaBusinessRegistrationApplication {
  id: string
  applicant_user_id: string | null
  application_reference?: string | null
  is_public?: boolean
  status: string
  current_step: number
  draft_payload: Record<string, unknown>
  officer_notes: string | null
  reviewed_by: string | null
  reviewed_at: string | null
  submitted_at: string | null
  fee_paid_at?: string | null
  fee_amount_paid?: number | null
  inspection_outcome?: string | null
  inspection_notes?: string | null
  inspected_by?: string | null
  inspected_at?: string | null
  created_at: string
  updated_at: string
}

export const listMyBusinessRegistrationApplications = async () => {
  return liberiaApiRequest<LiberiaBusinessRegistrationApplication[]>(
    '/api/v1/business-registration/applications/me'
  )
}

export const createBusinessRegistrationApplication = async () => {
  return liberiaApiRequest<LiberiaBusinessRegistrationApplication>(
    '/api/v1/business-registration/applications',
    {
      method: 'POST',
    }
  )
}

export const getBusinessRegistrationApplication = async (applicationId: string) => {
  return liberiaApiRequest<LiberiaBusinessRegistrationApplication>(
    `/api/v1/business-registration/applications/${encodeURIComponent(applicationId)}`
  )
}

export const patchBusinessRegistrationApplication = async (
  applicationId: string,
  body: { draft_payload?: Record<string, unknown>; current_step?: number }
) => {
  return liberiaApiRequest<LiberiaBusinessRegistrationApplication>(
    `/api/v1/business-registration/applications/${encodeURIComponent(applicationId)}`,
    {
      method: 'PATCH',
      body,
    }
  )
}

export const submitBusinessRegistrationApplication = async (applicationId: string) => {
  return liberiaApiRequest<LiberiaBusinessRegistrationApplication>(
    `/api/v1/business-registration/applications/${encodeURIComponent(applicationId)}/submit`,
    {
      method: 'POST',
    }
  )
}

export const deleteBusinessRegistrationApplication = async (applicationId: string) => {
  return liberiaApiRequest<{ id: string; deleted: boolean }>(
    `/api/v1/business-registration/applications/${encodeURIComponent(applicationId)}`,
    {
      method: 'DELETE',
    }
  )
}

export const listAdminBusinessRegistrationApplications = async (opts?: { status?: string | null }) => {
  const params = new URLSearchParams()
  if (opts?.status) params.set('status', opts.status)
  const q = params.toString()
  return liberiaApiRequest<LiberiaBusinessRegistrationApplication[]>(
    `/api/v1/business-registration/admin/applications${q ? `?${q}` : ''}`
  )
}

export const patchAdminBusinessRegistrationApplication = async (
  applicationId: string,
  body: { status?: string; officer_notes?: string | null }
) => {
  return liberiaApiRequest<LiberiaBusinessRegistrationApplication>(
    `/api/v1/business-registration/admin/applications/${encodeURIComponent(applicationId)}`,
    {
      method: 'PATCH',
      body,
    }
  )
}

export const listBusinessRegistrationInspectionQueue = async () => {
  return liberiaApiRequest<LiberiaBusinessRegistrationApplication[]>(
    '/api/v1/business-registration/admin/inspections/queue'
  )
}

export const patchBusinessRegistrationInspectionOutcome = async (
  applicationId: string,
  body: { outcome: 'pass' | 'fail' | 'follow_up'; notes?: string | null }
) => {
  return liberiaApiRequest<LiberiaBusinessRegistrationApplication>(
    `/api/v1/business-registration/admin/applications/${encodeURIComponent(applicationId)}/inspection`,
    {
      method: 'PATCH',
      body,
    }
  )
}

export interface LiberiaBusinessRegistrationLicenseRenewalRow {
  id: string
  license_number: string
  business_name: string
  issue_date: string
  expiry_date: string
  renewed_paid_at: string | null
  renewed: boolean
}

export interface LiberiaBusinessRegistrationComplianceQueueResponse {
  overdue: LiberiaBusinessRegistrationLicenseRenewalRow[]
  expiring_soon: LiberiaBusinessRegistrationLicenseRenewalRow[]
}

export const listBusinessRegistrationRenewals = async (opts?: { year?: number; month?: number }) => {
  const params = new URLSearchParams()
  if (opts?.year) params.set('year', String(opts.year))
  if (opts?.month) params.set('month', String(opts.month))
  const q = params.toString()
  return liberiaApiRequest<LiberiaBusinessRegistrationLicenseRenewalRow[]>(
    `/api/v1/business-registration/admin/licenses/renewals${q ? `?${q}` : ''}`
  )
}

export const getBusinessRegistrationComplianceQueue = async () => {
  return liberiaApiRequest<LiberiaBusinessRegistrationComplianceQueueResponse>(
    '/api/v1/business-registration/admin/licenses/compliance-queue'
  )
}

export const patchBusinessRegistrationLicenseRenewed = async (
  licenseId: string,
  body: { renewed: boolean }
) => {
  return liberiaApiRequest<LiberiaBusinessRegistrationLicenseRenewalRow>(
    `/api/v1/business-registration/admin/licenses/${encodeURIComponent(licenseId)}/renewed`,
    {
      method: 'PATCH',
      body,
    }
  )
}

export interface LiberiaBusinessRegistrationFeeConfig {
  amount_lrd: number
  currency: string
  simulate_payment_allowed?: boolean
}

export interface LiberiaPublicBusinessRegistrationCreateResult {
  access_code: string
  application: LiberiaBusinessRegistrationApplication
}

export const createPublicBusinessRegistrationApplication = async () => {
  if (!liberiaApiBaseUrl) throw new Error('QLS API base URL is not configured')
  const response = await fetch(`${liberiaApiBaseUrl}/api/v1/public/business-registration/applications`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...publicCountryHeaders() },
  })
  const payload = await response.json().catch(() => null)
  if (!response.ok) throw new Error(payload?.error?.message || 'Failed to start application')
  return payload?.data as LiberiaPublicBusinessRegistrationCreateResult
}

export const patchPublicBusinessRegistrationApplication = async (
  applicationId: string,
  accessCode: string,
  body: { draft_payload?: Record<string, unknown>; current_step?: number }
) => {
  if (!liberiaApiBaseUrl) throw new Error('QLS API base URL is not configured')
  const response = await fetch(
    `${liberiaApiBaseUrl}/api/v1/public/business-registration/applications/${encodeURIComponent(applicationId)}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...publicCountryHeaders() },
      body: JSON.stringify({ ...body, access_code: accessCode }),
    }
  )
  const payload = await response.json().catch(() => null)
  if (!response.ok) throw new Error(payload?.error?.message || 'Failed to save draft')
  return payload?.data as LiberiaBusinessRegistrationApplication
}

export const submitPublicBusinessRegistrationApplication = async (
  applicationId: string,
  accessCode: string
) => {
  if (!liberiaApiBaseUrl) throw new Error('QLS API base URL is not configured')
  const response = await fetch(
    `${liberiaApiBaseUrl}/api/v1/public/business-registration/applications/${encodeURIComponent(applicationId)}/submit`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...publicCountryHeaders() },
      body: JSON.stringify({ access_code: accessCode }),
    }
  )
  const payload = await response.json().catch(() => null)
  if (!response.ok) throw new Error(payload?.error?.message || 'Failed to submit')
  return payload?.data as LiberiaBusinessRegistrationApplication
}

export const lookupPublicBusinessRegistrationStatus = async (
  applicationReference: string,
  accessCode: string
) => {
  if (!liberiaApiBaseUrl) throw new Error('QLS API base URL is not configured')
  const q = new URLSearchParams({
    application_reference: applicationReference.trim(),
    access_code: accessCode.trim(),
  })
  const response = await fetch(
    `${liberiaApiBaseUrl}/api/v1/public/business-registration/status?${q.toString()}`,
    { headers: publicCountryHeaders() }
  )
  const payload = await response.json().catch(() => null)
  if (!response.ok) throw new Error(payload?.error?.message || 'Application not found')
  return payload?.data as {
    application_reference: string
    status: string
    current_step: number
    submitted_at: string | null
    fee_paid_at: string | null
    fee_amount_paid: number | null
    officer_notes: string | null
    updated_at: string
  }
}

export const getPublicBusinessRegistrationFeeConfig = async (
  applicationId: string,
  accessCode: string
) => {
  if (!liberiaApiBaseUrl) throw new Error('QLS API base URL is not configured')
  const q = new URLSearchParams({ access_code: accessCode })
  const response = await fetch(
    `${liberiaApiBaseUrl}/api/v1/public/business-registration/applications/${encodeURIComponent(applicationId)}/fee-config?${q.toString()}`,
    { headers: publicCountryHeaders() }
  )
  const payload = await response.json().catch(() => null)
  if (!response.ok) throw new Error(payload?.error?.message || 'Failed to load fee')
  return payload?.data as LiberiaBusinessRegistrationFeeConfig
}

export const listPublicBusinessRegistrationPaymentProviders = async () => {
  if (!liberiaApiBaseUrl) throw new Error('QLS API base URL is not configured')
  const response = await fetch(
    `${liberiaApiBaseUrl}/api/v1/public/business-registration/payment-providers`,
    { headers: publicCountryHeaders() }
  )
  const payload = await response.json().catch(() => null)
  if (!response.ok) throw new Error(payload?.error?.message || 'Failed to load payment providers')
  return (payload?.data || []) as LiberiaPublicPaymentProvider[]
}

export const getPublicBusinessLicenseFees = async () => {
  if (!liberiaApiBaseUrl) throw new Error('QLS API base URL is not configured')
  const response = await fetch(`${liberiaApiBaseUrl}/api/v1/public/business-registration/license-fees`, {
    headers: publicCountryHeaders(),
  })
  const payload = await response.json().catch(() => null)
  if (!response.ok) throw new Error(payload?.error?.message || 'Failed to load license fees')
  return payload?.data as { schedule: LiberiaBusinessLicenseFeeRow[] }
}

export const simulatePublicBusinessRegistrationFee = async (
  applicationId: string,
  accessCode: string
) => {
  if (!liberiaApiBaseUrl) throw new Error('QLS API base URL is not configured')
  const response = await fetch(
    `${liberiaApiBaseUrl}/api/v1/public/business-registration/applications/${encodeURIComponent(applicationId)}/fees/simulate`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...publicCountryHeaders() },
      body: JSON.stringify({ access_code: accessCode }),
    }
  )
  const payload = await response.json().catch(() => null)
  if (!response.ok) throw new Error(payload?.error?.message || 'Failed to record test payment')
  return payload?.data as LiberiaPermitFilingMobilePaymentInit & { status: string }
}

export const initiatePublicBusinessRegistrationFeePaystack = async (
  applicationId: string,
  accessCode: string,
  body: { payment_provider_id: string }
) => {
  if (!liberiaApiBaseUrl) throw new Error('QLS API base URL is not configured')
  const response = await fetch(
    `${liberiaApiBaseUrl}/api/v1/public/business-registration/applications/${encodeURIComponent(applicationId)}/fees/paystack/initiate`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...publicCountryHeaders() },
      body: JSON.stringify({ ...body, access_code: accessCode }),
    }
  )
  const payload = await response.json().catch(() => null)
  if (!response.ok) throw new Error(payload?.error?.message || 'Failed to start Paystack payment')
  return payload?.data as LiberiaPermitFilingMobilePaymentInit
}

export const initiatePublicBusinessRegistrationFeeMobile = async (
  applicationId: string,
  accessCode: string,
  body: { payer_phone: string; payment_provider_id: string }
) => {
  if (!liberiaApiBaseUrl) throw new Error('QLS API base URL is not configured')
  const response = await fetch(
    `${liberiaApiBaseUrl}/api/v1/public/business-registration/applications/${encodeURIComponent(applicationId)}/fees/mobile/initiate`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...publicCountryHeaders() },
      body: JSON.stringify({ ...body, access_code: accessCode }),
    }
  )
  const payload = await response.json().catch(() => null)
  if (!response.ok) throw new Error(payload?.error?.message || 'Failed to start mobile payment')
  return payload?.data as LiberiaPermitFilingMobilePaymentInit
}

export const pollPublicBusinessRegistrationPaymentStatus = async (momoReferenceId: string) => {
  if (!liberiaApiBaseUrl) throw new Error('QLS API base URL is not configured')
  const response = await fetch(
    `${liberiaApiBaseUrl}/api/v1/public/business-registration/payments/mobile/status/${encodeURIComponent(momoReferenceId)}`,
    { headers: publicCountryHeaders() }
  )
  const payload = await response.json().catch(() => null)
  if (!response.ok) throw new Error(payload?.error?.message || 'Failed to check payment status')
  return payload?.data as { status: string; momo_reference_id: string; reason?: string; message?: string }
}

export const uploadPublicBusinessRegistrationDocument = async (
  applicationId: string,
  accessCode: string,
  docType: string,
  file: File
) => {
  if (!liberiaApiBaseUrl) throw new Error('QLS API base URL is not configured')
  const form = new FormData()
  form.append('file', file)
  form.append('doc_type', docType)
  form.append('access_code', accessCode)
  const response = await fetch(
    `${liberiaApiBaseUrl}/api/v1/public/business-registration/applications/${encodeURIComponent(applicationId)}/documents`,
    { method: 'POST', body: form, headers: publicCountryHeaders() }
  )
  const payload = await response.json().catch(() => null)
  if (!response.ok) throw new Error(payload?.error?.message || 'Upload failed')
  return payload?.data as LiberiaBusinessRegistrationDocument
}

export const listPublicBusinessRegistrationDocuments = async (
  applicationId: string,
  accessCode: string
) => {
  if (!liberiaApiBaseUrl) throw new Error('QLS API base URL is not configured')
  const q = new URLSearchParams({ access_code: accessCode })
  const response = await fetch(
    `${liberiaApiBaseUrl}/api/v1/public/business-registration/applications/${encodeURIComponent(applicationId)}/documents?${q.toString()}`,
    { headers: publicCountryHeaders() }
  )
  const payload = await response.json().catch(() => null)
  if (!response.ok) throw new Error(payload?.error?.message || 'Failed to list documents')
  return (payload?.data || []) as LiberiaBusinessRegistrationDocument[]
}

export const simulateBusinessRegistrationFee = async (applicationId: string) => {
  return liberiaApiRequest<LiberiaPermitFilingMobilePaymentInit & { status: string }>(
    `/api/v1/business-registration/applications/${encodeURIComponent(applicationId)}/fees/simulate`,
    { method: 'POST' }
  )
}

export const getBusinessRegistrationFeeConfig = async () => {
  return liberiaApiRequest<LiberiaBusinessRegistrationFeeConfig>('/api/v1/business-registration/fee-config')
}

export const getBusinessRegistrationFeeConfigForApplication = async (applicationId: string) => {
  return liberiaApiRequest<LiberiaBusinessRegistrationFeeConfig>(
    `/api/v1/business-registration/fee-config?application_id=${encodeURIComponent(applicationId)}`
  )
}

export interface LiberiaBusinessRegistrationDocument {
  id: string
  application_id: string
  doc_type: string
  original_filename: string
  mime_type: string | null
  file_size: number
  uploaded_by: string | null
  created_at: string
}

export const listBusinessRegistrationDocuments = async (applicationId: string) => {
  return liberiaApiRequest<LiberiaBusinessRegistrationDocument[]>(
    `/api/v1/business-registration/applications/${encodeURIComponent(applicationId)}/documents`
  )
}

export const uploadBusinessRegistrationDocument = async (
  applicationId: string,
  docType: string,
  file: File,
  context: LiberiaApiAuthContext
) => {
  if (!liberiaApiBaseUrl) {
    throw new Error('QLS API base URL is not configured')
  }

  const formData = new FormData()
  formData.append('file', file)
  formData.append('doc_type', docType)

  const response = await fetch(
    `${liberiaApiBaseUrl}/api/v1/business-registration/applications/${encodeURIComponent(applicationId)}/documents`,
    {
      method: 'POST',
      credentials: 'include',
      headers: buildLiberiaApiHeadersWithoutContentType(context),
      body: formData,
    }
  )

  const payload = await response.json().catch(() => null)

  if (!response.ok) {
    throw new Error(payload?.error?.message || `QLS API request failed (${response.status})`)
  }

  return payload?.data as LiberiaBusinessRegistrationDocument
}

export const verifyLocalBusinessRegistrationIdentity = async (applicationId: string) => {
  return liberiaApiRequest<LiberiaBusinessRegistrationApplication>(
    `/api/v1/business-registration/applications/${encodeURIComponent(applicationId)}/identity/verify-local`,
    {
      method: 'POST',
    }
  )
}

export interface LiberiaRegistrationFeeInitiateResponse {
  flow: string
  momo_reference_id: string
  payment_url?: string | null
  message?: string
}

export const initiateBusinessRegistrationFeeMobile = async (
  applicationId: string,
  body: { amount?: number; payer_phone: string; payment_provider_id: string }
) => {
  return liberiaApiRequest<LiberiaRegistrationFeeInitiateResponse>(
    `/api/v1/business-registration/applications/${encodeURIComponent(applicationId)}/fees/mobile/initiate`,
    {
      method: 'POST',
      body,
    }
  )
}

export const initiateBusinessRegistrationFeePaystack = async (
  applicationId: string,
  body: { amount?: number; payment_provider_id: string }
) => {
  return liberiaApiRequest<LiberiaRegistrationFeeInitiateResponse>(
    `/api/v1/business-registration/applications/${encodeURIComponent(applicationId)}/fees/paystack/initiate`,
    {
      method: 'POST',
      body,
    }
  )
}

export const listLiberiaFireStations = async () => {
  return liberiaApiRequest<LiberiaFireStation[]>('/api/v1/fire/stations')
}

export const createLiberiaFireStation = async (body: Record<string, unknown>) => {
  return liberiaApiRequest<LiberiaFireStation>('/api/v1/fire/stations', {
    method: 'POST',
    body,
  })
}

export const updateLiberiaFireStation = async (stationId: string, body: Record<string, unknown>) => {
  return liberiaApiRequest<LiberiaFireStation>(`/api/v1/fire/stations/${encodeURIComponent(stationId)}`, {
    method: 'PATCH',
    body,
  })
}

export const listLiberiaFireApparatus = async () => {
  return liberiaApiRequest<LiberiaFireApparatus[]>('/api/v1/fire/apparatus')
}

export const createLiberiaFireApparatus = async (body: Record<string, unknown>) => {
  return liberiaApiRequest<LiberiaFireApparatus>('/api/v1/fire/apparatus', {
    method: 'POST',
    body,
  })
}

export const updateLiberiaFireApparatus = async (apparatusId: string, body: Record<string, unknown>) => {
  return liberiaApiRequest<LiberiaFireApparatus>(`/api/v1/fire/apparatus/${encodeURIComponent(apparatusId)}`, {
    method: 'PATCH',
    body,
  })
}

export const deleteLiberiaFireApparatus = async (apparatusId: string) => {
  return liberiaApiRequest<LiberiaFireApparatus>(`/api/v1/fire/apparatus/${encodeURIComponent(apparatusId)}`, {
    method: 'DELETE',
  })
}

export const listLiberiaFireHydrants = async () => {
  return liberiaApiRequest<LiberiaFireHydrant[]>('/api/v1/fire/hydrants')
}

export const createLiberiaFireHydrant = async (body: Record<string, unknown>) => {
  return liberiaApiRequest<LiberiaFireHydrant>('/api/v1/fire/hydrants', {
    method: 'POST',
    body,
  })
}

export const updateLiberiaFireHydrant = async (hydrantId: string, body: Record<string, unknown>) => {
  return liberiaApiRequest<LiberiaFireHydrant>(`/api/v1/fire/hydrants/${encodeURIComponent(hydrantId)}`, {
    method: 'PATCH',
    body,
  })
}

export const deleteLiberiaFireHydrant = async (hydrantId: string) => {
  return liberiaApiRequest<{ id: string }>(`/api/v1/fire/hydrants/${encodeURIComponent(hydrantId)}`, {
    method: 'DELETE',
  })
}

export const listLiberiaFireIncidents = async ({
  status,
  priority,
  startDate,
  endDate,
}: {
  status?: string
  priority?: string
  startDate?: string
  endDate?: string
} = {}) => {
  const params = new URLSearchParams()
  if (status) params.set('status', status)
  if (priority) params.set('priority', priority)
  if (startDate) params.set('startDate', startDate)
  if (endDate) params.set('endDate', endDate)
  const query = params.toString()
  return liberiaApiRequest<LiberiaFireIncident[]>(`/api/v1/fire/incidents${query ? `?${query}` : ''}`)
}

export const createLiberiaFireIncident = async (body: Record<string, unknown>) => {
  return liberiaApiRequest<LiberiaFireIncident>('/api/v1/fire/incidents', {
    method: 'POST',
    body,
  })
}

export const listLiberiaFireDispatchAssignments = async () => {
  return liberiaApiRequest<LiberiaFireDispatchAssignment[]>('/api/v1/fire/dispatches')
}

export const createLiberiaFireDispatchAssignment = async (body: Record<string, unknown>) => {
  return liberiaApiRequest<LiberiaFireDispatchAssignment>('/api/v1/fire/dispatches', {
    method: 'POST',
    body,
  })
}

export const updateLiberiaFireDispatchAssignment = async (
  dispatchId: string,
  body: Record<string, unknown>
) => {
  return liberiaApiRequest<LiberiaFireDispatchAssignment>(`/api/v1/fire/dispatches/${encodeURIComponent(dispatchId)}`, {
    method: 'PATCH',
    body,
  })
}

export const listLiberiaLawEnforcementAgencies = async () => {
  return liberiaApiRequest<LiberiaLawEnforcementAgency[]>('/api/v1/law-enforcement/agencies')
}

export const listLiberiaLawEnforcementDepartments = async (agencyId?: string) => {
  const params = new URLSearchParams()
  if (agencyId) params.set('agencyId', agencyId)
  const query = params.toString()
  return liberiaApiRequest<LiberiaLawEnforcementDepartment[]>(`/api/v1/law-enforcement/departments${query ? `?${query}` : ''}`)
}

export const createLiberiaLawEnforcementDepartment = async (body: Record<string, unknown>) => {
  return liberiaApiRequest<LiberiaLawEnforcementDepartment>('/api/v1/law-enforcement/departments', {
    method: 'POST',
    body,
  })
}

export const updateLiberiaLawEnforcementDepartment = async (departmentId: string, body: Record<string, unknown>) => {
  return liberiaApiRequest<LiberiaLawEnforcementDepartment>(`/api/v1/law-enforcement/departments/${encodeURIComponent(departmentId)}`, {
    method: 'PATCH',
    body,
  })
}

export const listLiberiaLawEnforcementUnits = async ({ availableOnly }: { availableOnly?: boolean } = {}) => {
  const params = new URLSearchParams()
  if (availableOnly) params.set('availableOnly', 'true')
  const query = params.toString()
  return liberiaApiRequest<LiberiaLawEnforcementUnit[]>(`/api/v1/law-enforcement/units${query ? `?${query}` : ''}`)
}

export const createLiberiaLawEnforcementUnit = async (body: Record<string, unknown>) => {
  return liberiaApiRequest<LiberiaLawEnforcementUnit>('/api/v1/law-enforcement/units', {
    method: 'POST',
    body,
  })
}

export const updateLiberiaLawEnforcementUnit = async (unitId: string, body: Record<string, unknown>) => {
  return liberiaApiRequest<LiberiaLawEnforcementUnit>(`/api/v1/law-enforcement/units/${encodeURIComponent(unitId)}`, {
    method: 'PATCH',
    body,
  })
}

export const deleteLiberiaLawEnforcementUnit = async (unitId: string) => {
  return liberiaApiRequest<{ id: string }>(`/api/v1/law-enforcement/units/${encodeURIComponent(unitId)}`, {
    method: 'DELETE',
  })
}

export const listLiberiaPoliceOfficers = async () => {
  return liberiaApiRequest<LiberiaPoliceOfficer[]>('/api/v1/law-enforcement/officers')
}

export const listLiberiaOfficerAgencyAssignments = async () => {
  return liberiaApiRequest<LiberiaOfficerAgencyAssignment[]>('/api/v1/law-enforcement/officer-agency-assignments')
}

export const listLiberiaOfficerSchedules = async ({
  officerUserId,
  date,
}: {
  officerUserId?: string
  date?: string
} = {}) => {
  const params = new URLSearchParams()
  if (officerUserId) params.set('officerUserId', officerUserId)
  if (date) params.set('date', date)
  const query = params.toString()
  return liberiaApiRequest<LiberiaOfficerSchedule[]>(`/api/v1/law-enforcement/officer-schedules${query ? `?${query}` : ''}`)
}

export const createLiberiaOfficerSchedule = async (body: Record<string, unknown>) => {
  return liberiaApiRequest<LiberiaOfficerSchedule>('/api/v1/law-enforcement/officer-schedules', {
    method: 'POST',
    body,
  })
}

export const updateLiberiaOfficerSchedule = async (scheduleId: string, body: Record<string, unknown>) => {
  return liberiaApiRequest<LiberiaOfficerSchedule>(`/api/v1/law-enforcement/officer-schedules/${encodeURIComponent(scheduleId)}`, {
    method: 'PATCH',
    body,
  })
}

export const deleteLiberiaOfficerSchedule = async (scheduleId: string) => {
  return liberiaApiRequest<{ id: string }>(`/api/v1/law-enforcement/officer-schedules/${encodeURIComponent(scheduleId)}`, {
    method: 'DELETE',
  })
}

export const listLiberiaOfficerCertifications = async ({
  officerUserId,
}: {
  officerUserId?: string
} = {}) => {
  const params = new URLSearchParams()
  if (officerUserId) params.set('officerUserId', officerUserId)
  const query = params.toString()
  return liberiaApiRequest<LiberiaOfficerCertification[]>(`/api/v1/law-enforcement/officer-certifications${query ? `?${query}` : ''}`)
}

export const createLiberiaOfficerCertification = async (body: Record<string, unknown>) => {
  return liberiaApiRequest<LiberiaOfficerCertification>('/api/v1/law-enforcement/officer-certifications', {
    method: 'POST',
    body,
  })
}

export const updateLiberiaOfficerCertification = async (certificationId: string, body: Record<string, unknown>) => {
  return liberiaApiRequest<LiberiaOfficerCertification>(`/api/v1/law-enforcement/officer-certifications/${encodeURIComponent(certificationId)}`, {
    method: 'PATCH',
    body,
  })
}

export const deleteLiberiaOfficerCertification = async (certificationId: string) => {
  return liberiaApiRequest<{ id: string }>(`/api/v1/law-enforcement/officer-certifications/${encodeURIComponent(certificationId)}`, {
    method: 'DELETE',
  })
}

export const saveLiberiaOfficerAssignment = async (body: Record<string, unknown>) => {
  return liberiaApiRequest<{ success: boolean }>('/api/v1/law-enforcement/officers/assignment', {
    method: 'POST',
    body,
  })
}

export const listLiberiaLawEnforcementIncidents = async ({
  status,
  priority_level,
  incident_type,
  startDate,
  endDate,
}: {
  status?: string
  priority_level?: string
  incident_type?: string
  startDate?: string
  endDate?: string
} = {}) => {
  const params = new URLSearchParams()
  if (status) params.set('status', status)
  if (priority_level) params.set('priority_level', priority_level)
  if (incident_type) params.set('incident_type', incident_type)
  if (startDate) params.set('startDate', startDate)
  if (endDate) params.set('endDate', endDate)
  const query = params.toString()
  return liberiaApiRequest<LiberiaLawEnforcementIncident[]>(`/api/v1/law-enforcement/incidents${query ? `?${query}` : ''}`)
}

export const createLiberiaLawEnforcementIncident = async (body: Record<string, unknown>) => {
  return liberiaApiRequest<LiberiaLawEnforcementIncident>('/api/v1/law-enforcement/incidents', {
    method: 'POST',
    body,
  })
}

export const updateLiberiaLawEnforcementIncident = async (incidentId: string, body: Record<string, unknown>) => {
  return liberiaApiRequest<LiberiaLawEnforcementIncident>(`/api/v1/law-enforcement/incidents/${encodeURIComponent(incidentId)}`, {
    method: 'PATCH',
    body,
  })
}

export const listLiberiaIncidentNotes = async (incidentId: string) => {
  return liberiaApiRequest<LiberiaIncidentNote[]>(`/api/v1/law-enforcement/incidents/${encodeURIComponent(incidentId)}/notes`)
}

export const createLiberiaIncidentNote = async (incidentId: string, body: Record<string, unknown>) => {
  return liberiaApiRequest<LiberiaIncidentNote>(`/api/v1/law-enforcement/incidents/${encodeURIComponent(incidentId)}/notes`, {
    method: 'POST',
    body,
  })
}

export const listLiberiaDispatchAssignments = async () => {
  return liberiaApiRequest<LiberiaDispatchAssignment[]>('/api/v1/law-enforcement/dispatches')
}

export const createLiberiaDispatchAssignment = async (body: Record<string, unknown>) => {
  return liberiaApiRequest<LiberiaDispatchAssignment>('/api/v1/law-enforcement/dispatches', {
    method: 'POST',
    body,
  })
}

export const updateLiberiaDispatchAssignment = async (dispatchId: string, body: Record<string, unknown>) => {
  return liberiaApiRequest<LiberiaDispatchAssignment>(`/api/v1/law-enforcement/dispatches/${encodeURIComponent(dispatchId)}`, {
    method: 'PATCH',
    body,
  })
}

export const listLiberiaAddressFlags = async ({
  severity,
  flag_type,
}: {
  severity?: string
  flag_type?: string
} = {}) => {
  const params = new URLSearchParams()
  if (severity) params.set('severity', severity)
  if (flag_type) params.set('flag_type', flag_type)
  const query = params.toString()
  return liberiaApiRequest<LiberiaAddressFlag[]>(`/api/v1/law-enforcement/address-flags${query ? `?${query}` : ''}`)
}

export const createLiberiaAddressFlag = async (body: Record<string, unknown>) => {
  return liberiaApiRequest<LiberiaAddressFlag>('/api/v1/law-enforcement/address-flags', {
    method: 'POST',
    body,
  })
}

export const updateLiberiaAddressFlag = async (flagId: string, body: Record<string, unknown>) => {
  return liberiaApiRequest<LiberiaAddressFlag>(`/api/v1/law-enforcement/address-flags/${encodeURIComponent(flagId)}`, {
    method: 'PATCH',
    body,
  })
}

export const deleteLiberiaAddressFlag = async (flagId: string) => {
  return liberiaApiRequest<{ id: string }>(`/api/v1/law-enforcement/address-flags/${encodeURIComponent(flagId)}`, {
    method: 'DELETE',
  })
}

export const getLiberiaTaxStatusCounts = async (context: LiberiaApiAuthContext) => {
  return liberiaApiRequest<Record<string, number>>('/api/v1/tax/status-counts', { context })
}

/** Quarterly points for the tax dashboard revenue projection chart. */
export interface LiberiaTaxQuarterlyRevenuePoint {
  period: string
  label: string
  year: number
  quarter: number
  actualCollected: number | null
  projectedQuarterlyLevy: number
  projectedQuarterlyCollections: number
}

/** Business Registration license fee payments (separate from property tax assessment collections). */
export interface LiberiaTaxBusinessRegistrationFeesSummary {
  calendarYear: number
  totalCollected: number
  paymentCount: number
}

export const getLiberiaTaxDashboard = async (context: LiberiaApiAuthContext) => {
  return liberiaApiRequest<{
    propertyCount: number
    assessmentCount: number
    totalRevenue: number
    collectionRate: number
    overdueParcels: number
    quarterlyRevenueProjection: LiberiaTaxQuarterlyRevenuePoint[]
    revenueProjectionMeta: {
      expectedAnnualRevenue: number
      expectedQuarterlyLevy: number
      projectedQuarterlyCollections: number
      collectionRateUsed: number
    }
    businessRegistrationFees: LiberiaTaxBusinessRegistrationFeesSummary
    permitFilingFees?: LiberiaTaxBusinessRegistrationFeesSummary
  }>('/api/v1/tax/dashboard', { context })
}

export interface LiberiaTaxCurrencyConfig {
  mode: 'major' | 'local'
  major_code: string
  local_country: string
  local_currency_code: string
  local_currency_name: string
}

export interface LiberiaTaxCurrencyResolved {
  code: string
  label: string
}

export interface LiberiaBusinessLicenseFeeRow {
  code: string
  name: string
  fee_lrd: number
  renewal_fee_lrd: number
}

export interface LiberiaBusinessLicenseFeeScheduleResponse {
  setting_key: string
  currency: 'LRD'
  schedule: LiberiaBusinessLicenseFeeRow[]
}

// Attraction entry fees: same shape as business license fees but no
// renewal_fee_lrd. The Tourism module reads this catalog and stores the code
// on attractions; the resolved amount is displayed for informational purposes.
export interface LiberiaAttractionFeeRow {
  code: string
  name: string
  fee_lrd: number
}

export interface LiberiaAttractionFeeScheduleResponse {
  setting_key: string
  currency: 'LRD'
  schedule: LiberiaAttractionFeeRow[]
}

export const getLiberiaTaxCurrency = async (context: LiberiaApiAuthContext) => {
  return liberiaApiRequest<{ config: LiberiaTaxCurrencyConfig; resolved: LiberiaTaxCurrencyResolved }>(
    '/api/v1/tax/currency',
    { context }
  )
}

export const getLiberiaBusinessLicenseFees = async (context: LiberiaApiAuthContext) => {
  return liberiaApiRequest<LiberiaBusinessLicenseFeeScheduleResponse>('/api/v1/tax/license-fees', { context })
}

// Country onboarding readiness. Used to gate address generation and to power
// the Admin "Country Setup Checklist" card.
export interface LiberiaCountrySetupReadiness {
  taxRatesConfigured: boolean
  valuationRulesConfigured: boolean
  isReady: boolean
  missing: Array<'tax_rates' | 'valuation_rules'>
  counts: {
    activeTaxLandUseTypesWithRate: number
    activeBaseRateValuationRules: number
  }
}

export const getLiberiaCountrySetupReadiness = async (
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaCountrySetupReadiness>(
    '/api/v1/tax/setup-readiness',
    { context }
  )
}

export const updateLiberiaTaxCurrency = async (
  body: Partial<LiberiaTaxCurrencyConfig>,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<{ config: LiberiaTaxCurrencyConfig; resolved: LiberiaTaxCurrencyResolved }>(
    '/api/v1/tax/currency',
    { method: 'PUT', body, context }
  )
}

export const updateLiberiaBusinessLicenseFees = async (
  body: Pick<LiberiaBusinessLicenseFeeScheduleResponse, 'schedule'>,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaBusinessLicenseFeeScheduleResponse>('/api/v1/tax/license-fees', {
    method: 'PUT',
    body,
    context,
  })
}

// Tax Revenue-owned attraction fee catalog (informational, no renewal). Read
// is open to any authenticated user; write is restricted to Tax Revenue office
// roles by the backend.
export const getLiberiaAttractionFees = async (context: LiberiaApiAuthContext) => {
  return liberiaApiRequest<LiberiaAttractionFeeScheduleResponse>('/api/v1/tax/attraction-fees', { context })
}

export const updateLiberiaAttractionFees = async (
  body: Pick<LiberiaAttractionFeeScheduleResponse, 'schedule'>,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaAttractionFeeScheduleResponse>('/api/v1/tax/attraction-fees', {
    method: 'PUT',
    body,
    context,
  })
}

export const getLiberiaTaxCollectionByLocation = async (
  context: LiberiaApiAuthContext,
  options?: { taxYear?: number }
) => {
  const qs =
    options?.taxYear != null
      ? `?tax_year=${encodeURIComponent(String(options.taxYear))}`
      : ''
  return liberiaApiRequest<any[]>(`/api/v1/tax/collection-by-location${qs}`, { context })
}

export const listLiberiaDelinquentTaxAssessments = async (context: LiberiaApiAuthContext) => {
  return liberiaApiRequest<any[]>('/api/v1/tax/delinquent-assessments', { context })
}

export const listLiberiaTaxFieldCollectors = async (context: LiberiaApiAuthContext) => {
  return liberiaApiRequest<LiberiaTaxFieldCollector[]>('/api/v1/tax/field-collectors', { context })
}

export const listLiberiaLinkableCollectorUsers = async (context: LiberiaApiAuthContext) => {
  return liberiaApiRequest<any[]>('/api/v1/tax/field-collectors/linkable-users', { context })
}

export const createLiberiaTaxFieldCollector = async (
  body: Record<string, unknown>,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaTaxFieldCollector>('/api/v1/tax/field-collectors', {
    method: 'POST',
    body,
    context,
  })
}

export const updateLiberiaTaxFieldCollector = async (
  id: string,
  body: Record<string, unknown>,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaTaxFieldCollector>(`/api/v1/tax/field-collectors/${id}`, {
    method: 'PATCH',
    body,
    context,
  })
}

export const deleteLiberiaTaxFieldCollector = async (
  id: string,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<{ id: string; deleted: boolean }>(`/api/v1/tax/field-collectors/${id}`, {
    method: 'DELETE',
    context,
  })
}

export const listLiberiaPropertyValuationRules = async (context: LiberiaApiAuthContext) => {
  return liberiaApiRequest<LiberiaPropertyValuationRule[]>('/api/v1/tax/valuation-rules', { context })
}

export const createLiberiaPropertyValuationRule = async (
  body: Record<string, unknown>,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaPropertyValuationRule>('/api/v1/tax/valuation-rules', {
    method: 'POST',
    body,
    context,
  })
}

export const updateLiberiaPropertyValuationRule = async (
  id: string,
  body: Record<string, unknown>,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaPropertyValuationRule>(`/api/v1/tax/valuation-rules/${id}`, {
    method: 'PATCH',
    body,
    context,
  })
}

export const deleteLiberiaPropertyValuationRule = async (
  id: string,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<{ id: string; deleted: boolean }>(`/api/v1/tax/valuation-rules/${id}`, {
    method: 'DELETE',
    context,
  })
}

export const createLiberiaPropertyInvitation = async (
  taxPropertyId: string,
  body: { email: string; phone?: string | null },
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaPropertyInvitation>(`/api/v1/tax/properties/${taxPropertyId}/invitations`, {
    method: 'POST',
    body,
    context,
  })
}

export const getLiberiaTaxBillGenerationStats = async (
  fiscalYear: number,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<{
    totalApproved: number
    totalBilled: number
    totalUnbilled: number
    unbilledRevenue: number
  }>(`/api/v1/tax/bill-generation/stats?fiscal_year=${encodeURIComponent(String(fiscalYear))}`, { context })
}

export const generateLiberiaTaxBills = async (
  body: Record<string, unknown>,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<{ generatedCount: number; message: string }>('/api/v1/tax/bill-generation', {
    method: 'POST',
    body,
    context,
  })
}

/** Property-roll analytics window; `businessRegistrationFees` is a separate revenue stream from license registrations. */
export interface LiberiaTaxAnalyticsResponse {
  totalRevenue: number
  totalDue: number
  collectionRate: number
  delinquentCount: number
  propertyCount: number
  businessRegistrationFees: {
    totalCollected: number
    paymentCount: number
  }
  revenueByLandUse: Array<{ name: string; count: number; revenue: number }>
  paymentMethodStats: unknown[]
  propertySize: Array<{ name: string; value: number }>
  valuationAccuracy: number
  totalAssessedValue: number
  totalCalculatedValue: number
  propertiesWithValuation: number
  activeRulesCount: number
  ruleTypeDistribution: unknown[]
  expectedMonthlyRevenue: number
  expectedQuarterlyRevenue: number
  expectedAnnualRevenue: number
  trendData: Array<{ date: string; revenue: number; collections: number; delinquent: number }>
}

export const getLiberiaTaxAnalytics = async (
  range: '7days' | '30days' | '90days',
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaTaxAnalyticsResponse>(
    `/api/v1/tax/analytics?range=${encodeURIComponent(range)}`,
    { context }
  )
}

export const listLiberiaTaxReports = async (
  reportType: string,
  context: LiberiaApiAuthContext
) => {
  const query = reportType && reportType !== 'all' ? `?type=${encodeURIComponent(reportType)}` : ''
  return liberiaApiRequest<any[]>(`/api/v1/tax/reports${query}`, { context })
}

export const createLiberiaTaxReport = async (
  body: {
    report_type: string
    time_period: string
  },
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<any>('/api/v1/tax/reports', {
    method: 'POST',
    body,
    context,
  })
}

export const listLiberiaTaxPaymentRecords = async (context: LiberiaApiAuthContext) => {
  return liberiaApiRequest<LiberiaTaxPaymentRecord[]>('/api/v1/tax/payments', { context })
}

export const createLiberiaTaxPaymentRecord = async (
  body: Record<string, unknown>,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaTaxPaymentRecord>('/api/v1/tax/payments', {
    method: 'POST',
    body,
    context,
  })
}

export const listLiberiaAdminUsers = async (context: LiberiaApiAuthContext) => {
  return liberiaApiRequest<LiberiaAdminUser[]>('/api/v1/admin/users', { context })
}

export const getLiberiaAdminUser = async (userId: string, context: LiberiaApiAuthContext) => {
  return liberiaApiRequest<LiberiaAdminUser>(`/api/v1/admin/users/${userId}`, { context })
}

export const createLiberiaAdminUser = async (
  body: {
    email: string
    password: string
    full_name: string
    primary_role: string
    roles: string[]
    scoped_roles?: Record<string, string[]>
    is_provisioned_account?: boolean
    must_change_password?: boolean
    country_id?: string | null
    is_qls_admin?: boolean
  },
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaAdminUser>('/api/v1/admin/users', {
    method: 'POST',
    body,
    context,
  })
}

export const deleteLiberiaAdminUser = async (
  userId: string,
  reason: string,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<{ id: string; deleted: boolean }>(`/api/v1/admin/users/${userId}`, {
    method: 'DELETE',
    body: { reason },
    context,
  })
}

export const getLiberiaAdminStatus = async (context: LiberiaApiAuthContext) => {
  return liberiaApiRequest<{ isAdmin: boolean }>('/api/v1/admin/status', { context })
}

export const syncLiberiaProvisionedUser = async (
  body: {
    user_id: string
    email: string
    full_name: string
    primary_role: string
    roles: string[]
    scoped_roles?: Record<string, string[]>
    is_provisioned_account?: boolean
    must_change_password?: boolean
  },
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaAdminUser>('/api/v1/admin/users/sync-provisioned', {
    method: 'POST',
    body,
    context,
  })
}

export const updateLiberiaAdminUserPrimaryRole = async (
  userId: string,
  primaryRole: string,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaAdminUser>(`/api/v1/admin/users/${userId}/primary-role`, {
    method: 'PATCH',
    body: {
      primary_role: primaryRole,
    },
    context,
  })
}

export const updateLiberiaAdminUserRoles = async (
  userId: string,
  body: {
    roles: string[]
    scoped_roles?: Record<string, string[]>
  },
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaAdminUser>(`/api/v1/admin/users/${userId}/roles`, {
    method: 'PUT',
    body,
    context,
  })
}

// Multi-Portals tab client: only edits the user's additional portal access.
// The primary portal is owned by the Users & Roles tab and is NOT touched by
// this endpoint — the backend always preserves it inside `user_roles`.
export const updateLiberiaAdminUserAdditionalRoles = async (
  userId: string,
  body: {
    additional_roles: string[]
    scoped_roles?: Record<string, string[]>
  },
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaAdminUser>(
    `/api/v1/admin/users/${userId}/additional-roles`,
    {
      method: 'PUT',
      body,
      context,
    }
  )
}

export const resetLiberiaAdminUserPassword = async (
  userId: string,
  newPassword: string,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaAdminUser>(`/api/v1/admin/users/${userId}/reset-password`, {
    method: 'POST',
    body: {
      new_password: newPassword,
    },
    context,
  })
}

export const updateLiberiaAdminUserMfa = async (
  userId: string,
  required: boolean,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaAdminUser>(`/api/v1/admin/users/${userId}/mfa`, {
    method: 'PATCH',
    body: { required },
    context,
  })
}

function normalizeGeographyCountry(row: Record<string, unknown>): LiberiaCountry {
  const st = String(row.status ?? 'active')
  const status: LiberiaCountry['status'] =
    st === 'inactive' || st === 'pending' || st === 'active' ? st : 'active'
  return {
    id: String(row.id ?? ''),
    name: String(row.name ?? ''),
    code: String(row.code ?? ''),
    status,
    created_at: String(row.created_at ?? row.createdAt ?? ''),
    updated_at: String(row.updated_at ?? row.updatedAt ?? ''),
  }
}

function normalizeGeographyAdministrativeArea(row: Record<string, unknown>): LiberiaAdministrativeArea {
  const coords = row.coordinates
  const hasCoords =
    coords &&
    typeof coords === 'object' &&
    coords !== null &&
    'latitude' in (coords as object) &&
    'longitude' in (coords as object)
  const pop = row.population
  const km = row.area_km2 ?? row.areaKm2
  const countryId = (row.countryId ?? row.country_id ?? null) as string | null
  const parentIdRaw = row.parentId ?? row.parent_id ?? null
  const parent_id = parentIdRaw ? String(parentIdRaw) : null
  const nestedCountry =
    row.country && typeof row.country === 'object' ? (row.country as Record<string, unknown>) : null
  const nestedParent =
    row.parent && typeof row.parent === 'object' ? (row.parent as Record<string, unknown>) : null
  const countryName = String(nestedCountry?.name ?? row.countryName ?? row.country_name ?? '')
  const countryCode = String(
    nestedCountry?.code ?? nestedCountry?.iso_code ?? row.countryCode ?? row.country_code ?? ''
  )
  const parentName = String(nestedParent?.name ?? row.parentName ?? row.parent_name ?? '')

  return {
    id: String(row.id ?? ''),
    name: String(row.name ?? ''),
    type: String(row.type ?? ''),
    level: Number(row.level ?? 0),
    parent_id,
    country_id: countryId ? String(countryId) : nestedCountry?.id ? String(nestedCountry.id) : null,
    iso_code: (row.iso_code ?? row.isoCode ?? null) as string | null,
    population: pop != null && pop !== '' && Number.isFinite(Number(pop)) ? Number(pop) : null,
    area_km2: km != null && km !== '' && Number.isFinite(Number(km)) ? Number(km) : null,
    coordinates: hasCoords
      ? {
          latitude: Number((coords as { latitude: number }).latitude),
          longitude: Number((coords as { longitude: number }).longitude),
        }
      : null,
    boundaries: row.boundaries ?? null,
    metadata:
      row.metadata && typeof row.metadata === 'object' && !Array.isArray(row.metadata)
        ? (row.metadata as Record<string, unknown>)
        : {},
    is_active: Boolean(row.isActive ?? row.is_active ?? true),
    created_at: String(row.createdAt ?? row.created_at ?? ''),
    updated_at: String(row.updatedAt ?? row.updated_at ?? ''),
    country:
      nestedCountry || countryId
        ? {
            id: String(nestedCountry?.id ?? countryId ?? ''),
            name: countryName,
            code: countryCode,
          }
        : undefined,
    parent:
      nestedParent || parent_id
        ? {
            id: String(nestedParent?.id ?? parent_id ?? ''),
            name: parentName,
            type: String(nestedParent?.type ?? row.parent_type ?? ''),
          }
        : undefined,
  }
}

/**
 * Read-only countries from Postgres (`public.countries`) via `/api/v1/countries`.
 * Use for partner, surveyor, and address flows. Admin CRUD stays on `/admin/geography/countries`.
 */
export const listLiberiaGeographyCountries = async (
  context: LiberiaApiAuthContext,
  status?: string
) => {
  const query = status ? `?status=${encodeURIComponent(status)}` : ''
  const raw = await liberiaApiRequest<Record<string, unknown>[] | null>(`/api/v1/countries${query}`, { context })
  return (raw || []).map(normalizeGeographyCountry)
}

/**
 * Read-only administrative areas from Postgres via `/api/v1/administrative-areas`.
 */
export const listLiberiaGeographyAdministrativeAreas = async (
  context: LiberiaApiAuthContext,
  params?: {
    countryId?: string
    parentId?: string
    level?: number
    type?: string
    activeOnly?: boolean
  }
) => {
  const q = new URLSearchParams()
  if (params?.countryId) q.set('countryId', params.countryId)
  if (params?.parentId) q.set('parentId', params.parentId)
  if (params?.level !== undefined) q.set('level', String(params.level))
  if (params?.type) q.set('type', params.type)
  if (params?.activeOnly === false) q.set('activeOnly', 'false')
  const suffix = q.toString() ? `?${q.toString()}` : ''
  const raw = await liberiaApiRequest<Record<string, unknown>[] | null>(`/api/v1/administrative-areas${suffix}`, {
    context,
  })
  return (raw || []).map(normalizeGeographyAdministrativeArea)
}

export const listLiberiaAdminCountries = async (
  context: LiberiaApiAuthContext,
  status?: string
) => {
  const query = status ? `?status=${encodeURIComponent(status)}` : ''
  return liberiaApiRequest<LiberiaCountry[]>(`/api/v1/admin/geography/countries${query}`, { context })
}

export const getLiberiaCurrentUser = async (context: LiberiaApiAuthContext) => {
  return liberiaApiRequest<LiberiaCurrentUser>('/api/v1/me', { context })
}

export const getLiberiaUserProfile = async (context: LiberiaApiAuthContext) => {
  return liberiaApiRequest<LiberiaUserProfile>('/api/v1/me/profile', { context })
}

export const updateLiberiaUserProfile = async (
  body: Partial<LiberiaUserProfile>,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaUserProfile>('/api/v1/me/profile', {
    method: 'PUT',
    body,
    context,
  })
}

export const getLiberiaNotificationPreferences = async (context: LiberiaApiAuthContext) => {
  return liberiaApiRequest<LiberiaNotificationPreferences>('/api/v1/me/notification-preferences', {
    context,
  })
}

export const updateLiberiaNotificationPreferences = async (
  body: Partial<LiberiaNotificationPreferences>,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaNotificationPreferences>('/api/v1/me/notification-preferences', {
    method: 'PUT',
    body,
    context,
  })
}

export const getLiberiaCurrentUserRoles = async (context: LiberiaApiAuthContext) => {
  return liberiaApiRequest<{
    primaryRole: string | null
    coarseRole: string | null
    roles: LiberiaRoleAccess[]
  }>('/api/v1/me/roles', { context })
}

export const createLiberiaAdminCountry = async (
  body: {
    name: string
    code: string
    status?: 'active' | 'inactive' | 'pending'
  },
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaCountry>('/api/v1/admin/geography/countries', {
    method: 'POST',
    body,
    context,
  })
}

export const updateLiberiaAdminCountry = async (
  countryId: string,
  body: Partial<{
    name: string
    code: string
    status: 'active' | 'inactive' | 'pending'
  }>,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaCountry>(`/api/v1/admin/geography/countries/${countryId}`, {
    method: 'PATCH',
    body,
    context,
  })
}

export const deleteLiberiaAdminCountry = async (
  countryId: string,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<{ id: string; deleted: boolean }>(`/api/v1/admin/geography/countries/${countryId}`, {
    method: 'DELETE',
    context,
  })
}

export const bulkUpsertLiberiaAdminCountries = async (
  countries: Array<Record<string, unknown>>,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaCountry[]>('/api/v1/admin/geography/countries/bulk-upsert', {
    method: 'POST',
    body: { countries },
    context,
  })
}

export const listLiberiaAdministrativeAreaTypes = async (context: LiberiaApiAuthContext) => {
  return liberiaApiRequest<LiberiaAdministrativeAreaType[]>('/api/v1/admin/geography/area-types', { context })
}

export const listLiberiaCountryHierarchyLevels = async (
  countryId: string,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaCountryHierarchyLevel[]>(
    `/api/v1/admin/geography/countries/${encodeURIComponent(countryId)}/hierarchy-levels`,
    { context }
  )
}

export const createLiberiaCountryHierarchyLevel = async (
  countryId: string,
  body: {
    level_number: number
    level_name: string
    level_type: string
    is_required: boolean
    sort_order: number
  },
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaCountryHierarchyLevel>(
    `/api/v1/admin/geography/countries/${encodeURIComponent(countryId)}/hierarchy-levels`,
    {
      method: 'POST',
      body,
      context,
    }
  )
}

export const updateLiberiaCountryHierarchyLevel = async (
  id: string,
  body: Partial<{
    level_number: number
    level_name: string
    level_type: string
    is_required: boolean
    sort_order: number
  }>,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaCountryHierarchyLevel>(
    `/api/v1/admin/geography/hierarchy-levels/${encodeURIComponent(id)}`,
    {
      method: 'PATCH',
      body,
      context,
    }
  )
}

export const deleteLiberiaCountryHierarchyLevel = async (
  id: string,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<{ id: string; deleted: boolean }>(
    `/api/v1/admin/geography/hierarchy-levels/${encodeURIComponent(id)}`,
    {
      method: 'DELETE',
      context,
    }
  )
}

/** Featured announcement for this visitor: their country plus All. Omit the id to see All only. */
export const getLiberiaActiveAnnouncement = async (countryId?: string | null) => {
  const query = new URLSearchParams()
  if (countryId) {
    query.set('country_id', countryId)
  }
  const suffix = query.toString() ? `?${query.toString()}` : ''
  return liberiaApiRequest<LiberiaAnnouncement | null>(`/api/v1/announcements/active${suffix}`)
}

export const listLiberiaAnnouncements = async (params?: {
  activeOnly?: boolean
  category?: string[]
  announcementType?: string[]
  status?: string[]
  /** Matches API `sortBy=featured` (priority then recency). */
  sortBy?: 'updated' | 'featured'
}) => {
  const query = new URLSearchParams()

  if (params?.activeOnly) query.set('activeOnly', 'true')
  if (params?.category?.length) query.set('category', params.category.join(','))
  if (params?.announcementType?.length) query.set('announcementType', params.announcementType.join(','))
  if (params?.status?.length) query.set('status', params.status.join(','))
  if (params?.sortBy === 'featured') query.set('sortBy', 'featured')

  const suffix = query.toString() ? `?${query.toString()}` : ''
  return liberiaApiRequest<LiberiaAnnouncement[]>(`/api/v1/announcements${suffix}`)
}

export const createLiberiaSupportSubmission = async (body: {
  name: string
  email: string
  subject: string
  message: string
  submissionType?: string
  phone?: string
  organization?: string
}) => {
  return liberiaApiRequest<LiberiaSupportSubmission>('/api/v1/support-submissions', {
    method: 'POST',
    body,
  })
}

export const listLiberiaUserNotifications = async (
  params: {
    type?: string
    isRead?: boolean
    limit?: number
    countryId?: string | null
  },
  context: LiberiaApiAuthContext
) => {
  const query = new URLSearchParams()

  if (params.type) query.set('type', params.type)
  if (params.isRead !== undefined) query.set('isRead', String(params.isRead))
  if (params.countryId) query.set('country_id', params.countryId)
  query.set('limit', String(params.limit ?? 50))

  return liberiaApiRequest<LiberiaUserNotification[]>(
    `/api/v1/notifications?${query.toString()}`,
    { context }
  )
}

export const markLiberiaNotificationAsRead = async (
  notificationId: string,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaUserNotification>(
    `/api/v1/notifications/${encodeURIComponent(notificationId)}/read`,
    {
      method: 'POST',
      context,
    }
  )
}

export const listLiberiaAdminAnnouncements = async (context: LiberiaApiAuthContext) => {
  return liberiaApiRequest<LiberiaAnnouncement[]>('/api/v1/admin/communications/announcements', { context })
}

export const createLiberiaAdminAnnouncement = async (
  body: {
    subject: string
    message: string
    status: 'active' | 'inactive'
    priority: 'normal' | 'urgent' | 'emergency'
    category?: string
    announcement_type?: string | null
    target_geoblocs?: string[] | null
    affected_area_description?: string | null
    detour_description?: string | null
    estimated_delay_minutes?: number | null
    work_start_date?: string | null
    work_end_date?: string | null
    /** Null is All countries. Omit the key so road and law-enforcement saves use the creator's country. */
    country_id?: string | null
  },
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaAnnouncement>('/api/v1/admin/communications/announcements', {
    method: 'POST',
    body,
    context,
  })
}

export const updateLiberiaAdminAnnouncement = async (
  announcementId: string,
  body: {
    subject: string
    message: string
    status: 'active' | 'inactive'
    priority: 'normal' | 'urgent' | 'emergency'
    category?: string
    announcement_type?: string | null
    target_geoblocs?: string[] | null
    affected_area_description?: string | null
    detour_description?: string | null
    estimated_delay_minutes?: number | null
    work_start_date?: string | null
    work_end_date?: string | null
    /** Null is All countries. Omit the key to leave the saved country unchanged. */
    country_id?: string | null
  },
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaAnnouncement>(`/api/v1/admin/communications/announcements/${announcementId}`, {
    method: 'PATCH',
    body,
    context,
  })
}

export const deleteLiberiaAdminAnnouncement = async (
  announcementId: string,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<{ id: string; deleted: boolean }>(`/api/v1/admin/communications/announcements/${announcementId}`, {
    method: 'DELETE',
    context,
  })
}

export const listLiberiaAdminSupportSubmissions = async (
  type: 'contact' | 'support' | 'call_request',
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaSupportSubmission[]>(`/api/v1/admin/communications/support?type=${encodeURIComponent(type)}`, {
    context,
  })
}

export const updateLiberiaAdminSupportSubmissionStatus = async (
  id: string,
  status: string,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaSupportSubmission>(`/api/v1/admin/communications/support/${id}/status`, {
    method: 'PATCH',
    body: { status },
    context,
  })
}

export const respondToLiberiaAdminSupportSubmission = async (
  id: string,
  response: string,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaSupportSubmission>(`/api/v1/admin/communications/support/${id}/respond`, {
    method: 'POST',
    body: { response },
    context,
  })
}

export const sendLiberiaSystemNotification = async (
  body: {
    title: string
    message: string
    target_roles: string[]
    country_id?: string | null
  },
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<{ notifications_sent: number; recipient_count: number }>(
    '/api/v1/admin/communications/notifications/system',
    {
      method: 'POST',
      body,
      context,
    }
  )
}

export const createLiberiaAdministrativeAreaType = async (
  body: {
    name: string
    description?: string
    level?: number
    is_active?: boolean
  },
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaAdministrativeAreaType>('/api/v1/admin/geography/area-types', {
    method: 'POST',
    body,
    context,
  })
}

export const updateLiberiaAdministrativeAreaType = async (
  areaTypeId: string,
  body: Partial<{
    name: string
    description: string
    level: number
    is_active: boolean
  }>,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaAdministrativeAreaType>(`/api/v1/admin/geography/area-types/${areaTypeId}`, {
    method: 'PATCH',
    body,
    context,
  })
}

export const deleteLiberiaAdministrativeAreaType = async (
  areaTypeId: string,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<{ id: string; deleted: boolean }>(`/api/v1/admin/geography/area-types/${areaTypeId}`, {
    method: 'DELETE',
    context,
  })
}

export const listLiberiaAdministrativeAreas = async (
  context: LiberiaApiAuthContext,
  params?: {
    countryId?: string
    parentId?: string
    level?: number
    type?: string
    activeOnly?: boolean
  }
) => {
  const query = new URLSearchParams()

  if (params?.countryId) query.set('countryId', params.countryId)
  if (params?.parentId) query.set('parentId', params.parentId)
  if (params?.level !== undefined) query.set('level', String(params.level))
  if (params?.type) query.set('type', params.type)
  if (params?.activeOnly === false) query.set('activeOnly', 'false')

  const suffix = query.toString() ? `?${query.toString()}` : ''
  return liberiaApiRequest<LiberiaAdministrativeArea[]>(`/api/v1/admin/geography/administrative-areas${suffix}`, {
    context,
  })
}

export const createLiberiaAdministrativeArea = async (
  body: Record<string, unknown>,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaAdministrativeArea>('/api/v1/admin/geography/administrative-areas', {
    method: 'POST',
    body,
    context,
  })
}

export const updateLiberiaAdministrativeArea = async (
  areaId: string,
  body: Record<string, unknown>,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaAdministrativeArea>(`/api/v1/admin/geography/administrative-areas/${areaId}`, {
    method: 'PATCH',
    body,
    context,
  })
}

export const deleteLiberiaAdministrativeArea = async (
  areaId: string,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<{ id: string; deleted: boolean }>(`/api/v1/admin/geography/administrative-areas/${areaId}`, {
    method: 'DELETE',
    context,
  })
}

export const bulkUpsertLiberiaAdministrativeAreas = async (
  areas: Array<Record<string, unknown>>,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaAdministrativeArea[]>('/api/v1/admin/geography/administrative-areas/bulk-upsert', {
    method: 'POST',
    body: { areas },
    context,
  })
}

export const listLiberiaPartnerTypes = async (context: LiberiaApiAuthContext) => {
  return liberiaApiRequest<LiberiaPartnerType[]>('/api/v1/admin/setup/partner-types', { context })
}

export const listLiberiaAvailablePartnerTypes = async () => {
  return liberiaApiRequest<LiberiaPartnerType[]>('/api/v1/partner-types')
}

export const getLiberiaMyPartnerProfile = async (context: LiberiaApiAuthContext) => {
  return liberiaApiRequest<LiberiaPartnerProfile | null>('/api/v1/me/partner-profile', { context })
}

export const updateLiberiaMyPartnerProfile = async (
  body: {
    company_name?: string | null
    contact_person?: string | null
    phone?: string | null
    website?: string | null
    description?: string | null
    partner_type_id?: string | null
  },
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaPartnerProfile>('/api/v1/me/partner-profile', {
    method: 'PUT',
    body,
    context,
  })
}

export const listLiberiaSystemSettings = async (context: LiberiaApiAuthContext) => {
  return liberiaApiRequest<LiberiaSystemSetting[]>('/api/v1/admin/setup/system-settings', { context })
}

export interface LiberiaVendorModuleSetting {
  setting_key: string
  setting_value: string
  setting_type?: string
  country_id?: string
}

export interface LiberiaVendorAnalytics {
  totalUsers?: number
  usersByModule?: Array<{ module?: string; count?: number }>
  monthlyGrowth?: Array<{ period?: string; count?: number }>
  quarterlyGrowth?: Array<{ period?: string; count?: number }>
  yearlyGrowth?: Array<{ period?: string; count?: number }>
  dailyLogins?: Array<{ date?: string; unique_users?: number }>
  searchMetrics?: {
    totalAddressSearches?: number
    totalPlaceSearches?: number
    addressSearches?: Array<{ date?: string; count?: number }>
    placeSearches?: Array<{ date?: string; count?: number }>
  }
  googleApiMetrics?: {
    totalCalls?: number
    dailyCalls?: Array<{ date?: string; count?: number }>
    hourlyDistribution?: Array<{ hour?: number; count?: number }>
    peakHour?: number
    leastBusyHour?: number
    mostActiveAreas?: Array<{ area?: string; count?: number }>
  }
  directionsMetrics?: {
    totalRequests?: number
    dailyRequests?: Array<{ date?: string; count?: number }>
  }
}

export const getVendorAnalytics = async (countryId: string, context: LiberiaApiAuthContext) => {
  return liberiaApiRequest<LiberiaVendorAnalytics>(
    `/api/v1/vendor/analytics?country_id=${encodeURIComponent(countryId)}`,
    { context }
  )
}

export const listVendorPackagingCountries = async (context: LiberiaApiAuthContext) => {
  const raw = await liberiaApiRequest<Array<Record<string, unknown>>>('/api/v1/vendor/countries', { context })
  return (raw || []).map((row) => ({
    id: String(row.id || ''),
    name: String(row.name || ''),
    code: String(row.code || ''),
    status: String(row.status || 'active'),
  }))
}

export const listVendorCountryModules = async (
  countryId: string,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaVendorModuleSetting[]>(
    `/api/v1/vendor/module-settings?country_id=${encodeURIComponent(countryId)}`,
    { context }
  )
}

export const updateVendorCountryModule = async (
  moduleKey: string,
  countryId: string,
  enabled: boolean,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaVendorModuleSetting>(
    `/api/v1/vendor/module-settings/${encodeURIComponent(moduleKey)}`,
    {
      method: 'PUT',
      body: { country_id: countryId, enabled },
      context,
    }
  )
}

// Unauthenticated read for public-facing branding keys. Safe for logged-out visitors.
// Backed by GET /api/v1/public/branding which whitelists a fixed key set server-side.
/** Merged branding for this visitor: their country, then All for any key they have not set. */
export const listLiberiaPublicBranding = async (countryId?: string | null) => {
  const query = new URLSearchParams()
  if (countryId) {
    query.set('country_id', countryId)
  }
  const suffix = query.toString() ? `?${query.toString()}` : ''
  return liberiaAuthRequest<LiberiaSystemSetting[]>(`/api/v1/public/branding${suffix}`)
}

/** Admin read. Null country is All. A country id returns that country's values with All as fallback. */
export const listLiberiaAdminBranding = async (
  countryId: string | null,
  context: LiberiaApiAuthContext
) => {
  const query = new URLSearchParams()
  query.set('country_id', countryId || 'all')
  return liberiaApiRequest<LiberiaSystemSetting[]>(
    `/api/v1/admin/setup/branding?${query.toString()}`,
    { context }
  )
}

export const updateLiberiaSystemSetting = async (
  key: string,
  value: string,
  context: LiberiaApiAuthContext,
  countryId?: string | null
) => {
  return liberiaApiRequest<LiberiaSystemSetting>(`/api/v1/admin/setup/system-settings/${encodeURIComponent(key)}`, {
    method: 'PUT',
    body: { value, country_id: countryId ?? null },
    context,
  })
}

export const getLiberiaSystemHealth = async (context: LiberiaApiAuthContext) => {
  return liberiaApiRequest<LiberiaSystemHealth>('/api/v1/admin/setup/system-health', { context })
}

export interface LiberiaBulkProcessingBatch {
  id: string
  batch_name: string
  country_code: string
  administrative_area: string
  structure_type: string
  total_buildings: number
  generated_count: number
  validated_count: number
  rejected_count: number
  integrated_count: number
  processing_status: 'generated' | 'queued' | 'in_progress' | 'completed' | 'failed' | 'cancelled' | 'scheduled'
  error_message?: string | null
  started_at: string
  completed_at?: string | null
  created_by?: string | null
  scheduled_at?: string | null
}

export interface LiberiaBulkProcessingStagingItem {
  id: string
  batch_id: string
  batch_name: string
  country_code: string
  administrative_area: string
  structure_type: string
  building_footprint_data: Record<string, unknown> | null
  confidence_score: number | null
  building_area_m2: number | null
  generated_building_number: string
  generated_street_name: string
  generated_city: string
  generated_district: string
  generated_county: string
  generated_state: string
  generated_country: string
  generated_postal_code: string
  generated_zipcode: string
  generated_plus_code: string
  generated_formatted_address: string
  generated_coordinates: { latitude: number; longitude: number } | null
  generated_geobloc_id?: string | null
  processing_status: 'generated' | 'validated' | 'rejected' | 'integrated'
  validation_notes?: string | null
  validated_by?: string | null
  validated_at?: string | null
  integrated_at?: string | null
  integrated_by?: string | null
  generated_address_id?: string | null
  osm_enrichment_status?: string | null
  qls_enrichment_status?: string | null
  created_at: string
  updated_at: string
  created_by?: string | null
}

export const listLiberiaBulkProcessingBatches = async (context: LiberiaApiAuthContext) => {
  return liberiaApiRequest<LiberiaBulkProcessingBatch[]>('/api/v1/admin/bulk-processing/batches', { context })
}

export const updateLiberiaBulkProcessingBatch = async (
  batchId: string,
  updates: Record<string, unknown>,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaBulkProcessingBatch>(`/api/v1/admin/bulk-processing/batches/${encodeURIComponent(batchId)}`, {
    method: 'PATCH',
    body: updates,
    context,
  })
}

export const deleteLiberiaBulkProcessingBatch = async (
  batchId: string,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<{ id: string; deleted: boolean }>(`/api/v1/admin/bulk-processing/batches/${encodeURIComponent(batchId)}`, {
    method: 'DELETE',
    context,
  })
}

export const listLiberiaBulkProcessingStaging = async (
  params: {
    batchId?: string
    status?: string
  },
  context: LiberiaApiAuthContext
) => {
  const query = new URLSearchParams()
  if (params.batchId && params.batchId !== 'all') query.set('batchId', params.batchId)
  if (params.status && params.status !== 'all') query.set('status', params.status)
  const suffix = query.toString() ? `?${query.toString()}` : ''
  return liberiaApiRequest<LiberiaBulkProcessingStagingItem[]>(`/api/v1/admin/bulk-processing/staging${suffix}`, { context })
}

export const updateLiberiaBulkProcessingStaging = async (
  body: {
    ids?: string[]
    batchId?: string | null
    updates: Record<string, unknown>
  },
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaBulkProcessingStagingItem[]>('/api/v1/admin/bulk-processing/staging', {
    method: 'PATCH',
    body,
    context,
  })
}

export const deleteLiberiaBulkProcessingStaging = async (
  body: {
    ids?: string[]
    batchId?: string | null
  },
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<{ deletedCount: number; ids: string[] }>('/api/v1/admin/bulk-processing/staging', {
    method: 'DELETE',
    body,
    context,
  })
}

export const integrateLiberiaBulkProcessingStaging = async (
  ids: string[],
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<{
    inserted: RegistryAddress[]
    failed: Array<{ id: string; error: string }>
  }>('/api/v1/admin/bulk-processing/staging/integrate', {
    method: 'POST',
    body: { ids },
    context,
  })
}

export const importLiberiaOpenBuildingsCsv = async (
  body: {
    csvContent: string
    batchName: string
    countryCode: string
    administrativeArea: string
    structureType?: string
    minConfidence?: number
    maxBuildings?: number
  },
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<{
    batchId: string
    totalBuildings: number
    insertedRecords: number
    message: string
  }>('/api/v1/admin/bulk-processing/import-open-buildings', {
    method: 'POST',
    body,
    context,
  })
}

export const generateLiberiaBulkAddresses = async (
  body: {
    batchName: string
    countryCode: string
    administrativeArea: string
    administrativeAreaData?: Record<string, unknown> | null
    structureType: string
    buildingTypes?: string[]
    maxBuildings?: number
  },
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<{
    batchId: string
    generatedCount: number
    droppedCount?: number
    droppedReasons?: Record<string, number>
    skippedExisting?: number
    queued?: boolean
    message: string
    osm?: {
      name?: string
      displayName?: string
      osmId?: number | null
      osmType?: string | null
      queryMode?: string
      source?: string
    } | null
    enrichment?: {
      google?: { enrichedCount: number; failedCount: number }
      qls?: { enrichedCount: number; failedCount: number }
      error?: string
    } | null
    scope?: {
      kind?: string
      cityChildren?: Array<{ id: string; name: string; type: string }>
    }
  }>('/api/v1/admin/bulk-processing/generate', {
    method: 'POST',
    body,
    context,
  })
}

export const executeLiberiaBulkProcessingBatch = async (
  batchId: string,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<{
    batchId: string
    generatedCount: number
    droppedCount?: number
    droppedReasons?: Record<string, number>
    skippedExisting?: number
    message: string
    osm?: {
      name?: string
      displayName?: string
      osmId?: number | null
      osmType?: string | null
      queryMode?: string
      source?: string
    } | null
    enrichment?: {
      google?: { enrichedCount: number; failedCount: number }
      qls?: { enrichedCount: number; failedCount: number }
      error?: string
    } | null
  }>(`/api/v1/admin/bulk-processing/batches/${encodeURIComponent(batchId)}/execute`, {
    method: 'POST',
    context,
  })
}

export const enrichLiberiaBulkProcessingWithGoogle = async (
  batchId: string,
  maxRecords: number,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<{
    enrichedCount: number
    failedCount: number
    totalProcessed: number
    message: string
    errors?: string[]
  }>(`/api/v1/admin/bulk-processing/batches/${encodeURIComponent(batchId)}/enrich/google`, {
    method: 'POST',
    body: { maxRecords },
    context,
  })
}

export const enrichLiberiaBulkProcessingWithQls = async (
  batchId: string,
  body: {
    retryOnly?: boolean
    limit?: number
  },
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<{
    enrichedCount: number
    failedCount: number
    totalProcessed: number
    message: string
  }>(`/api/v1/admin/bulk-processing/batches/${encodeURIComponent(batchId)}/enrich/qls`, {
    method: 'POST',
    body,
    context,
  })
}

export interface LiberiaDirectionStep {
  instruction: string
  distanceText: string
  durationText: string
  maneuver: string | null
  start: { lat: number; lng: number }
  end: { lat: number; lng: number }
}

export interface LiberiaDirectionsResult {
  status: string
  mode: string
  originLabel: string
  destinationLabel: string
  distanceText: string
  durationText: string
  distanceMeters: number
  durationSeconds: number
  overviewPolyline: string
  bounds: {
    northeast: { lat: number; lng: number }
    southwest: { lat: number; lng: number }
  } | null
  steps: LiberiaDirectionStep[]
  googleMapsUrl: string
  crossBorder: boolean
}

export const getLiberiaDirections = async (body: {
  origin: { lat?: number; lng?: number; address?: string } | string
  destination: { lat?: number; lng?: number; address?: string } | string
  mode?: 'driving' | 'walking' | 'transit' | 'bicycling'
}) => {
  return liberiaApiRequest<LiberiaDirectionsResult>('/api/v1/navigation/directions', {
    method: 'POST',
    body,
  })
}

export const trackLiberiaNavigation = async (body: {
  session_token: string
  geobloc?: string | null
  geobloc_id?: string | null
  city?: string | null
  state?: string | null
  country?: string | null
  zipcode?: string | null
  address_id?: string | null
  page_type?: string | null
  referrer?: string | null
  user_agent?: string | null
  metadata?: Record<string, unknown>
}, context?: LiberiaApiAuthContext | null) => {
  return liberiaApiRequest<{ id: string; visited_at: string }>('/api/v1/navigation/track', {
    method: 'POST',
    body,
    context: context || undefined,
  })
}

export const getLiberiaMostVisitedGeoblocs = async (
  params: {
    startDate?: string
    endDate?: string
    limit?: number
  },
  context: LiberiaApiAuthContext
) => {
  const query = new URLSearchParams()
  if (params.startDate) query.set('startDate', params.startDate)
  if (params.endDate) query.set('endDate', params.endDate)
  if (params.limit) query.set('limit', String(params.limit))

  return liberiaApiRequest<any[]>(`/api/v1/navigation/analytics/most-visited?${query.toString()}`, {
    context,
  })
}

export const getLiberiaVisitsByPeriod = async (
  params: {
    startDate?: string
    endDate?: string
    geobloc?: string
    city?: string
  },
  context: LiberiaApiAuthContext
) => {
  const query = new URLSearchParams()
  if (params.startDate) query.set('startDate', params.startDate)
  if (params.endDate) query.set('endDate', params.endDate)
  if (params.geobloc) query.set('geobloc', params.geobloc)
  if (params.city) query.set('city', params.city)

  return liberiaApiRequest<any[]>(`/api/v1/navigation/analytics/visits-by-period?${query.toString()}`, {
    context,
  })
}

export const getLiberiaNavigationSummary = async (
  params: {
    startDate?: string
    endDate?: string
  },
  context: LiberiaApiAuthContext
) => {
  const query = new URLSearchParams()
  if (params.startDate) query.set('startDate', params.startDate)
  if (params.endDate) query.set('endDate', params.endDate)

  return liberiaApiRequest<{
    totalPageViews: number
    uniqueVisitors: number
    pageTypeStats: Array<{ page_type: string; count: number }>
    platformStats: Array<{ platform: string; count: number }>
  }>(`/api/v1/navigation/analytics/summary?${query.toString()}`, {
    context,
  })
}

export interface LiberiaAdminSession {
  id: string
  user_id: string
  session_id: string
  session_token: string
  login_at: string
  created_at: string
  updated_at: string
  last_activity: string
  expires_at: string
  logout_at?: string | null
  is_active: boolean
  ip_address?: string | null
  user_agent?: string | null
  country?: string | null
  city?: string | null
  device_info?: Record<string, unknown> | null
  user_profiles?: {
    email: string
    full_name: string | null
    role: string
  } | null
}

export interface LiberiaAdminSessionStats {
  totalSessions: number
  activeSessions: number
  todaySessions: number
  uniqueActiveUsers: number
  topCountries: Array<{
    country: string
    count: number
  }>
}

export interface LiberiaAdminUserAnalytics {
  totalUsers: number
  activityData: {
    active_users: number
    inactive_users: number
    retention_rate: number
  }
  growthData: Array<{
    date: string
    new_signups: number
  }>
  geographicData: Array<{
    country: string
    user_count: number
  }>
}

export interface LiberiaFaqEntry {
  id: string
  question: string
  answer: string
  category: string
  tags: string[]
  is_active: boolean
  order_priority: number
  requires_auth: boolean
  created_by?: string | null
  created_at: string
  updated_at: string
}

export const listLiberiaAdminSessions = async (
  context: LiberiaApiAuthContext,
  options: {
    activeOnly?: boolean
  } = {}
) => {
  const query = new URLSearchParams()
  if (options.activeOnly) query.set('activeOnly', 'true')

  const suffix = query.toString() ? `?${query.toString()}` : ''
  return liberiaApiRequest<LiberiaAdminSession[]>(`/api/v1/admin/sessions${suffix}`, {
    context,
  })
}

export const listLiberiaActiveAdminSessions = async (context: LiberiaApiAuthContext) => {
  return liberiaApiRequest<LiberiaAdminSession[]>('/api/v1/admin/sessions/active', {
    context,
  })
}

export const getLiberiaAdminSessionStats = async (context: LiberiaApiAuthContext) => {
  return liberiaApiRequest<LiberiaAdminSessionStats>('/api/v1/admin/sessions/stats', {
    context,
  })
}

export const terminateLiberiaAdminSession = async (
  sessionId: string,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<{ id: string; terminated: boolean }>(
    `/api/v1/admin/sessions/${encodeURIComponent(sessionId)}/terminate`,
    {
      method: 'POST',
      context,
    }
  )
}

export const cleanupLiberiaAdminSessions = async (
  body: {
    cleanup_type?: 'inactive' | 'all_old'
    mode?: 'inactive' | 'all_old'
    cutoffHours?: number
    days?: number
  },
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<{
    cleaned_sessions: number
    cleanup_type: string
  }>('/api/v1/admin/sessions/cleanup', {
    method: 'POST',
    body,
    context,
  })
}

export const getLiberiaAdminUserAnalytics = async (context: LiberiaApiAuthContext) => {
  return liberiaApiRequest<LiberiaAdminUserAnalytics>('/api/v1/admin/sessions/analytics/users', {
    context,
  })
}

export interface LiberiaAuditLog {
  id: string
  user_id?: string
  action: string
  resource_type: string
  resource_id?: string
  old_values?: unknown
  new_values?: unknown
  ip_address?: string
  user_agent?: string
  severity?: string
  metadata?: Record<string, unknown>
  created_at: string
}

export const listLiberiaAuditLogs = async (
  options: {
    limit?: number
    search?: string
    action?: string
    resourceType?: string
  } = {},
  context: LiberiaApiAuthContext
) => {
  const query = new URLSearchParams()
  if (options.limit != null) query.set('limit', String(options.limit))
  if (options.search) query.set('search', options.search)
  if (options.action && options.action !== 'all') query.set('action', options.action)
  if (options.resourceType && options.resourceType !== 'all') {
    query.set('resourceType', options.resourceType)
  }

  const suffix = query.toString() ? `?${query.toString()}` : ''
  return liberiaApiRequest<LiberiaAuditLog[]>(`/api/v1/admin/audit-logs${suffix}`, {
    context,
  })
}

export const listLiberiaAuditLogsByUser = async (
  userId: string,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaAuditLog[]>(
    `/api/v1/admin/audit-logs/users/${encodeURIComponent(userId)}`,
    { context }
  )
}

export const listLiberiaAuditLogsByResource = async (
  resourceType: string,
  resourceId: string,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaAuditLog[]>(
    `/api/v1/admin/audit-logs/resources/${encodeURIComponent(resourceType)}/${encodeURIComponent(resourceId)}`,
    { context }
  )
}

export const listLiberiaFaqEntries = async (options: {
  requiresAuth?: boolean
} = {}) => {
  const query = new URLSearchParams()
  if (options.requiresAuth !== undefined) {
    query.set('requiresAuth', String(options.requiresAuth))
  }

  const suffix = query.toString() ? `?${query.toString()}` : ''
  return liberiaApiRequest<LiberiaFaqEntry[]>(`/api/v1/faqs${suffix}`)
}

export const listLiberiaAdminFaqEntries = async (context: LiberiaApiAuthContext) => {
  return liberiaApiRequest<LiberiaFaqEntry[]>('/api/v1/admin/faqs', {
    context,
  })
}

export const createLiberiaFaqEntry = async (
  body: Omit<LiberiaFaqEntry, 'id' | 'created_at' | 'updated_at' | 'created_by'>,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaFaqEntry>('/api/v1/admin/faqs', {
    method: 'POST',
    body,
    context,
  })
}

export const updateLiberiaFaqEntry = async (
  id: string,
  body: Partial<Omit<LiberiaFaqEntry, 'id' | 'created_at' | 'updated_at' | 'created_by'>>,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaFaqEntry>(`/api/v1/admin/faqs/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body,
    context,
  })
}

export const deleteLiberiaFaqEntry = async (id: string, context: LiberiaApiAuthContext) => {
  return liberiaApiRequest<{ id: string; deleted: boolean }>(
    `/api/v1/admin/faqs/${encodeURIComponent(id)}`,
    {
      method: 'DELETE',
      context,
    }
  )
}

export const createLiberiaPartnerType = async (
  body: {
    name: string
    description?: string
  },
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaPartnerType>('/api/v1/admin/setup/partner-types', {
    method: 'POST',
    body,
    context,
  })
}

export const updateLiberiaPartnerType = async (
  partnerTypeId: string,
  body: Partial<{
    name: string
    description: string
  }>,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaPartnerType>(`/api/v1/admin/setup/partner-types/${partnerTypeId}`, {
    method: 'PATCH',
    body,
    context,
  })
}

export const deleteLiberiaPartnerType = async (
  partnerTypeId: string,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<{ id: string; deleted: boolean }>(`/api/v1/admin/setup/partner-types/${partnerTypeId}`, {
    method: 'DELETE',
    context,
  })
}

export const listLiberiaProjects = async (context: LiberiaApiAuthContext) => {
  return liberiaApiRequest<LiberiaProject[]>('/api/v1/projects', { context })
}

export const createLiberiaProject = async (
  body: {
    name: string
    description?: string
    country_id?: string | null
    administrative_area_id?: string | null
  },
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaProject>('/api/v1/projects', {
    method: 'POST',
    body,
    context,
  })
}

export const updateLiberiaProject = async (
  projectId: string,
  body: Partial<{
    name: string
    description: string | null
    status: string
    country_id: string | null
    administrative_area_id: string | null
  }>,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaProject>(`/api/v1/projects/${encodeURIComponent(projectId)}`, {
    method: 'PATCH',
    body,
    context,
  })
}

export const listLiberiaProjectMembers = async (
  projectId: string,
  context: LiberiaApiAuthContext,
  includeInactive = false
) => {
  const suffix = includeInactive ? '?includeInactive=true' : ''
  return liberiaApiRequest<LiberiaProjectMember[]>(`/api/v1/projects/${encodeURIComponent(projectId)}/members${suffix}`, {
    context,
  })
}

export const getLiberiaProjectBulkValidation = async (
  projectId: string,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaProjectBulkValidation | null>(
    `/api/v1/projects/${encodeURIComponent(projectId)}/bulk-validation`,
    {
      context,
    }
  )
}

export const recordLiberiaProjectBulkValidationDownload = async (
  projectId: string,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaProjectBulkValidation>(
    `/api/v1/projects/${encodeURIComponent(projectId)}/bulk-validation/download`,
    {
      method: 'POST',
      context,
    }
  )
}

export const recordLiberiaProjectBulkValidationUpload = async (
  projectId: string,
  uploadedCount: number,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaProjectBulkValidation>(
    `/api/v1/projects/${encodeURIComponent(projectId)}/bulk-validation/upload`,
    {
      method: 'POST',
      body: {
        uploaded_count: uploadedCount,
      },
      context,
    }
  )
}

export const updateLiberiaProjectGeneratedAddressValidationStatus = async (
  projectId: string,
  addressId: string,
  validationStatus: 'validated' | 'invalid' | 'pending',
  context: LiberiaApiAuthContext
) => {
  const data = await liberiaApiRequest<any>(
    `/api/v1/projects/${encodeURIComponent(projectId)}/addresses/${encodeURIComponent(addressId)}/validation-status`,
    {
      method: 'PATCH',
      body: { validationStatus },
      context,
    }
  )

  return mapBackendAddressToRegistryAddress(data)
}

export const deleteLiberiaProjectGeneratedAddress = async (
  projectId: string,
  addressId: string,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<{ id: string; deleted: boolean }>(
    `/api/v1/projects/${encodeURIComponent(projectId)}/addresses/${encodeURIComponent(addressId)}`,
    {
      method: 'DELETE',
      context,
    }
  )
}

export const bulkUpdateLiberiaProjectGeneratedAddressValidationStatuses = async (
  projectId: string,
  rows: Array<{
    id: string
    validationStatus: 'validated' | 'invalid' | 'pending'
    notes?: string | null
  }>,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<{
    updated: LiberiaRegistryAddress[]
    failed: Array<{ id: string; message: string }>
  }>(`/api/v1/projects/${encodeURIComponent(projectId)}/bulk-validation/rows`, {
    method: 'POST',
    body: { rows },
    context,
  })
}

export const listLiberiaAssignableProjectUsers = async (context: LiberiaApiAuthContext) => {
  return liberiaApiRequest<Array<{
    id: string
    email: string
    full_name: string | null
    role: string | null
  }>>('/api/v1/projects/assignable-users', {
    context,
  })
}

export const updateLiberiaProjectMemberStatus = async (
  projectId: string,
  memberId: string,
  status: 'active' | 'inactive',
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaProjectMember>(
    `/api/v1/projects/${encodeURIComponent(projectId)}/members/${encodeURIComponent(memberId)}/status`,
    {
      method: 'PATCH',
      body: { status },
      context,
    }
  )
}

export const createLiberiaProjectInvitation = async (
  projectId: string,
  email: string,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<{
    id: string
    project_id: string
    email: string
    invited_by: string
    status: string
    invitation_token: string
    expires_at: string
    created_at: string
    updated_at: string
  }>(`/api/v1/projects/${encodeURIComponent(projectId)}/invitations`, {
    method: 'POST',
    body: { email },
    context,
  })
}

export const respondToLiberiaProjectInvitation = async (
  invitationId: string,
  response: 'accepted' | 'declined',
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<{
    invitation_id: string
    project_id: string
    status: string
  }>(`/api/v1/projects/invitations/${encodeURIComponent(invitationId)}/respond`, {
    method: 'POST',
    body: { response },
    context,
  })
}

export const listLiberiaPartners = async (context: LiberiaApiAuthContext) => {
  return liberiaApiRequest<LiberiaPartnerUser[]>('/api/v1/admin/partner-assignments/partners', { context })
}

/** Partner: own territory assignments (no admin:access). */
export const listLiberiaMyPartnerAreaAssignments = async (context: LiberiaApiAuthContext) => {
  return liberiaApiRequest<LiberiaPartnerAreaAssignment[]>('/api/v1/me/partner-area-assignments', { context })
}

export const listLiberiaPartnerAreaAssignments = async (
  context: LiberiaApiAuthContext,
  partnerId?: string
) => {
  const suffix = partnerId ? `?partnerId=${encodeURIComponent(partnerId)}` : ''
  return liberiaApiRequest<LiberiaPartnerAreaAssignment[]>(`/api/v1/admin/partner-assignments/assignments${suffix}`, {
    context,
  })
}

export const listLiberiaUnassignedAreas = async (context: LiberiaApiAuthContext) => {
  return liberiaApiRequest<LiberiaAdministrativeArea[]>('/api/v1/admin/partner-assignments/unassigned-areas', { context })
}

export const assignLiberiaAreaToPartner = async (
  body: {
    partner_id: string
    administrative_area_id: string
    notes?: string
  },
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaPartnerAreaAssignment>('/api/v1/admin/partner-assignments/assignments', {
    method: 'POST',
    body,
    context,
  })
}

export const updateLiberiaPartnerAssignmentStatus = async (
  assignmentId: string,
  status: 'active' | 'completed' | 'inactive',
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaPartnerAreaAssignment>(`/api/v1/admin/partner-assignments/assignments/${assignmentId}/status`, {
    method: 'PATCH',
    body: { status },
    context,
  })
}

export const deleteLiberiaPartnerAssignment = async (
  assignmentId: string,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<{ id: string; deleted: boolean }>(`/api/v1/admin/partner-assignments/assignments/${assignmentId}`, {
    method: 'DELETE',
    context,
  })
}

export const listLiberiaStructureTypes = async (activeOnly = true) => {
  const suffix = activeOnly ? '?activeOnly=true' : ''
  return liberiaApiRequest<LiberiaStructureType[]>(`/api/v1/structure-types${suffix}`)
}

export const getLiberiaPublicBoundaryConflict = async (latitude: number, longitude: number) => {
  const query = new URLSearchParams({
    latitude: String(latitude),
    longitude: String(longitude),
  })

  return liberiaApiRequest<LiberiaAnalysisResult['boundaryConflict']>(
    `/api/v1/address-generation/public/boundary-conflict?${query.toString()}`
  )
}

export const listLiberiaAdminStructureTypes = async (context: LiberiaApiAuthContext, activeOnly = false) => {
  const suffix = activeOnly ? '?activeOnly=true' : ''
  return liberiaApiRequest<LiberiaStructureType[]>(`/api/v1/admin/setup/structure-types${suffix}`, { context })
}

export const createLiberiaStructureType = async (
  body: {
    name: string
    description?: string
    is_system_required?: boolean
    is_active?: boolean
  },
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaStructureType>('/api/v1/admin/setup/structure-types', {
    method: 'POST',
    body,
    context,
  })
}

export const updateLiberiaStructureType = async (
  structureTypeId: string,
  body: Partial<{
    name: string
    description: string
    is_system_required: boolean
    is_active: boolean
  }>,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<LiberiaStructureType>(`/api/v1/admin/setup/structure-types/${structureTypeId}`, {
    method: 'PATCH',
    body,
    context,
  })
}

export const deleteLiberiaStructureType = async (
  structureTypeId: string,
  context: LiberiaApiAuthContext
) => {
  return liberiaApiRequest<{ id: string; deleted: boolean }>(`/api/v1/admin/setup/structure-types/${structureTypeId}`, {
    method: 'DELETE',
    context,
  })
}

// ============================================================
// Tourism & Hospitality
// ============================================================
export type LiberiaTourismAttractionType =
  | 'landmark'
  | 'natural'
  | 'cultural'
  | 'recreational'
  | 'historical'
  | 'religious'
  | 'beach'
  | 'wildlife'

export type LiberiaTourismAccommodationType =
  | 'hotel'
  | 'lodge'
  | 'guesthouse'
  | 'resort'
  | 'hostel'
  | 'vacation_rental'
  | 'bed_and_breakfast'

export type LiberiaTourismPriceRange = 'budget' | 'mid_range' | 'luxury'
export type LiberiaTourismDifficulty = 'easy' | 'moderate' | 'challenging'

export interface LiberiaTourismAttraction {
  id: string
  name: string
  type: LiberiaTourismAttractionType
  description: string | null
  address_id: string | null
  formatted_address: string | null
  latitude: number | null
  longitude: number | null
  county: string | null
  city: string | null
  opening_hours: string | null
  // Attraction entry fee is now resolved from the Tax Revenue catalog. The
  // code is the stable identifier; entry_fee is the live LRD amount looked up
  // from the catalog and returned by the API for convenience.
  entry_fee_code: string | null
  entry_fee: number | null
  public_transport_info: string | null
  rating: number
  review_count: number
  amenities: string[]
  accessibility_features: string[]
  contact_phone: string | null
  contact_email: string | null
  website: string | null
  is_active: boolean
  is_verified: boolean
  is_featured: boolean
  created_by: string | null
  verified_by: string | null
  verified_at: string | null
  created_at: string
  updated_at: string
}

export interface LiberiaTourismAccommodation {
  id: string
  name: string
  type: LiberiaTourismAccommodationType
  star_rating: number | null
  description: string | null
  // Every accommodation must be linked to one attraction. attraction_name
  // is enriched at read time via a LEFT JOIN.
  address_id: string | null
  attraction_id: string | null
  attraction_name: string | null
  formatted_address: string | null
  latitude: number | null
  longitude: number | null
  county: string | null
  city: string | null
  contact_phone: string | null
  contact_email: string | null
  website: string | null
  room_count: number
  amenities: string[]
  price_range: LiberiaTourismPriceRange
  is_verified: boolean
  is_active: boolean
  is_featured: boolean
  created_by: string | null
  verified_by: string | null
  verified_at: string | null
  created_at: string
  updated_at: string
}

export interface LiberiaTourismRouteStop {
  id: string
  order: number
  // Free-form waypoint label inside the parent attraction's domain.
  // Replaces the old attraction_id/attraction_name pair (1:N model).
  name: string
  duration_minutes: number
  notes: string | null
}

export interface LiberiaTourismRoute {
  id: string
  name: string
  description: string | null
  duration_hours: number
  distance_km: number
  difficulty: LiberiaTourismDifficulty
  is_active: boolean
  is_featured: boolean
  // Parent attraction this route belongs to (1:N). Nullable only for legacy
  // rows pre-migration; new routes must declare a parent.
  attraction_id: string | null
  attraction_name: string | null
  created_by: string | null
  stops: LiberiaTourismRouteStop[]
  created_at: string
  updated_at: string
}

export interface LiberiaTourismStats {
  // Top-line KPIs (back-compatible with the existing dashboard).
  total_attractions: number
  total_accommodations: number
  total_routes: number
  attractions_by_type: Record<string, number>
  accommodations_by_type: Record<string, number>
  avg_attraction_rating: number
  total_room_capacity: number
  // Pending = accommodations awaiting verification (legacy name preserved).
  pending_verifications: number

  // Attraction trust & monetization.
  verified_attractions: number
  pending_attraction_verifications: number
  featured_attractions: number
  paid_attractions: number
  free_attractions: number

  // Accommodation trust.
  verified_accommodations: number
  featured_accommodations: number

  // Route metrics.
  featured_routes: number
  orphan_routes: number
  total_route_waypoints: number
  avg_waypoints_per_route: number
  total_route_duration_hours: number
  total_route_distance_km: number

  // Lodging coverage gap.
  attractions_with_lodging: number
  attractions_without_lodging: number

  // Breakdowns for charts.
  accommodations_by_price_range: { budget: number; mid_range: number; luxury: number }
  accommodations_by_star_rating: Array<{
    star_rating: number
    properties: number
    rooms: number
  }>
  attractions_by_city: Array<{ city: string; count: number }>
  routes_by_difficulty: { easy: number; moderate: number; challenging: number }
  top_attractions_by_routes: Array<{ id: string; name: string; route_count: number }>
  top_attractions_by_accommodations: Array<{
    id: string
    name: string
    accommodation_count: number
  }>
}

export const getLiberiaTourismStats = async () => {
  return liberiaApiRequest<LiberiaTourismStats>('/api/v1/tourism/stats')
}

export const listPublicLiberiaTourismAttractions = async (): Promise<LiberiaTourismAttraction[]> => {
  if (!liberiaApiBaseUrl) return []
  const response = await fetch(`${liberiaApiBaseUrl}/api/v1/public/tourism/attractions`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', ...publicCountryHeaders() },
  })
  if (!response.ok) {
    return []
  }
  const payload = await response.json().catch(() => null)
  return (payload?.data || []) as LiberiaTourismAttraction[]
}

export const getPublicLiberiaTourismAttraction = async (id: string): Promise<LiberiaTourismAttraction | null> => {
  if (!liberiaApiBaseUrl) return null
  const response = await fetch(`${liberiaApiBaseUrl}/api/v1/public/tourism/attractions/${encodeURIComponent(id)}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', ...publicCountryHeaders() },
  })
  if (!response.ok) return null
  const payload = await response.json().catch(() => null)
  return (payload?.data ?? null) as LiberiaTourismAttraction | null
}

// Lists Tourism Board-verified accommodations open to the public. Returns an
// empty array if the API base URL isn't configured or the request fails, so
// the public page degrades gracefully. Optionally accepts an attractionId to
// scope results to accommodations explicitly linked to that attraction.
export const listPublicLiberiaTourismAccommodations = async (
  options: { attractionId?: string } = {}
): Promise<LiberiaTourismAccommodation[]> => {
  if (!liberiaApiBaseUrl) return []
  const params = new URLSearchParams()
  if (options.attractionId) params.set('attraction_id', options.attractionId)
  const qs = params.toString() ? `?${params.toString()}` : ''
  const response = await fetch(`${liberiaApiBaseUrl}/api/v1/public/tourism/accommodations${qs}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', ...publicCountryHeaders() },
  })
  if (!response.ok) return []
  const payload = await response.json().catch(() => null)
  return (payload?.data || []) as LiberiaTourismAccommodation[]
}

// Lists active tour routes (with stops) open to the public.
export const listPublicLiberiaTourismRoutes = async (
  options: { attractionId?: string } = {}
): Promise<LiberiaTourismRoute[]> => {
  if (!liberiaApiBaseUrl) return []
  const params = new URLSearchParams()
  if (options.attractionId) params.set('attraction_id', options.attractionId)
  const qs = params.toString() ? `?${params.toString()}` : ''
  const response = await fetch(
    `${liberiaApiBaseUrl}/api/v1/public/tourism/routes${qs}`,
    {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', ...publicCountryHeaders() },
    }
  )
  if (!response.ok) return []
  const payload = await response.json().catch(() => null)
  return (payload?.data || []) as LiberiaTourismRoute[]
}

export const listLiberiaTourismAttractions = async (
  options: { ownerOnly?: boolean } = {}
) => {
  const qs = options.ownerOnly ? '?owner_only=true' : ''
  return liberiaApiRequest<LiberiaTourismAttraction[]>(`/api/v1/tourism/attractions${qs}`)
}

export const getLiberiaTourismAttraction = async (id: string) => {
  return liberiaApiRequest<LiberiaTourismAttraction>(`/api/v1/tourism/attractions/${id}`)
}

export const createLiberiaTourismAttraction = async (body: Partial<LiberiaTourismAttraction>) => {
  return liberiaApiRequest<LiberiaTourismAttraction>('/api/v1/tourism/attractions', {
    method: 'POST',
    body,
  })
}

export const updateLiberiaTourismAttraction = async (
  id: string,
  body: Partial<LiberiaTourismAttraction>
) => {
  return liberiaApiRequest<LiberiaTourismAttraction>(`/api/v1/tourism/attractions/${id}`, {
    method: 'PATCH',
    body,
  })
}

export const deleteLiberiaTourismAttraction = async (id: string) => {
  return liberiaApiRequest<{ deleted: boolean }>(`/api/v1/tourism/attractions/${id}`, {
    method: 'DELETE',
  })
}

export const verifyLiberiaTourismAttraction = async (
  id: string,
  body: { is_featured?: boolean } = {}
) => {
  return liberiaApiRequest<LiberiaTourismAttraction>(`/api/v1/tourism/admin/attractions/${id}/verify`, {
    method: 'POST',
    body,
  })
}

export const listLiberiaTourismAccommodations = async (
  options: { ownerOnly?: boolean; attractionId?: string } = {}
) => {
  const params = new URLSearchParams()
  if (options.ownerOnly) params.set('owner_only', 'true')
  if (options.attractionId) params.set('attraction_id', options.attractionId)
  const qs = params.toString() ? `?${params.toString()}` : ''
  return liberiaApiRequest<LiberiaTourismAccommodation[]>(`/api/v1/tourism/accommodations${qs}`)
}

export const getLiberiaTourismAccommodation = async (id: string) => {
  return liberiaApiRequest<LiberiaTourismAccommodation>(`/api/v1/tourism/accommodations/${id}`)
}

export const createLiberiaTourismAccommodation = async (body: Partial<LiberiaTourismAccommodation>) => {
  return liberiaApiRequest<LiberiaTourismAccommodation>('/api/v1/tourism/accommodations', {
    method: 'POST',
    body,
  })
}

export const updateLiberiaTourismAccommodation = async (
  id: string,
  body: Partial<LiberiaTourismAccommodation>
) => {
  return liberiaApiRequest<LiberiaTourismAccommodation>(`/api/v1/tourism/accommodations/${id}`, {
    method: 'PATCH',
    body,
  })
}

export const deleteLiberiaTourismAccommodation = async (id: string) => {
  return liberiaApiRequest<{ deleted: boolean }>(`/api/v1/tourism/accommodations/${id}`, {
    method: 'DELETE',
  })
}

export const listLiberiaTourismPendingAccommodations = async () => {
  return liberiaApiRequest<LiberiaTourismAccommodation[]>(
    '/api/v1/tourism/admin/accommodations/pending-verification'
  )
}

export const verifyLiberiaTourismAccommodation = async (
  id: string,
  body: { is_featured?: boolean } = {}
) => {
  return liberiaApiRequest<LiberiaTourismAccommodation>(
    `/api/v1/tourism/admin/accommodations/${id}/verify`,
    {
      method: 'POST',
      body,
    }
  )
}

export const listLiberiaTourismRoutes = async (
  options: { ownerOnly?: boolean; attractionId?: string } = {}
) => {
  const params = new URLSearchParams()
  if (options.ownerOnly) params.set('owner_only', 'true')
  if (options.attractionId) params.set('attraction_id', options.attractionId)
  const qs = params.toString() ? `?${params.toString()}` : ''
  return liberiaApiRequest<LiberiaTourismRoute[]>(`/api/v1/tourism/routes${qs}`)
}

export const getLiberiaTourismRoute = async (id: string) => {
  return liberiaApiRequest<LiberiaTourismRoute>(`/api/v1/tourism/routes/${id}`)
}

export const createLiberiaTourismRoute = async (body: {
  name: string
  // 1:N model: parent attraction is required for new routes.
  attraction_id: string
  description?: string | null
  duration_hours?: number | null
  distance_km?: number | null
  difficulty?: LiberiaTourismDifficulty
  is_active?: boolean
  is_featured?: boolean
  stops?: Array<{
    name?: string
    order?: number
    duration_minutes?: number
    notes?: string | null
  }>
}) => {
  return liberiaApiRequest<LiberiaTourismRoute>('/api/v1/tourism/routes', {
    method: 'POST',
    body,
  })
}

export const updateLiberiaTourismRoute = async (
  id: string,
  body: {
    name?: string
    description?: string | null
    duration_hours?: number | null
    distance_km?: number | null
    difficulty?: LiberiaTourismDifficulty
    is_active?: boolean
    is_featured?: boolean
    attraction_id?: string
    stops?: Array<{
      name?: string
      order?: number
      duration_minutes?: number
      notes?: string | null
    }>
  }
) => {
  return liberiaApiRequest<LiberiaTourismRoute>(`/api/v1/tourism/routes/${id}`, {
    method: 'PATCH',
    body,
  })
}

export const deleteLiberiaTourismRoute = async (id: string) => {
  return liberiaApiRequest<{ deleted: boolean }>(`/api/v1/tourism/routes/${id}`, {
    method: 'DELETE',
  })
}

/** Utility Management — infrastructure assets and service requests. */
export const listLiberiaUtilityAssets = async () => {
  return liberiaApiRequest<any[]>('/api/v1/utility-management/assets')
}

export const createLiberiaUtilityAsset = async (body: Record<string, unknown>) => {
  return liberiaApiRequest<any>('/api/v1/utility-management/assets', { method: 'POST', body })
}

export const updateLiberiaUtilityAsset = async (id: string, body: Record<string, unknown>) => {
  return liberiaApiRequest<any>(`/api/v1/utility-management/assets/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body,
  })
}

export const listLiberiaUtilityServiceRequests = async () => {
  return liberiaApiRequest<any[]>('/api/v1/utility-management/service-requests')
}

export const createLiberiaUtilityServiceRequest = async (body: Record<string, unknown>) => {
  return liberiaApiRequest<any>('/api/v1/utility-management/service-requests', { method: 'POST', body })
}

export const updateLiberiaUtilityServiceRequest = async (id: string, body: Record<string, unknown>) => {
  return liberiaApiRequest<any>(
    `/api/v1/utility-management/service-requests/${encodeURIComponent(id)}`,
    { method: 'PATCH', body }
  )
}

/** Utility private network — public applications */
export const submitPublicUtilityServiceApplication = async (body: Record<string, unknown>) => {
  return liberiaApiRequest<{
    application_reference: string
    access_code: string
    status: string
  }>('/api/v1/public/utility-management/marketplace/service-applications', {
    method: 'POST',
    body,
  })
}

export const lookupPublicUtilityServiceApplication = async (reference: string, accessCode: string) => {
  const q = new URLSearchParams({ reference, access_code: accessCode })
  return liberiaApiRequest<Record<string, unknown>>(
    `/api/v1/public/utility-management/marketplace/service-applications/status?${q}`
  )
}

export const submitPublicUtilityAgentApplication = async (body: Record<string, unknown>) => {
  return liberiaApiRequest<{
    application_reference: string
    access_code: string
    status: string
  }>('/api/v1/public/utility-management/marketplace/agent-applications', {
    method: 'POST',
    body,
  })
}

export const lookupPublicUtilityAgentApplication = async (reference: string, accessCode: string) => {
  const q = new URLSearchParams({ reference, access_code: accessCode })
  return liberiaApiRequest<Record<string, unknown>>(
    `/api/v1/public/utility-management/marketplace/agent-applications/status?${q}`
  )
}

/** Utility private network — authenticated */
export const listUtilityServiceApplications = async (status?: string) => {
  const q = status ? `?status=${encodeURIComponent(status)}` : ''
  return liberiaApiRequest<Record<string, unknown>[]>(
    `/api/v1/utility-management/marketplace/service-applications${q}`
  )
}

export const reviewUtilityServiceApplication = async (
  id: string,
  body: { action: string; reason?: string; notes?: string }
) => {
  return liberiaApiRequest<Record<string, unknown>>(
    `/api/v1/utility-management/marketplace/service-applications/${encodeURIComponent(id)}/review`,
    { method: 'POST', body }
  )
}

export const listUtilityAgentApplications = async (status?: string) => {
  const q = status ? `?status=${encodeURIComponent(status)}` : ''
  return liberiaApiRequest<Record<string, unknown>[]>(
    `/api/v1/utility-management/marketplace/agent-applications${q}`
  )
}

export const reviewUtilityAgentApplication = async (
  id: string,
  body: { action: string; reason?: string; notes?: string }
) => {
  return liberiaApiRequest<Record<string, unknown>>(
    `/api/v1/utility-management/marketplace/agent-applications/${encodeURIComponent(id)}/review`,
    { method: 'POST', body }
  )
}

export const getMyUtilityOrganization = async () => {
  return liberiaApiRequest<Record<string, unknown> | null>('/api/v1/utility-management/marketplace/my-organization')
}

export const listUtilitySites = async () => {
  return liberiaApiRequest<Record<string, unknown>[]>('/api/v1/utility-management/marketplace/sites')
}

export const createUtilitySite = async (body: Record<string, unknown>) => {
  return liberiaApiRequest<Record<string, unknown>>('/api/v1/utility-management/marketplace/sites', {
    method: 'POST',
    body,
  })
}

export const updateUtilitySite = async (id: string, body: Record<string, unknown>) => {
  return liberiaApiRequest<Record<string, unknown>>(
    `/api/v1/utility-management/marketplace/sites/${encodeURIComponent(id)}`,
    { method: 'PATCH', body }
  )
}

export const generateUtilityServiceJobs = async (body?: { from_date?: string; to_date?: string }) => {
  return liberiaApiRequest<{ created: number; from_date: string; to_date: string; jobs: Record<string, unknown>[] }>(
    '/api/v1/utility-management/marketplace/jobs/generate',
    { method: 'POST', body: body || {} }
  )
}

export const listUtilityServiceJobs = async (params?: { status?: string; available?: boolean; mine?: boolean }) => {
  const q = new URLSearchParams()
  if (params?.status) q.set('status', params.status)
  if (params?.available) q.set('available', '1')
  if (params?.mine) q.set('mine', '1')
  const query = q.toString()
  return liberiaApiRequest<Record<string, unknown>[]>(
    `/api/v1/utility-management/marketplace/jobs${query ? `?${query}` : ''}`
  )
}

export const claimUtilityServiceJob = async (jobId: string) => {
  return liberiaApiRequest<Record<string, unknown>>(
    `/api/v1/utility-management/marketplace/jobs/${encodeURIComponent(jobId)}/claim`,
    { method: 'POST', body: {} }
  )
}

export const completeUtilityServiceJob = async (jobId: string, opts?: { asAgent?: boolean }) => {
  return liberiaApiRequest<Record<string, unknown>>(
    `/api/v1/utility-management/marketplace/jobs/${encodeURIComponent(jobId)}/complete`,
    { method: 'POST', body: opts?.asAgent ? { as_agent: true } : {} }
  )
}

export const listUtilityAffiliationRequests = async (status?: string) => {
  const q = status ? `?status=${encodeURIComponent(status)}` : ''
  return liberiaApiRequest<Record<string, unknown>[]>(
    `/api/v1/utility-management/marketplace/affiliations${q}`
  )
}

export const decideUtilityAffiliationRequest = async (
  id: string,
  body: { action: string; notes?: string }
) => {
  return liberiaApiRequest<Record<string, unknown>>(
    `/api/v1/utility-management/marketplace/affiliations/${encodeURIComponent(id)}/review`,
    { method: 'POST', body }
  )
}

export const getUtilityAgentProfile = async () => {
  return liberiaApiRequest<Record<string, unknown>>('/api/v1/utility-management/marketplace/my-profile')
}

export const listUtilityOrganizationsForAgent = async () => {
  return liberiaApiRequest<Record<string, unknown>[]>('/api/v1/utility-management/marketplace/organizations')
}

export const requestUtilityOrganizationAffiliation = async (organizationId: string, notes?: string) => {
  return liberiaApiRequest<Record<string, unknown>>('/api/v1/utility-management/marketplace/affiliations', {
    method: 'POST',
    body: { organization_id: organizationId, notes },
  })
}

/** Waste Management — collection routes and events. */
export const listLiberiaWasteRoutes = async () => {
  return liberiaApiRequest<any[]>('/api/v1/waste-management/routes')
}

export const createLiberiaWasteRoute = async (body: Record<string, unknown>) => {
  return liberiaApiRequest<any>('/api/v1/waste-management/routes', { method: 'POST', body })
}

export const updateLiberiaWasteRoute = async (id: string, body: Record<string, unknown>) => {
  return liberiaApiRequest<any>(`/api/v1/waste-management/routes/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body,
  })
}

export const listLiberiaWasteEvents = async (params?: {
  routeId?: string
  status?: string
  date?: string
}) => {
  const q = new URLSearchParams()
  if (params?.routeId) q.set('route_id', params.routeId)
  if (params?.status) q.set('status', params.status)
  if (params?.date) q.set('date', params.date)
  const query = q.toString()
  return liberiaApiRequest<any[]>(`/api/v1/waste-management/events${query ? `?${query}` : ''}`)
}

export const createLiberiaWasteEvent = async (body: Record<string, unknown>) => {
  return liberiaApiRequest<any>('/api/v1/waste-management/events', { method: 'POST', body })
}

export const updateLiberiaWasteEvent = async (id: string, body: Record<string, unknown>) => {
  return liberiaApiRequest<any>(`/api/v1/waste-management/events/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body,
  })
}

export const getLiberiaWasteStats = async () => {
  return liberiaApiRequest<any>('/api/v1/waste-management/stats')
}

/** Waste private network — public applications */
export const submitPublicWasteServiceApplication = async (body: Record<string, unknown>) => {
  return liberiaApiRequest<{
    application_reference: string
    access_code: string
    status: string
  }>('/api/v1/public/waste-management/marketplace/service-applications', {
    method: 'POST',
    body,
  })
}

export const lookupPublicWasteServiceApplication = async (reference: string, accessCode: string) => {
  const q = new URLSearchParams({ reference, access_code: accessCode })
  return liberiaApiRequest<Record<string, unknown>>(
    `/api/v1/public/waste-management/marketplace/service-applications/status?${q}`
  )
}

export const submitPublicWasteAgentApplication = async (body: Record<string, unknown>) => {
  return liberiaApiRequest<{
    application_reference: string
    access_code: string
    status: string
  }>('/api/v1/public/waste-management/marketplace/agent-applications', {
    method: 'POST',
    body,
  })
}

export const lookupPublicWasteAgentApplication = async (reference: string, accessCode: string) => {
  const q = new URLSearchParams({ reference, access_code: accessCode })
  return liberiaApiRequest<Record<string, unknown>>(
    `/api/v1/public/waste-management/marketplace/agent-applications/status?${q}`
  )
}

/** Waste private network — authenticated */
export const listWasteServiceApplications = async (status?: string) => {
  const q = status ? `?status=${encodeURIComponent(status)}` : ''
  return liberiaApiRequest<Record<string, unknown>[]>(
    `/api/v1/waste-management/marketplace/service-applications${q}`
  )
}

export const reviewWasteServiceApplication = async (
  id: string,
  body: { action: string; reason?: string; notes?: string }
) => {
  return liberiaApiRequest<Record<string, unknown>>(
    `/api/v1/waste-management/marketplace/service-applications/${encodeURIComponent(id)}/review`,
    { method: 'POST', body }
  )
}

export const listWasteAgentApplications = async (status?: string) => {
  const q = status ? `?status=${encodeURIComponent(status)}` : ''
  return liberiaApiRequest<Record<string, unknown>[]>(
    `/api/v1/waste-management/marketplace/agent-applications${q}`
  )
}

export const reviewWasteAgentApplication = async (
  id: string,
  body: { action: string; reason?: string; notes?: string }
) => {
  return liberiaApiRequest<Record<string, unknown>>(
    `/api/v1/waste-management/marketplace/agent-applications/${encodeURIComponent(id)}/review`,
    { method: 'POST', body }
  )
}

export const getMyWasteOrganization = async () => {
  return liberiaApiRequest<Record<string, unknown> | null>('/api/v1/waste-management/marketplace/my-organization')
}

export const listWasteHouseholds = async () => {
  return liberiaApiRequest<Record<string, unknown>[]>('/api/v1/waste-management/marketplace/households')
}

export const createWasteHousehold = async (body: Record<string, unknown>) => {
  return liberiaApiRequest<Record<string, unknown>>('/api/v1/waste-management/marketplace/households', {
    method: 'POST',
    body,
  })
}

export const updateWasteHousehold = async (id: string, body: Record<string, unknown>) => {
  return liberiaApiRequest<Record<string, unknown>>(
    `/api/v1/waste-management/marketplace/households/${encodeURIComponent(id)}`,
    { method: 'PATCH', body }
  )
}

export const generateWastePickupJobs = async (body?: { from_date?: string; to_date?: string }) => {
  return liberiaApiRequest<{ created: number; from_date: string; to_date: string; jobs: Record<string, unknown>[] }>(
    '/api/v1/waste-management/marketplace/jobs/generate',
    { method: 'POST', body: body || {} }
  )
}

export const listWastePickupJobs = async (params?: { status?: string; available?: boolean; mine?: boolean }) => {
  const q = new URLSearchParams()
  if (params?.status) q.set('status', params.status)
  if (params?.available) q.set('available', '1')
  if (params?.mine) q.set('mine', '1')
  const query = q.toString()
  return liberiaApiRequest<Record<string, unknown>[]>(
    `/api/v1/waste-management/marketplace/jobs${query ? `?${query}` : ''}`
  )
}

export const claimWastePickupJob = async (jobId: string) => {
  return liberiaApiRequest<Record<string, unknown>>(
    `/api/v1/waste-management/marketplace/jobs/${encodeURIComponent(jobId)}/claim`,
    { method: 'POST', body: {} }
  )
}

export const completeWastePickupJob = async (jobId: string, opts?: { asAgent?: boolean }) => {
  return liberiaApiRequest<Record<string, unknown>>(
    `/api/v1/waste-management/marketplace/jobs/${encodeURIComponent(jobId)}/complete`,
    { method: 'POST', body: opts?.asAgent ? { as_agent: true } : {} }
  )
}

export const listWasteAffiliationRequests = async (status?: string) => {
  const q = status ? `?status=${encodeURIComponent(status)}` : ''
  return liberiaApiRequest<Record<string, unknown>[]>(
    `/api/v1/waste-management/marketplace/affiliations${q}`
  )
}

export const decideWasteAffiliationRequest = async (
  id: string,
  body: { action: string; notes?: string }
) => {
  return liberiaApiRequest<Record<string, unknown>>(
    `/api/v1/waste-management/marketplace/affiliations/${encodeURIComponent(id)}/review`,
    { method: 'POST', body }
  )
}

export const getWasteAgentProfile = async () => {
  return liberiaApiRequest<Record<string, unknown>>('/api/v1/waste-management/marketplace/my-profile')
}

export const listWasteOrganizationsForAgent = async () => {
  return liberiaApiRequest<Record<string, unknown>[]>('/api/v1/waste-management/marketplace/organizations')
}

export const requestWasteOrganizationAffiliation = async (organizationId: string, notes?: string) => {
  return liberiaApiRequest<Record<string, unknown>>('/api/v1/waste-management/marketplace/affiliations', {
    method: 'POST',
    body: { organization_id: organizationId, notes },
  })
}

/** Census & Statistics. */
export const listLiberiaCensusAreas = async () => {
  return liberiaApiRequest<any[]>('/api/v1/census-statistics/areas')
}

export const createLiberiaCensusArea = async (body: Record<string, unknown>) => {
  return liberiaApiRequest<any>('/api/v1/census-statistics/areas', { method: 'POST', body })
}

export const listLiberiaCensusCampaigns = async () => {
  return liberiaApiRequest<any[]>('/api/v1/census-statistics/campaigns')
}

export const createLiberiaCensusCampaign = async (body: Record<string, unknown>) => {
  return liberiaApiRequest<any>('/api/v1/census-statistics/campaigns', { method: 'POST', body })
}

export const listLiberiaCensusTemplates = async () => {
  return liberiaApiRequest<any[]>('/api/v1/census-statistics/templates')
}

export const createLiberiaCensusTemplate = async (body: Record<string, unknown>) => {
  return liberiaApiRequest<any>('/api/v1/census-statistics/templates', { method: 'POST', body })
}

export const listLiberiaCensusSurveys = async (censusYear?: number) => {
  const query = censusYear ? `?census_year=${encodeURIComponent(String(censusYear))}` : ''
  return liberiaApiRequest<any[]>(`/api/v1/census-statistics/surveys${query}`)
}

export const createLiberiaCensusSurvey = async (body: Record<string, unknown>) => {
  return liberiaApiRequest<any>('/api/v1/census-statistics/surveys', { method: 'POST', body })
}

export const updateLiberiaCensusSurvey = async (id: string, body: Record<string, unknown>) => {
  return liberiaApiRequest<any>(`/api/v1/census-statistics/surveys/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body,
  })
}

export const listLiberiaCensusAggregations = async (campaignId?: string) => {
  const query = campaignId ? `?campaign_id=${encodeURIComponent(campaignId)}` : ''
  return liberiaApiRequest<any[]>(`/api/v1/census-statistics/aggregations${query}`)
}

export const listLiberiaCensusAssignments = async (enumeratorId?: string) => {
  const query = enumeratorId ? `?enumerator_id=${encodeURIComponent(enumeratorId)}` : ''
  return liberiaApiRequest<any[]>(`/api/v1/census-statistics/assignments${query}`)
}

export const getLiberiaCensusStats = async () => {
  return liberiaApiRequest<any>('/api/v1/census-statistics/stats')
}

/** Community Mapping. */
export const getLiberiaCommunityMappingRole = async () => {
  return liberiaApiRequest<{ community_role: string; trust_score?: number }>(
    '/api/v1/community-mapping/me'
  )
}

export const listLiberiaCommunityContributions = async () => {
  return liberiaApiRequest<any[]>('/api/v1/community-mapping/contributions')
}

export const listLiberiaPendingCommunityContributions = async () => {
  return liberiaApiRequest<any[]>('/api/v1/community-mapping/contributions/pending')
}

export const submitLiberiaCommunityContribution = async (body: Record<string, unknown>) => {
  return liberiaApiRequest<any>('/api/v1/community-mapping/contributions', { method: 'POST', body })
}

export const reviewLiberiaCommunityContribution = async (
  contributionId: string,
  body: Record<string, unknown>
) => {
  return liberiaApiRequest<any>(
    `/api/v1/community-mapping/contributions/${encodeURIComponent(contributionId)}/review`,
    { method: 'PATCH', body }
  )
}

export const listLiberiaCommunityLeaderboard = async () => {
  return liberiaApiRequest<any[]>('/api/v1/community-mapping/leaderboard')
}

export const listLiberiaCommunityCoverage = async () => {
  return liberiaApiRequest<any[]>('/api/v1/community-mapping/coverage')
}

export const getLiberiaCommunityMappingStats = async () => {
  return liberiaApiRequest<any>('/api/v1/community-mapping/stats')
}
